import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { xml2js } from "../../json/xml2js";
import queryString from "query-string";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { Tooltip } from "react-tooltip";
import moment from "moment";
import ClientFormHeader from "../common/ClientFormHeader";
import FormButtonBar from "../common/FormButtonBar";
import Signature from "../common/Signature";
import Comment from "../common/Comment";
import BackIcon from "../common/BackIcon";
import ShareModal from "../common/ShareModal";
import ExpandDiv from "../common/ExpandDiv";
import {
  FormCheckbox,
  FormInput,
  FormRadioButton,
  FormTextArea,
  Select,
  FieldTable,
} from "../common/ControlBundle";
import {
  getCareTeams,
  getConfig,
  getSubmission,
  saveForm,
  cloneForm,
  deleteFormSubmission,
  handleFormChange,
  compareForms,
  getFormsySubmissionsForClient,
  sendShareEmail,
  getClientData,
  handleAddFieldTableRow,
  handleDeleteFieldTableRow,
  handleValidatedFieldTableChange,
} from "../../scripts/formScripts";
import {
  getFieldName,
  getFieldValue,
  getFieldDate,
  ArrayCheck,
} from "../../scripts/helperScripts";
import { getSchema } from "./joi/validation";
import Joi from "joi-browser";

function SCFRCPSFASTAssessment() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();
  const qs = queryString.parse(location.search);
  console.log("Params", clientid, formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(false);
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);
  console.log("Schema Config: ", schema);

  const selectedProgramId = useMemo(() => {
    return data.SelectedProgram?.["@value"];
  }, [data.SelectedProgram]);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !initialProgram?.ccx_client_programid ||
      !data.SelectedProgram ||
      data.SelectedProgram["@value"]
    ) {
      return;
    }

    const obj = { ...data };
    obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
    setData(obj);

    if (formsyDetails.ClientProgramId === initialProgram.ccx_client_programid) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = initialProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(initialProgram.ccx_programunit);
    setFormsyDetails(details);
  }, [initialProgram, selectedProgramId, data, formsyDetails]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      Object.keys(formsyConfig).length > 0 ||
      loadingConfig ||
      !safyInfo?.user
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadingConfig(true);
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadingConfig(false);

        console.log("New Form:", newForm);
        console.log(
          "loadConfig setData:",
          response.FormConfiguration[0].config[0]
        );
      }
    };

    loadConfig();
  }, [
    loadingConfig,
    formid,
    clientid,
    safyInfo?.user,
    formsyConfig,
    submissionid,
  ]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  const helperConfiguration = useMemo(() => {
    try {
      return JSON.parse(formsyConfig.HelperConfiguration[0]);
    } catch {
      const defaultConfig = {
        buttonBarConfig: {
          sign: {
            display: false,
            initialSignatureType: "full",
          },
          clone: {
            display: false,
          },
          delete: {
            display: false,
          },
          share: {
            display: false,
          },
          comment: {
            display: false,
          },
        },
        signatureType: "full",
        finalSignType: "Provider",
      };

      return defaultConfig;
    }
  }, [formsyConfig.HelperConfiguration]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validateForm = () => {
    let dataObj = {};

    for (const property in data) {
      if (data[property] && data[property]["@value"])
        dataObj[property] = data[property]["@value"];
    }
    console.log("DataOBJ, ", dataObj, data);
    const options = { abortEarly: false };
    const { error } = Joi.validate(dataObj, schema, options);
    if (!error) return {};

    console.log("error.details: ", error.details);
    let validationErrors = {};
    const obj = { ...data };
    for (let item of error.details) {
      if (item.path.length === 1) {
        validationErrors[item.path[0]] = item.message;
      } else {
        const fieldTableValues = JSON.parse(getFieldValue(obj[item.path[0]]));
        const errs = fieldTableValues[item.path[1]].Errors || {};
        errs[item.path[2]] = item.message;
        fieldTableValues[item.path[1]].Errors = errs;
        obj[item.path[0]]["@value"] = JSON.stringify(fieldTableValues);
      }
    }
    setData(obj);
    console.log("validationErrors: ", validationErrors);
    setErrors(validationErrors);
    return validationErrors;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  const handleRowChange = (e, index, fieldname) => {
    const rowObj = handleValidatedFieldTableChange(
      data,
      e,
      index,
      fieldname,
      schema
    );
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleDeleteRow = (index, fieldname) => {
    const rowObj = handleDeleteFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleAddRow = (fieldname, newRow, unshift = false) => {
    const rowObj = handleAddFieldTableRow(data, fieldname, newRow, unshift);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments; // commentsOverride is usually null, but specifically undefined during Final Signing
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.["@value"]) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
      setCommentToEdit(undefined);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setExpandStatus(true);
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    const requiredSignatureTypes = helperConfiguration.finalSignType.split(",");

    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    console.log("requiredSignatureTypes: ", requiredSignatureTypes);
    console.log("capturedSignatureTypes: ", capturedSignatureTypes);

    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );

    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  const [expandStatus, setExpandStatus] = useState(undefined);

  const handleExpandStatusChange = (overrideValue, callback) => {
    if (overrideValue !== undefined) {
      setExpandStatus(overrideValue);
      if (callback) {
        window.setTimeout(callback, 5);
      }
    } else setExpandStatus(!expandStatus);
  };

  const assessmentTypeOptions = [
    { _id: "Initial", name: "Initial" },
    { _id: "Scheduled Update", name: "Scheduled Update" },
    { _id: "Major Life Event", name: "Major Life Event" },
    { _id: "Exit", name: "Exit" },
  ];

  const yesNoOptions = [
    { _id: "No", name: "No" },
    { _id: "Yes", name: "Yes" },
  ];

  const getFamilyScores = useCallback(
    (categoryName) => {
      if (!data.SelectedProgram) {
        return 0;
      }

      let score = 0;

      const fieldNames = JSON.parse(
        data.Scores[`@${categoryName}`].replaceAll("'", '"')
      );
      fieldNames.forEach((name) => {
        const value = data[name]["@value"];

        if (value) {
          score += parseInt(value);
        }
      });

      return score;
    },
    [data]
  );

  const getTableScores = useCallback(
    (fieldName, categoryName, index) => {
      if (!data.SelectedProgram) {
        return 0;
      }

      let score = 0;

      const fieldNames = JSON.parse(
        data.Scores[`@${categoryName}`].replaceAll("'", '"')
      );
      fieldNames.forEach((name) => {
        const valueRow = JSON.parse(data[fieldName]["@value"])[index];

        if (valueRow) {
          const value = valueRow[name];

          if (value) {
            score += parseInt(value);
          }
        }
      });

      return score;
    },
    [data]
  );

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        />
        <FormButtonBar
          config={helperConfiguration.buttonBarConfig}
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validateForm}
          beforePrintFunction={(callback) => {
            handleExpandStatusChange(true, callback);
          }}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        <div className="py-2 border-bottom border-dark">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <div className=" row">
                <div className="col-4">
                  <FormInput
                    name="DateCompleted"
                    label="Date Completed"
                    error={errors.DateCompleted}
                    value={getFieldValue(data.DateCompleted)}
                    onChange={handleChange}
                    identical={compare("DateCompleted")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    type="date"
                  />
                </div>
                <div className="col">
                  <FormRadioButton
                    name="AssessmentType"
                    label="Assessment Type"
                    error={errors.AssessmentType}
                    value={getFieldValue(data.AssessmentType)}
                    options={assessmentTypeOptions}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <FormInput
                name="AssessorName"
                label="Assessor Name"
                error={errors.AssessorName}
                value={getFieldValue(data.AssessorName)}
                onChange={handleChange}
                identical={compare("AssessorName")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <button
                className="btn btn-primary mt-2"
                onClick={(e) => {
                  handleExpandStatusChange();
                }}
              >
                {expandStatus === true ? "Collapse All" : "Expand All"}
              </button>
              <ExpandDiv
                id="FamilyAssessment"
                expandStatus={expandStatus}
                className="mt-2"
                title="Family Assessment"
              >
                <div className="container bg-secondary p-2">
                  {
                    // Family Functioning
                  }
                  <React.Fragment>
                    <div className="m-2 row">
                      <div className="col-6">
                        <div className="h5 text-white">Family Functioning</div>
                      </div>
                      <div className="col-1 bg-body py-2">
                        {getFamilyScores("familyFunctioning")}
                      </div>
                      <div className="col"></div>
                    </div>
                    <div className="mx-2 row border-bottom bg-body">
                      <div className="col py-2 bg-light">
                        For Family Functioning, use the following categories and
                        action levels:
                        <br />
                        0 - No evidence of any needs; no need for action.
                        <br />
                        1 - Identified need that requires monitoring, watchful
                        waiting, or preventive action based on history,
                        suspicion or disagreement.
                        <br />
                        2 - Action is required to ensure that the identified
                        need is addressed; need is interfering with functioning.
                        <br />3 - Need is dangerous or disabling; requires
                        immediate and/or intensive action.
                      </div>
                    </div>
                    <div className="mx-2 row border-bottom">
                      <div
                        className="col py-2 bg-body"
                        data-tooltip-id="FamilyConflictTooltip"
                      >
                        Family Conflict
                        {errors.FamilyConflict !== undefined ? (
                          <div className="alert alert-danger">
                            {errors.FamilyConflict}
                          </div>
                        ) : null}
                        <Tooltip className="w-50" id="FamilyConflictTooltip">
                          {getTooltip("FamilyConflict")}
                        </Tooltip>
                      </div>
                      <div className="col py-2 bg-body border-start border-5">
                        <FormInput
                          name="FamilyConflict"
                          value={getFieldValue(data.FamilyConflict)}
                          onChange={handleChange}
                          identical={compare("FamilyConflict")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          type="number"
                          min="0"
                          max="3"
                        />
                      </div>
                    </div>
                    <div className="mx-2 row border-bottom">
                      <div
                        className="col py-2 bg-body"
                        data-tooltip-id="FamilyRoleAppropriatenessTooltip"
                      >
                        Family Role Appropriateness
                        {errors.FamilyRoleAppropriateness !== undefined ? (
                          <div className="alert alert-danger">
                            {errors.FamilyRoleAppropriateness}
                          </div>
                        ) : null}
                        <Tooltip
                          className="w-50"
                          id="FamilyRoleAppropriatenessTooltip"
                        >
                          {getTooltip("FamilyRoleAppropriateness")}
                        </Tooltip>
                      </div>
                      <div className="col py-2 bg-body border-start border-5">
                        <FormInput
                          name="FamilyRoleAppropriateness"
                          value={getFieldValue(data.FamilyRoleAppropriateness)}
                          onChange={handleChange}
                          identical={compare("FamilyRoleAppropriateness")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          type="number"
                          min="0"
                          max="3"
                        />
                      </div>
                    </div>
                    <div className="mx-2 row border-bottom">
                      <div
                        className="col py-2 bg-body"
                        data-tooltip-id="FamilySafetyTooltip"
                      >
                        Family Safety
                        {errors.FamilySafety !== undefined ? (
                          <div className="alert alert-danger">
                            {errors.FamilySafety}
                          </div>
                        ) : null}
                        <Tooltip className="w-50" id="FamilySafetyTooltip">
                          {getTooltip("FamilySafety")}
                        </Tooltip>
                      </div>
                      <div className="col py-2 bg-body border-start border-5">
                        <FormInput
                          name="FamilySafety"
                          value={getFieldValue(data.FamilySafety)}
                          onChange={handleChange}
                          identical={compare("FamilySafety")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          type="number"
                          min="0"
                          max="3"
                        />
                      </div>
                    </div>
                    <div className="mx-2 row border-bottom">
                      <div
                        className="col py-2 bg-body"
                        data-tooltip-id="FinancialResourcesTooltip"
                      >
                        Financial Resources
                        {errors.FinancialResources !== undefined ? (
                          <div className="alert alert-danger">
                            {errors.FinancialResources}
                          </div>
                        ) : null}
                        <Tooltip
                          className="w-50"
                          id="FinancialResourcesTooltip"
                        >
                          {getTooltip("FinancialResources")}
                        </Tooltip>
                      </div>
                      <div className="col py-2 bg-body border-start border-5">
                        <FormInput
                          name="FinancialResources"
                          value={getFieldValue(data.FinancialResources)}
                          onChange={handleChange}
                          identical={compare("FinancialResources")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          type="number"
                          min="0"
                          max="3"
                        />
                      </div>
                    </div>
                    <div className="mx-2 row border-bottom">
                      <div
                        className="col py-2 bg-body"
                        data-tooltip-id="ResidentialStabilityTooltip"
                      >
                        Residential Stability
                        {errors.ResidentialStability !== undefined ? (
                          <div className="alert alert-danger">
                            {errors.ResidentialStability}
                          </div>
                        ) : null}
                        <Tooltip
                          className="w-50"
                          id="ResidentialStabilityTooltip"
                        >
                          {getTooltip("ResidentialStability")}
                        </Tooltip>
                      </div>
                      <div className="col py-2 bg-body border-start border-5">
                        <FormInput
                          name="ResidentialStability"
                          value={getFieldValue(data.ResidentialStability)}
                          onChange={handleChange}
                          identical={compare("ResidentialStability")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          type="number"
                          min="0"
                          max="3"
                        />
                      </div>
                    </div>
                  </React.Fragment>
                  {
                    // Family Strengths
                  }
                  <React.Fragment>
                    <div className="m-2 row">
                      <div className="col-6">
                        <div className="h5 text-white">Family Strengths</div>
                      </div>
                      <div className="col-1 bg-body py-2">
                        {getFamilyScores("familyStrengths")}
                        <div className="col"></div>
                      </div>
                    </div>
                    <div className="mx-2 row border-bottom bg-body">
                      <div className="col py-2 bg-light">
                        For Family Strengths, use the following categories and
                        action levels:
                        <br />
                        0 - Well-developed, centerpiece strength; may be used as
                        a focus of an intervention/action plan.
                        <br />
                        1 - Identified and useful strength. Strength will be
                        used, maintained or built upon as part of the plan. May
                        require some effort to develop strength into a
                        centerpiece strength.
                        <br />
                        2 - Strengths have been identified but require
                        strength-building efforts before they can be effectively
                        utilized as part of a plan. Identified but not useful.
                        <br />3 - An area in which no current strength is
                        identified; efforts may be recommended to develop a
                        strength in this area.
                      </div>
                    </div>
                    <div className="mx-2 row border-bottom">
                      <div
                        className="col py-2 bg-body"
                        data-tooltip-id="FamilySocialIdentityTooltip"
                      >
                        Family Social Identity
                        {errors.FamilySocialIdentity !== undefined ? (
                          <div className="alert alert-danger">
                            {errors.FamilySocialIdentity}
                          </div>
                        ) : null}
                        <Tooltip
                          className="w-50"
                          id="FamilySocialIdentityTooltip"
                        >
                          {getTooltip("FamilySocialIdentity")}
                        </Tooltip>
                      </div>
                      <div className="col py-2 bg-body border-start border-5">
                        <FormInput
                          name="FamilySocialIdentity"
                          value={getFieldValue(data.FamilySocialIdentity)}
                          onChange={handleChange}
                          identical={compare("FamilySocialIdentity")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          type="number"
                          min="0"
                          max="3"
                        />
                      </div>
                    </div>
                    <div className="mx-2 row border-bottom">
                      <div
                        className="col py-2 bg-body"
                        data-tooltip-id="FamilyCollaborationTooltip"
                      >
                        Parental-Caregiver Collaboration
                        {errors.FamilyCollaboration !== undefined ? (
                          <div className="alert alert-danger">
                            {errors.FamilyCollaboration}
                          </div>
                        ) : null}
                        <Tooltip
                          className="w-50"
                          id="FamilyCollaborationTooltip"
                        >
                          {getTooltip("FamilyCollaboration")}
                        </Tooltip>
                      </div>
                      <div className="col py-2 bg-body border-start border-5">
                        <FormInput
                          name="FamilyCollaboration"
                          value={getFieldValue(data.FamilyCollaboration)}
                          onChange={handleChange}
                          identical={compare("FamilyCollaboration")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          type="number"
                          min="0"
                          max="3"
                        />
                      </div>
                    </div>
                    <div className="mx-2 row border-bottom">
                      <div
                        className="col py-2 bg-body"
                        data-tooltip-id="SiblingRelationshipsTooltip"
                      >
                        Relations Among Siblings
                        {errors.SiblingRelationships !== undefined ? (
                          <div className="alert alert-danger">
                            {errors.SiblingRelationships}
                          </div>
                        ) : null}
                        <Tooltip
                          className="w-50"
                          id="SiblingRelationshipsTooltip"
                        >
                          {getTooltip("SiblingRelationships")}
                        </Tooltip>
                      </div>
                      <div className="col py-2 bg-body border-start border-5">
                        <FormInput
                          name="SiblingRelationships"
                          value={getFieldValue(data.SiblingRelationships)}
                          onChange={handleChange}
                          identical={compare("SiblingRelationships")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          type="number"
                          min="0"
                          max="3"
                        />
                      </div>
                    </div>
                    <div className="mx-2 row border-bottom">
                      <div
                        className="col py-2 bg-body"
                        data-tooltip-id="ExtendedRelationshipsTooltip"
                      >
                        Extended Family Relations
                        {errors.ExtendedRelationships !== undefined ? (
                          <div className="alert alert-danger">
                            {errors.ExtendedRelationships}
                          </div>
                        ) : null}
                        <Tooltip
                          className="w-50"
                          id="ExtendedRelationshipsTooltip"
                        >
                          {getTooltip("ExtendedRelationships")}
                        </Tooltip>
                      </div>
                      <div className="col py-2 bg-body border-start border-5">
                        <FormInput
                          name="ExtendedRelationships"
                          value={getFieldValue(data.ExtendedRelationships)}
                          onChange={handleChange}
                          identical={compare("ExtendedRelationships")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          type="number"
                          min="0"
                          max="3"
                        />
                      </div>
                    </div>
                    <div className="mx-2 row border-bottom">
                      <div
                        className="col py-2 bg-body"
                        data-tooltip-id="NaturalSupportsTooltip"
                      >
                        Natural Supports
                        {errors.NaturalSupports !== undefined ? (
                          <div className="alert alert-danger">
                            {errors.NaturalSupports}
                          </div>
                        ) : null}
                        <Tooltip className="w-50" id="NaturalSupportsTooltip">
                          {getTooltip("NaturalSupports")}
                        </Tooltip>
                      </div>
                      <div className="col py-2 bg-body border-start border-5">
                        <FormInput
                          name="NaturalSupports"
                          value={getFieldValue(data.NaturalSupports)}
                          onChange={handleChange}
                          identical={compare("NaturalSupports")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          type="number"
                          min="0"
                          max="3"
                        />
                      </div>
                    </div>
                    <div className="mx-2 row border-bottom">
                      <div
                        className="col py-2 bg-body"
                        data-tooltip-id="FamilyTraditionsTooltip"
                      >
                        Traditions and Cultural Rituals
                        {errors.FamilyTraditions !== undefined ? (
                          <div className="alert alert-danger">
                            {errors.FamilyTraditions}
                          </div>
                        ) : null}
                        <Tooltip className="w-50" id="FamilyTraditionsTooltip">
                          {getTooltip("FamilyTraditions")}
                        </Tooltip>
                      </div>
                      <div className="col py-2 bg-body border-start border-5">
                        <FormInput
                          name="FamilyTraditions"
                          value={getFieldValue(data.FamilyTraditions)}
                          onChange={handleChange}
                          identical={compare("FamilyTraditions")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          type="number"
                          min="0"
                          max="3"
                        />
                      </div>
                    </div>
                  </React.Fragment>
                  <React.Fragment>
                    <div className="m-2 row">
                      <div className="col-6">
                        <div className="h5 text-white">Advocacy & Capacity</div>
                      </div>
                      <div className="col-1 bg-body py-2">
                        {getFamilyScores("advocacyAndCapacity")}
                      </div>
                      <div className="col"></div>
                    </div>
                    <div className="mx-2 row border-bottom bg-body">
                      <div className="col py-2 bg-light">
                        For Advocacy & Capacity use the following categories and
                        skill level ratings:
                        <br />0 - Mastery, could teach others. Maintain mastery.{" "}
                        <br />
                        1 - Comfortable and routine. Develop mastery. <br />
                        2 - Comfortable, but not routine. Build into a routine.
                        <br />
                        3 - Tried; Not yet comfortable. Develop comfort.
                        <br />4 - Have never done. Try the skill.
                      </div>
                    </div>
                    <div className="mx-2 row border-bottom">
                      <div
                        className="col py-2 bg-body"
                        data-tooltip-id="KnowledgeOfNeedsTooltip"
                      >
                        Knowledge of Family-Child Needs
                        {errors.KnowledgeOfNeeds !== undefined ? (
                          <div className="alert alert-danger">
                            {errors.KnowledgeOfNeeds}
                          </div>
                        ) : null}
                        <Tooltip className="w-50" id="KnowledgeOfNeedsTooltip">
                          {getTooltip("KnowledgeOfNeeds")}
                        </Tooltip>
                      </div>
                      <div className="col py-2 bg-body border-start border-5">
                        <FormInput
                          name="KnowledgeOfNeeds"
                          value={getFieldValue(data.KnowledgeOfNeeds)}
                          onChange={handleChange}
                          identical={compare("KnowledgeOfNeeds")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          type="number"
                          min="0"
                          max="3"
                        />
                      </div>
                    </div>
                    <div className="mx-2 row border-bottom">
                      <div
                        className="col py-2 bg-body"
                        data-tooltip-id="KnowledgeOfOptionsTooltip"
                      >
                        Knowledge of Service Options
                        {errors.KnowledgeOfOptions !== undefined ? (
                          <div className="alert alert-danger">
                            {errors.KnowledgeOfOptions}
                          </div>
                        ) : null}
                        <Tooltip
                          className="w-50"
                          id="KnowledgeOfOptionsTooltip"
                        >
                          {getTooltip("KnowledgeOfOptions")}
                        </Tooltip>
                      </div>
                      <div className="col py-2 bg-body border-start border-5">
                        <FormInput
                          name="KnowledgeOfOptions"
                          value={getFieldValue(data.KnowledgeOfOptions)}
                          onChange={handleChange}
                          identical={compare("KnowledgeOfOptions")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          type="number"
                          min="0"
                          max="3"
                        />
                      </div>
                    </div>
                    <div className="mx-2 row border-bottom">
                      <div
                        className="col py-2 bg-body"
                        data-tooltip-id="KnowledgeOfRightsTooltip"
                      >
                        Knowledge of Rights & Responsibilities
                        {errors.KnowledgeOfRights !== undefined ? (
                          <div className="alert alert-danger">
                            {errors.KnowledgeOfRights}
                          </div>
                        ) : null}
                        <Tooltip className="w-50" id="KnowledgeOfRightsTooltip">
                          {getTooltip("KnowledgeOfRights")}
                        </Tooltip>
                      </div>
                      <div className="col py-2 bg-body border-start border-5">
                        <FormInput
                          name="KnowledgeOfRights"
                          value={getFieldValue(data.KnowledgeOfRights)}
                          onChange={handleChange}
                          identical={compare("KnowledgeOfRights")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          type="number"
                          min="0"
                          max="3"
                        />
                      </div>
                    </div>
                    <div className="mx-2 row border-bottom">
                      <div
                        className="col py-2 bg-body"
                        data-tooltip-id="AbilityToListenTooltip"
                      >
                        Ability to Listen
                        {errors.AbilityToListen !== undefined ? (
                          <div className="alert alert-danger">
                            {errors.AbilityToListen}
                          </div>
                        ) : null}
                        <Tooltip className="w-50" id="AbilityToListenTooltip">
                          {getTooltip("AbilityToListen")}
                        </Tooltip>
                      </div>
                      <div className="col py-2 bg-body border-start border-5">
                        <FormInput
                          name="AbilityToListen"
                          value={getFieldValue(data.AbilityToListen)}
                          onChange={handleChange}
                          identical={compare("AbilityToListen")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          type="number"
                          min="0"
                          max="3"
                        />
                      </div>
                    </div>
                    <div className="mx-2 row border-bottom">
                      <div
                        className="col py-2 bg-body"
                        data-tooltip-id="AbilityToCommunicateTooltip"
                      >
                        Ability to Communicate
                        {errors.AbilityToCommunicate !== undefined ? (
                          <div className="alert alert-danger">
                            {errors.AbilityToCommunicate}
                          </div>
                        ) : null}
                        <Tooltip
                          className="w-50"
                          id="AbilityToCommunicateTooltip"
                        >
                          {getTooltip("AbilityToCommunicate")}
                        </Tooltip>
                      </div>
                      <div className="col py-2 bg-body border-start border-5">
                        <FormInput
                          name="AbilityToCommunicate"
                          value={getFieldValue(data.AbilityToCommunicate)}
                          onChange={handleChange}
                          identical={compare("AbilityToCommunicate")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          type="number"
                          min="0"
                          max="3"
                        />
                      </div>
                    </div>
                  </React.Fragment>
                  {
                    // Cultural Factors
                  }
                  <React.Fragment>
                    <div className="m-2 row">
                      <div className="col-6 py-2">
                        <div className="h5 text-white">Cultural Factors</div>
                      </div>
                      <div className="col-1 bg-body py-2">
                        {getFamilyScores("culturalFactors")}
                      </div>
                      <div className="col"></div>
                    </div>
                    <div className="mx-2 row border-bottom bg-body">
                      <div className="col py-2 bg-light">
                        For Cultural Factors, use the following categories and
                        action levels:
                        <br />
                        0 - No evidence of any needs; no need for action.
                        <br />
                        1 - Identified need that requires monitoring, watchful
                        waiting, or preventive action based on history,
                        suspicion or disagreement.
                        <br />
                        2 - Action is required to ensure that the identified
                        need is addressed; need is interfering with functioning.
                        <br />3 - Need is dangerous or disabling; requires
                        immediate and/or intensive action.
                      </div>
                    </div>
                    <div className="mx-2 row border-bottom">
                      <div
                        className="col py-2 bg-body"
                        data-tooltip-id="FamilyLanguageTooltip"
                      >
                        Language
                        {errors.FamilyLanguage !== undefined ? (
                          <div className="alert alert-danger">
                            {errors.FamilyLanguage}
                          </div>
                        ) : null}
                        <Tooltip className="w-50" id="FamilyLanguageTooltip">
                          {getTooltip("FamilyLanguage")}
                        </Tooltip>
                      </div>
                      <div className="col py-2 bg-body border-start border-5">
                        <FormInput
                          name="FamilyLanguage"
                          value={getFieldValue(data.FamilyLanguage)}
                          onChange={handleChange}
                          identical={compare("FamilyLanguage")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          type="number"
                          min="0"
                          max="3"
                        />
                      </div>
                    </div>
                    <div className="mx-2 row border-bottom">
                      <div
                        className="col py-2 bg-body"
                        data-tooltip-id="FamilyCulturalStressTooltip"
                      >
                        Cultural Stress
                        {errors.FamilyCulturalStress !== undefined ? (
                          <div className="alert alert-danger">
                            {errors.FamilyCulturalStress}
                          </div>
                        ) : null}
                        <Tooltip
                          className="w-50"
                          id="FamilyCulturalStressTooltip"
                        >
                          {getTooltip("FamilyCulturalStress")}
                        </Tooltip>
                      </div>
                      <div className="col py-2 bg-body border-start border-5">
                        <FormInput
                          name="FamilyCulturalStress"
                          value={getFieldValue(data.FamilyCulturalStress)}
                          onChange={handleChange}
                          identical={compare("FamilyCulturalStress")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          type="number"
                          min="0"
                          max="3"
                        />
                      </div>
                    </div>
                  </React.Fragment>
                  <div className="mx-2 row border-bottom bg-body">
                    <div className="col pt-2">
                      <FormTextArea
                        name="FamilyRationale"
                        label="Please write a rationale for any item in Family Assessment Domain rated actionable ('2' or '3', or '3' or '4' for Advocacy & Capacity)."
                        error={errors.FamilyRationale}
                        value={getFieldValue(data.FamilyRationale)}
                        onChange={handleChange}
                        identical={compare("FamilyRationale")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                </div>
              </ExpandDiv>
              <ExpandDiv
                id="CaregiverAssessment"
                expandStatus={expandStatus}
                className="mt-2"
                title="Caregiver Assessment"
              >
                <div className="container bg-secondary p-2">
                  <FieldTable
                    name="CaregiverAssessments"
                    commentsHandler={handleComments}
                  >
                    {data.CaregiverAssessments["@value"]
                      ? JSON.parse(data.CaregiverAssessments["@value"]).map(
                          (item, index) => (
                            <React.Fragment
                              key={`CaregiverAssessments.${item._id || index}`}
                            >
                              <div className="m-2 row border-bottom bg-body">
                                <div className="col pb-2">
                                  <FormInput
                                    name="Name"
                                    label={
                                      <>
                                        {index + 1}. Caregiver's Name
                                        {!formDisabled ? (
                                          <i
                                            className="far fa-trash-alt ms-2 me-2 d-print-none"
                                            onClick={() => {
                                              handleDeleteRow(
                                                index,
                                                "CaregiverAssessments"
                                              );
                                            }}
                                          />
                                        ) : null}
                                      </>
                                    }
                                    value={item.Name}
                                    error={item.Errors?.Name}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "CaregiverAssessments"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                </div>
                                <div className="col pb-2">
                                  <FormInput
                                    name="DateCompleted"
                                    label="Date Completed"
                                    value={getFieldValue(data.DateCompleted)}
                                    onChange={handleChange}
                                    identical={compare("DateCompleted")}
                                    commentsHandler={handleComments}
                                    disabled
                                    type="date"
                                  />
                                </div>
                              </div>
                              {errors.CaregiverAssessments !== undefined ? (
                                <div className="alert alert-danger">
                                  {errors.CaregiverAssessments}
                                </div>
                              ) : null}
                              {
                                // Caregiver Needs
                              }
                              <React.Fragment>
                                <div className="m-2 row">
                                  <div className="col-6 py-2">
                                    <div className="h5 text-white">
                                      Caregiver Needs
                                    </div>
                                  </div>
                                  <div className="col-1 bg-body py-2">
                                    {getTableScores(
                                      "CaregiverAssessments",
                                      "caregiverNeeds",
                                      index
                                    )}
                                  </div>
                                  <div className="col"></div>
                                </div>
                                <div className="mx-2 row border-bottom bg-body">
                                  <div className="col py-2 bg-light">
                                    For the Caregiver Needs, use the following
                                    categories and action levels:
                                    <br />
                                    0 - No evidence of any needs; no need for
                                    action.
                                    <br />
                                    1 - Identified need that requires
                                    monitoring, watchful waiting, or preventive
                                    action based on history, suspicion or
                                    disagreement.
                                    <br />
                                    2 - Action is required to ensure that the
                                    identified need is addressed; need is
                                    interfering with functioning.
                                    <br />3 - Need is dangerous or disabling;
                                    requires immediate and/or intensive action.
                                  </div>
                                </div>
                                <div className="mx-2 row border-bottom">
                                  <div
                                    className="col py-2 bg-body"
                                    data-tooltip-id={`Monitoring${index}`}
                                  >
                                    Monitoring
                                    <Tooltip
                                      className="w-50"
                                      id={`Monitoring${index}`}
                                    >
                                      {getTooltip("CaregiverMonitoring")}
                                    </Tooltip>
                                  </div>
                                  <div className="col py-2 bg-body border-start border-5">
                                    <FormInput
                                      name="Monitoring"
                                      value={item.Monitoring}
                                      error={item.Errors?.Monitoring}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "CaregiverAssessments"
                                        );
                                      }}
                                      disabled={formDisabled}
                                      type="number"
                                      min="0"
                                      max="3"
                                    />
                                  </div>
                                </div>
                                <div className="mx-2 row border-bottom">
                                  <div
                                    className="col py-2 bg-body"
                                    data-tooltip-id={`Knowledge${index}`}
                                  >
                                    Knowledge
                                    <Tooltip
                                      className="w-50"
                                      id={`Knowledge${index}`}
                                    >
                                      {getTooltip("CaregiverKnowledge")}
                                    </Tooltip>
                                  </div>
                                  <div className="col py-2 bg-body border-start border-5">
                                    <FormInput
                                      name="Knowledge"
                                      value={item.Knowledge}
                                      error={item.Errors?.Knowledge}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "CaregiverAssessments"
                                        );
                                      }}
                                      disabled={formDisabled}
                                      type="number"
                                      min="0"
                                      max="3"
                                    />
                                  </div>
                                </div>
                                <div className="mx-2 row border-bottom">
                                  <div
                                    className="col py-2 bg-body"
                                    data-tooltip-id={`Boundaries${index}`}
                                  >
                                    Boundaries
                                    <Tooltip
                                      className="w-50"
                                      id={`Boundaries${index}`}
                                    >
                                      {getTooltip("CaregiverBoundaries")}
                                    </Tooltip>
                                  </div>
                                  <div className="col py-2 bg-body border-start border-5">
                                    <FormInput
                                      name="Boundaries"
                                      value={item.Boundaries}
                                      error={item.Errors?.Boundaries}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "CaregiverAssessments"
                                        );
                                      }}
                                      disabled={formDisabled}
                                      type="number"
                                      min="0"
                                      max="3"
                                    />
                                  </div>
                                </div>
                                <div className="mx-2 row border-bottom">
                                  <div
                                    className="col py-2 bg-body"
                                    data-tooltip-id={`Discipline${index}`}
                                  >
                                    Discipline
                                    <Tooltip
                                      className="w-50"
                                      id={`Discipline${index}`}
                                    >
                                      {getTooltip("CaregiverDiscipline")}
                                    </Tooltip>
                                  </div>
                                  <div className="col py-2 bg-body border-start border-5">
                                    <FormInput
                                      name="Discipline"
                                      value={item.Discipline}
                                      error={item.Errors?.Discipline}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "CaregiverAssessments"
                                        );
                                      }}
                                      disabled={formDisabled}
                                      type="number"
                                      min="0"
                                      max="3"
                                    />
                                  </div>
                                </div>
                                <div className="mx-2 row border-bottom">
                                  <div
                                    className="col py-2 bg-body"
                                    data-tooltip-id={`Medical${index}`}
                                  >
                                    Medical/Physical
                                    <Tooltip
                                      className="w-50"
                                      id={`Medical${index}`}
                                    >
                                      {getTooltip("CaregiverMedical")}
                                    </Tooltip>
                                  </div>
                                  <div className="col py-2 bg-body border-start border-5">
                                    <FormInput
                                      name="Medical"
                                      value={item.Medical}
                                      error={item.Errors?.Medical}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "CaregiverAssessments"
                                        );
                                      }}
                                      disabled={formDisabled}
                                      type="number"
                                      min="0"
                                      max="3"
                                    />
                                  </div>
                                </div>
                                <div className="mx-2 row border-bottom">
                                  <div
                                    className="col py-2 bg-body"
                                    data-tooltip-id={`MentalHealth${index}`}
                                  >
                                    Mental Health
                                    <Tooltip
                                      className="w-50"
                                      id={`MentalHealth${index}`}
                                    >
                                      {getTooltip("CaregiverMentalHealth")}
                                    </Tooltip>
                                  </div>
                                  <div className="col py-2 bg-body border-start border-5">
                                    <FormInput
                                      name="MentalHealth"
                                      value={item.MentalHealth}
                                      error={item.Errors?.MentalHealth}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "CaregiverAssessments"
                                        );
                                      }}
                                      disabled={formDisabled}
                                      type="number"
                                      min="0"
                                      max="3"
                                    />
                                  </div>
                                </div>
                                <div className="mx-2 row border-bottom">
                                  <div
                                    className="col py-2 bg-body"
                                    data-tooltip-id={`Developmental${index}`}
                                  >
                                    Developmental
                                    <Tooltip
                                      className="w-50"
                                      id={`Developmental${index}`}
                                    >
                                      {getTooltip("CaregiverDevelopmental")}
                                    </Tooltip>
                                  </div>
                                  <div className="col py-2 bg-body border-start border-5">
                                    <FormInput
                                      name="Developmental"
                                      value={item.Developmental}
                                      error={item.Errors?.Developmental}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "CaregiverAssessments"
                                        );
                                      }}
                                      disabled={formDisabled}
                                      type="number"
                                      min="0"
                                      max="3"
                                    />
                                  </div>
                                </div>
                                <div className="mx-2 row border-bottom">
                                  <div
                                    className="col py-2 bg-body"
                                    data-tooltip-id={`SubstanceUse${index}`}
                                  >
                                    Substance Use
                                    <Tooltip
                                      className="w-50"
                                      id={`SubstanceUse${index}`}
                                    >
                                      {getTooltip("CaregiverSubstanceUse")}
                                    </Tooltip>
                                  </div>
                                  <div className="col py-2 bg-body border-start border-5">
                                    <FormInput
                                      name="SubstanceUse"
                                      value={item.SubstanceUse}
                                      error={item.Errors?.SubstanceUse}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "CaregiverAssessments"
                                        );
                                      }}
                                      disabled={formDisabled}
                                      type="number"
                                      min="0"
                                      max="3"
                                    />
                                  </div>
                                </div>
                                <div className="mx-2 row border-bottom">
                                  <div
                                    className="col py-2 bg-body"
                                    data-tooltip-id={`TraumaticReaction${index}`}
                                  >
                                    Posttraumatic Reactions
                                    <Tooltip
                                      className="w-50"
                                      id={`TraumaticReaction${index}`}
                                    >
                                      {getTooltip("CaregiverTraumaticReaction")}
                                    </Tooltip>
                                  </div>
                                  <div className="col py-2 bg-body border-start border-5">
                                    <FormInput
                                      name="TraumaticReaction"
                                      value={item.TraumaticReaction}
                                      error={item.Errors?.TraumaticReaction}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "CaregiverAssessments"
                                        );
                                      }}
                                      disabled={formDisabled}
                                      type="number"
                                      min="0"
                                      max="3"
                                    />
                                  </div>
                                </div>
                                {parseInt(item.MentalHealth) > 0 ? (
                                  <>
                                    <div className="mx-2 row border-bottom">
                                      <div className="col py-2 text-white">
                                        <span className="h5">
                                          A{index + 1} Mental Health Module
                                        </span>{" "}
                                        <i>
                                          (Needs to be completed when the Mental
                                          Health item is rated '1', '2' or '3'.)
                                        </i>
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`Psychosis${index}`}
                                      >
                                        Psychosis (Thought Disorder)
                                        <Tooltip
                                          className="w-50"
                                          id={`Psychosis${index}`}
                                        >
                                          {getTooltip("CaregiverPsychosis")}
                                        </Tooltip>
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <FormInput
                                          name="Psychosis"
                                          value={item.Psychosis}
                                          error={item.Errors?.Psychosis}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "CaregiverAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                          type="number"
                                          min="0"
                                          max="3"
                                        />
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`MoodDisturbance${index}`}
                                      >
                                        Mood Disturbance
                                        <Tooltip
                                          className="w-50"
                                          id={`MoodDisturbance${index}`}
                                        >
                                          {getTooltip(
                                            "CaregiverMoodDisturbance"
                                          )}
                                        </Tooltip>
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <FormInput
                                          name="MoodDisturbance"
                                          value={item.MoodDisturbance}
                                          error={item.Errors?.MoodDisturbance}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "CaregiverAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                          type="number"
                                          min="0"
                                          max="3"
                                        />
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`Depression${index}`}
                                      >
                                        Depression
                                        <Tooltip
                                          className="w-50"
                                          id={`Depression${index}`}
                                        >
                                          {getTooltip("CaregiverDepression")}
                                        </Tooltip>
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <FormInput
                                          name="Depression"
                                          value={item.Depression}
                                          error={item.Errors?.Depression}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "CaregiverAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                          type="number"
                                          min="0"
                                          max="3"
                                        />
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`ImpulseControl${index}`}
                                      >
                                        Impulse Control
                                        <Tooltip
                                          className="w-50"
                                          id={`ImpulseControl${index}`}
                                        >
                                          {getTooltip(
                                            "CaregiverImpulseControl"
                                          )}
                                        </Tooltip>
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <FormInput
                                          name="ImpulseControl"
                                          value={item.ImpulseControl}
                                          error={item.Errors?.ImpulseControl}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "CaregiverAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                          type="number"
                                          min="0"
                                          max="3"
                                        />
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`Anxiety${index}`}
                                      >
                                        Anxiety
                                        <Tooltip
                                          className="w-50"
                                          id={`Anxiety${index}`}
                                        >
                                          {getTooltip("CaregiverAnxiety")}
                                        </Tooltip>
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <FormInput
                                          name="Anxiety"
                                          value={item.Anxiety}
                                          error={item.Errors?.Anxiety}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "CaregiverAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                          type="number"
                                          min="0"
                                          max="3"
                                        />
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`Problems${index}`}
                                      >
                                        Interpersonal Problems
                                        <Tooltip
                                          className="w-50"
                                          id={`Problems${index}`}
                                        >
                                          {getTooltip("CaregiverProblems")}
                                        </Tooltip>
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <FormInput
                                          name="Problems"
                                          value={item.Problems}
                                          error={item.Errors?.Problems}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "CaregiverAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                          type="number"
                                          min="0"
                                          max="3"
                                        />
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`Antisocial${index}`}
                                      >
                                        Antisocial Behavior
                                        <Tooltip
                                          className="w-50"
                                          id={`Antisocial${index}`}
                                        >
                                          {getTooltip("CaregiverAntisocial")}
                                        </Tooltip>
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <FormInput
                                          name="Antisocial"
                                          value={item.Antisocial}
                                          error={item.Errors?.Antisocial}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "CaregiverAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                          type="number"
                                          min="0"
                                          max="3"
                                        />
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`Anger${index}`}
                                      >
                                        Anger Control
                                        <Tooltip
                                          className="w-50"
                                          id={`Anger${index}`}
                                        >
                                          {getTooltip("CaregiverAnger")}
                                        </Tooltip>
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <FormInput
                                          name="Anger"
                                          value={item.Anger}
                                          error={item.Errors?.Anger}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "CaregiverAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                          type="number"
                                          min="0"
                                          max="3"
                                        />
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`EatingDisturbance${index}`}
                                      >
                                        Eating Disturbances
                                        <Tooltip
                                          id={`EatingDisturbance${index}`}
                                        >
                                          {getTooltip(
                                            "CaregiverEatingDisturbance"
                                          )}
                                        </Tooltip>
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <FormInput
                                          name="EatingDisturbance"
                                          value={item.EatingDisturbance}
                                          error={item.Errors?.EatingDisturbance}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "CaregiverAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                          type="number"
                                          min="0"
                                          max="3"
                                        />
                                      </div>
                                    </div>
                                  </>
                                ) : null}
                                {parseInt(item.SubstanceUse) > 0 ? (
                                  <>
                                    <div className="mx-2 row border-bottom">
                                      <div className="col py-2 text-white">
                                        <span className="h5">
                                          B{index + 1} Substance Use Disorder
                                          Module
                                        </span>{" "}
                                        <i>
                                          (Needs to be completed when the
                                          Substance Use item is rated '1', '2'
                                          or '3'.)
                                        </i>
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`SUSeverity${index}`}
                                      >
                                        Severity of Use
                                        <Tooltip
                                          className="w-50"
                                          id={`SUSeverity${index}`}
                                        >
                                          {getTooltip("CaregiverSUSeverity")}
                                        </Tooltip>
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <FormInput
                                          name="SUSeverity"
                                          value={item.SUSeverity}
                                          error={item.Errors?.SUSeverity}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "CaregiverAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                          type="number"
                                          min="0"
                                          max="3"
                                        />
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`SUDuration${index}`}
                                      >
                                        Duration of Use
                                        <Tooltip
                                          className="w-50"
                                          id={`SUDuration${index}`}
                                        >
                                          {getTooltip("CaregiverSUDuration")}
                                        </Tooltip>
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <FormInput
                                          name="SUDuration"
                                          value={item.SUDuration}
                                          error={item.Errors?.SUDuration}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "CaregiverAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                          type="number"
                                          min="0"
                                          max="3"
                                        />
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`RecoveryStage${index}`}
                                      >
                                        Stage of Recovery
                                        <Tooltip
                                          className="w-50"
                                          id={`RecoveryStage${index}`}
                                        >
                                          {getTooltip("CaregiverRecoveryStage")}
                                        </Tooltip>
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <FormInput
                                          name="RecoveryStage"
                                          value={item.RecoveryStage}
                                          error={item.Errors?.RecoveryStage}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "CaregiverAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                          type="number"
                                          min="0"
                                          max="3"
                                        />
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`PeerInfluences${index}`}
                                      >
                                        Peer Influences
                                        <Tooltip
                                          className="w-50"
                                          id={`PeerInfluences${index}`}
                                        >
                                          {getTooltip(
                                            "CaregiverPeerInfluences"
                                          )}
                                        </Tooltip>
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <FormInput
                                          name="PeerInfluences"
                                          value={item.PeerInfluences}
                                          error={item.Errors?.PeerInfluences}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "CaregiverAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                          type="number"
                                          min="0"
                                          max="3"
                                        />
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`EnvInfluences${index}`}
                                      >
                                        Environmental Influences
                                        <Tooltip
                                          className="w-50"
                                          id={`EnvInfluences${index}`}
                                        >
                                          {getTooltip("CaregiverEnvInfluences")}
                                        </Tooltip>
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <FormInput
                                          name="EnvInfluences"
                                          value={item.EnvInfluences}
                                          error={item.Errors?.EnvInfluences}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "CaregiverAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                          type="number"
                                          min="0"
                                          max="3"
                                        />
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`RecoverySupport${index}`}
                                      >
                                        Recovery Support in Community
                                        <Tooltip
                                          className="w-50"
                                          id={`RecoverySupport${index}`}
                                        >
                                          {getTooltip(
                                            "CaregiverRecoverySupport"
                                          )}
                                        </Tooltip>
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <FormInput
                                          name="RecoverySupport"
                                          value={item.RecoverySupport}
                                          error={item.Errors?.RecoverySupport}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "CaregiverAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                          type="number"
                                          min="0"
                                          max="3"
                                        />
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`AcuteIntoxication${index}`}
                                      >
                                        Acute Intoxication
                                        <Tooltip
                                          id={`AcuteIntoxication${index}`}
                                        >
                                          {getTooltip(
                                            "CaregiverAcuteIntoxication"
                                          )}
                                        </Tooltip>
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <FormInput
                                          name="AcuteIntoxication"
                                          value={item.AcuteIntoxication}
                                          error={item.Errors?.AcuteIntoxication}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "CaregiverAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                          type="number"
                                          min="0"
                                          max="3"
                                        />
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`WithdrawalHistory${index}`}
                                      >
                                        Withdrawal History
                                        <Tooltip
                                          id={`WithdrawalHistory${index}`}
                                        >
                                          {getTooltip(
                                            "CaregiverWithdrawalHistory"
                                          )}
                                        </Tooltip>
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <FormInput
                                          name="WithdrawalHistory"
                                          value={item.WithdrawalHistory}
                                          error={item.Errors?.WithdrawalHistory}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "CaregiverAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                          type="number"
                                          min="0"
                                          max="3"
                                        />
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`WithdrawalRisks${index}`}
                                      >
                                        Withdrawal Risks
                                        <Tooltip
                                          className="w-50"
                                          id={`WithdrawalRisks${index}`}
                                        >
                                          {getTooltip(
                                            "CaregiverWithdrawalRisks"
                                          )}
                                        </Tooltip>
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <FormInput
                                          name="WithdrawalRisks"
                                          value={item.WithdrawalRisks}
                                          error={item.Errors?.WithdrawalRisks}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "CaregiverAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                          type="number"
                                          min="0"
                                          max="3"
                                        />
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`TriggerAwareness${index}`}
                                      >
                                        Awareness of Relapse Triggers
                                        <Tooltip
                                          id={`TriggerAwareness${index}`}
                                        >
                                          {getTooltip(
                                            "CaregiverTriggerAwareness"
                                          )}
                                        </Tooltip>
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <FormInput
                                          name="TriggerAwareness"
                                          value={item.TriggerAwareness}
                                          error={item.Errors?.TriggerAwareness}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "CaregiverAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                          type="number"
                                          min="0"
                                          max="3"
                                        />
                                      </div>
                                    </div>
                                  </>
                                ) : null}
                                {parseInt(item.TraumaticReaction) > 0 ? (
                                  <>
                                    <div className="mx-2 row border-bottom">
                                      <div className="col py-2 text-white">
                                        <span className="h5">
                                          C{index + 1} Trauma Module
                                        </span>{" "}
                                        <i>
                                          (Needs to be completed when the
                                          Adjustment to Trauma item is rated
                                          '1', '2' or '3'.)
                                        </i>
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom bg-body">
                                      <div className="col py-2 bg-light">
                                        For the Trauma Module: Potentially
                                        Traumatic/Adverse Childhood Experiences,
                                        use the following categories and
                                        ratings:
                                        <br />
                                        No - No evidence of any trauma of this
                                        type.
                                        <br />
                                        Yes - Individual has had experience or
                                        there is suspicion that the individual
                                        has experienced this type of trauma—one
                                        incident, multiple incidents, or
                                        chronic, on-going experiences.
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`SexualAbuse${index}`}
                                      >
                                        Sexual Abuse
                                        <Tooltip
                                          className="w-50"
                                          id={`SexualAbuse${index}`}
                                        >
                                          {getTooltip("CaregiverSexualAbuse")}
                                        </Tooltip>
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <Select
                                          name="SexualAbuse"
                                          value={item.SexualAbuse}
                                          error={item.Errors?.SexualAbuse}
                                          options={yesNoOptions}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "CaregiverAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`PhysicalAbuse${index}`}
                                      >
                                        Physical Abuse
                                        <Tooltip
                                          className="w-50"
                                          id={`PhysicalAbuse${index}`}
                                        >
                                          {getTooltip("CaregiverPhysicalAbuse")}
                                        </Tooltip>
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <Select
                                          name="PhysicalAbuse"
                                          value={item.PhysicalAbuse}
                                          error={item.Errors?.PhysicalAbuse}
                                          options={yesNoOptions}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "CaregiverAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`Neglect${index}`}
                                      >
                                        Neglect
                                        <Tooltip
                                          className="w-50"
                                          id={`Neglect${index}`}
                                        >
                                          {getTooltip("CaregiverNeglect")}
                                        </Tooltip>
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <Select
                                          name="Neglect"
                                          value={item.Neglect}
                                          error={item.Errors?.Neglect}
                                          options={yesNoOptions}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "CaregiverAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`EmotionalAbuse${index}`}
                                      >
                                        Emotional Abuse
                                        <Tooltip
                                          className="w-50"
                                          id={`EmotionalAbuse${index}`}
                                        >
                                          {getTooltip(
                                            "CaregiverEmotionalAbuse"
                                          )}
                                        </Tooltip>
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <Select
                                          name="EmotionalAbuse"
                                          value={item.EmotionalAbuse}
                                          error={item.Errors?.EmotionalAbuse}
                                          options={yesNoOptions}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "CaregiverAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`MedicalTrauma${index}`}
                                      >
                                        Medical Trauma
                                        <Tooltip
                                          className="w-50"
                                          id={`MedicalTrauma${index}`}
                                        >
                                          {getTooltip("CaregiverMedicalTrauma")}
                                        </Tooltip>
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <Select
                                          name="MedicalTrauma"
                                          value={item.MedicalTrauma}
                                          error={item.Errors?.MedicalTrauma}
                                          options={yesNoOptions}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "CaregiverAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`DisasterTrauma${index}`}
                                      >
                                        Natural or Manmade Disaster
                                        <Tooltip
                                          className="w-50"
                                          id={`DisasterTrauma${index}`}
                                        >
                                          {getTooltip(
                                            "CaregiverDisasterTrauma"
                                          )}
                                        </Tooltip>
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <Select
                                          name="DisasterTrauma"
                                          value={item.DisasterTrauma}
                                          error={item.Errors?.DisasterTrauma}
                                          options={yesNoOptions}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "CaregiverAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`FamilyViolence${index}`}
                                      >
                                        Witness to Family Violence
                                        <Tooltip
                                          className="w-50"
                                          id={`FamilyViolence${index}`}
                                        >
                                          {getTooltip(
                                            "CaregiverFamilyViolence"
                                          )}
                                        </Tooltip>
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <Select
                                          name="FamilyViolence"
                                          value={item.FamilyViolence}
                                          error={item.Errors?.FamilyViolence}
                                          options={yesNoOptions}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "CaregiverAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`SchoolViolence${index}`}
                                      >
                                        Witness to Comm./School Violence
                                        <Tooltip
                                          className="w-50"
                                          id={`SchoolViolence${index}`}
                                        >
                                          {getTooltip(
                                            "CaregiverSchoolViolence"
                                          )}
                                        </Tooltip>
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <Select
                                          name="SchoolViolence"
                                          value={item.SchoolViolence}
                                          error={item.Errors?.SchoolViolence}
                                          options={yesNoOptions}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "CaregiverAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`WarTrauma${index}`}
                                      >
                                        War/Terrorism Affected
                                        <Tooltip
                                          className="w-50"
                                          id={`WarTrauma${index}`}
                                        >
                                          {getTooltip("CaregiverWarTrauma")}
                                        </Tooltip>
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <Select
                                          name="WarTrauma"
                                          value={item.WarTrauma}
                                          error={item.Errors?.WarTrauma}
                                          options={yesNoOptions}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "CaregiverAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`CriminalActsTrauma${index}`}
                                      >
                                        Witness/Victim of Criminal Acts
                                        <Tooltip
                                          id={`CriminalActsTrauma${index}`}
                                        >
                                          {getTooltip(
                                            "CaregiverCriminalActsTrauma"
                                          )}
                                        </Tooltip>
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <Select
                                          name="CriminalActsTrauma"
                                          value={item.CriminalActsTrauma}
                                          error={
                                            item.Errors?.CriminalActsTrauma
                                          }
                                          options={yesNoOptions}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "CaregiverAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`ParentalCriminalTrauma${index}`}
                                      >
                                        Parental Criminal Behavior
                                        {/* <Tooltip className="w-50" id={`ParentalCriminalTrauma${index}`}>{getTooltip("CaregiverParentalCriminalTrauma")}</Tooltip> */}
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <Select
                                          name="ParentalCriminalTrauma"
                                          value={item.ParentalCriminalTrauma}
                                          error={
                                            item.Errors?.ParentalCriminalTrauma
                                          }
                                          options={yesNoOptions}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "CaregiverAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`AttachmentTrauma${index}`}
                                      >
                                        Disrupt. in Caregiving/Attachment
                                        <Tooltip
                                          id={`AttachmentTrauma${index}`}
                                        >
                                          {getTooltip(
                                            "CaregiverAttachmentTrauma"
                                          )}
                                        </Tooltip>
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <Select
                                          name="AttachmentTrauma"
                                          value={item.AttachmentTrauma}
                                          error={item.Errors?.AttachmentTrauma}
                                          options={yesNoOptions}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "CaregiverAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </div>
                                    </div>
                                  </>
                                ) : null}
                                <div className="mx-2 row border-bottom bg-body">
                                  <div className="col pt-2">
                                    <FormTextArea
                                      name="NeedsRationale"
                                      label="Please write a rationale for any item in the Caregiver Needs or Modules above rated actionable ('2' or '3')."
                                      value={item.NeedsRationale}
                                      error={item.Errors?.NeedsRationale}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "CaregiverAssessments"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  </div>
                                </div>
                              </React.Fragment>
                              {
                                // Caregiver Strengths
                              }
                              <React.Fragment>
                                <div className="m-2 row">
                                  <div className="col-6">
                                    <div className="h5 text-white">
                                      Caregiver Strengths
                                    </div>
                                  </div>
                                  <div className="col-1 bg-body py-2">
                                    {getTableScores(
                                      "CaregiverAssessments",
                                      "caregiverStrengths",
                                      index
                                    )}
                                  </div>
                                  <div className="col"></div>
                                </div>
                                <div className="mx-2 row border-bottom bg-body">
                                  <div className="col py-2 bg-light">
                                    For Caregiver Strengths, use the following
                                    categories and action levels:
                                    <br />
                                    0 - Well-developed, centerpiece strength;
                                    may be used as a focus of an
                                    intervention/action plan.
                                    <br />
                                    1 - Identified and useful strength. Strength
                                    will be used, maintained or built upon as
                                    part of the plan. May require some effort to
                                    develop strength into a centerpiece
                                    strength.
                                    <br />
                                    2 - Strengths have been identified but
                                    require strength-building efforts before
                                    they can be effectively utilized as part of
                                    a plan. Identified but not useful.
                                    <br />3 - An area in which no current
                                    strength is identified; efforts may be
                                    recommended to develop a strength in this
                                    area.
                                  </div>
                                </div>
                                <div className="mx-2 row border-bottom">
                                  <div
                                    className="col py-2 bg-body"
                                    data-tooltip-id={`Involvement${index}`}
                                  >
                                    Involvement in Caregiving
                                    <Tooltip
                                      className="w-50"
                                      id={`Involvement${index}`}
                                    >
                                      {getTooltip("CaregiverInvolvement")}
                                    </Tooltip>
                                  </div>
                                  <div className="col py-2 bg-body border-start border-5">
                                    <FormInput
                                      name="Involvement"
                                      value={item.Involvement}
                                      error={item.Errors?.Involvement}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "CaregiverAssessments"
                                        );
                                      }}
                                      disabled={formDisabled}
                                      type="number"
                                      min="0"
                                      max="3"
                                    />
                                  </div>
                                </div>
                                <div className="mx-2 row border-bottom">
                                  <div
                                    className="col py-2 bg-body"
                                    data-tooltip-id={`Responsiveness${index}`}
                                  >
                                    Emotional Responsiveness
                                    <Tooltip
                                      className="w-50"
                                      id={`Responsiveness${index}`}
                                    >
                                      {getTooltip("CaregiverResponsiveness")}
                                    </Tooltip>
                                  </div>
                                  <div className="col py-2 bg-body border-start border-5">
                                    <FormInput
                                      name="Responsiveness"
                                      value={item.Responsiveness}
                                      error={item.Errors?.Responsiveness}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "CaregiverAssessments"
                                        );
                                      }}
                                      disabled={formDisabled}
                                      type="number"
                                      min="0"
                                      max="3"
                                    />
                                  </div>
                                </div>
                                <div className="mx-2 row border-bottom">
                                  <div
                                    className="col py-2 bg-body"
                                    data-tooltip-id={`Organization${index}`}
                                  >
                                    Organization
                                    <Tooltip
                                      className="w-50"
                                      id={`Organization${index}`}
                                    >
                                      {getTooltip("CaregiverOrganization")}
                                    </Tooltip>
                                  </div>
                                  <div className="col py-2 bg-body border-start border-5">
                                    <FormInput
                                      name="Organization"
                                      value={item.Organization}
                                      error={item.Errors?.Organization}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "CaregiverAssessments"
                                        );
                                      }}
                                      disabled={formDisabled}
                                      type="number"
                                      min="0"
                                      max="3"
                                    />
                                  </div>
                                </div>
                                <div className="mx-2 row border-bottom bg-body">
                                  <div className="col pt-2">
                                    <FormTextArea
                                      name="StrengthsRationale"
                                      label="Please write a rationale for any item in Caregiver Strengths items rated actionable ('2' or '3'), and useful strengths ('0' and '1')."
                                      value={item.StrengthsRationale}
                                      error={item.Errors?.StrengthsRationale}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "CaregiverAssessments"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  </div>
                                </div>
                              </React.Fragment>
                            </React.Fragment>
                          )
                        )
                      : null}
                    {!formDisabled ? (
                      <button
                        className="btn btn-primary mt-2"
                        onClick={() => {
                          handleAddRow("CaregiverAssessments", {
                            IsEditable: true,
                          });
                        }}
                      >
                        Add
                      </button>
                    ) : null}
                  </FieldTable>
                </div>
              </ExpandDiv>
              {data.ToddlerAssessmentsNA["@value"] === "true" ? (
                <div className="d-flex">
                  <div className="h4">Child Assessment: Ages 0-5</div>
                  <FormCheckbox
                    name="ToddlerAssessmentsNA"
                    label="N/A"
                    checked={data.ToddlerAssessmentsNA["@value"] === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    divClassName="mt-2 ms-3 h6"
                  />
                </div>
              ) : (
                <ExpandDiv
                  id="ToddlerAssessment"
                  expandStatus={expandStatus}
                  className="mt-2"
                  title="Child Assessment: Ages 0-5"
                >
                  <FormCheckbox
                    name="ToddlerAssessmentsNA"
                    label="N/A"
                    checked={data.ToddlerAssessmentsNA["@value"] === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    divClassName="ms-3"
                  />
                  {data.ToddlerAssessmentsNA["@value"] !== "true" ? (
                    <div className="container bg-secondary p-2">
                      <FieldTable
                        name="ToddlerAssessments"
                        commentsHandler={handleComments}
                      >
                        {data.ToddlerAssessments["@value"]
                          ? JSON.parse(data.ToddlerAssessments["@value"]).map(
                              (item, index) => (
                                <React.Fragment
                                  key={`ToddlerAssessments.${
                                    item._id || index
                                  }`}
                                >
                                  <div className="m-2 row border-bottom bg-body">
                                    <div className="col pb-2">
                                      <FormInput
                                        name="Name"
                                        label={
                                          <>
                                            {index + 1}. Child's Name
                                            {!formDisabled ? (
                                              <i
                                                className="far fa-trash-alt ms-2 me-2 d-print-none"
                                                onClick={() => {
                                                  handleDeleteRow(
                                                    index,
                                                    "ToddlerAssessments"
                                                  );
                                                }}
                                              />
                                            ) : null}
                                          </>
                                        }
                                        value={item.Name}
                                        error={item.Errors?.Name}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "ToddlerAssessments"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    </div>
                                    <div className="col pb-2">
                                      <FormInput
                                        name="DateCompleted"
                                        label="Date Completed"
                                        value={getFieldValue(
                                          data.DateCompleted
                                        )}
                                        onChange={handleChange}
                                        identical={compare("DateCompleted")}
                                        commentsHandler={handleComments}
                                        disabled
                                        type="date"
                                      />
                                    </div>
                                  </div>
                                  {errors.ToddlerAssessments !== undefined ? (
                                    <div className="alert alert-danger">
                                      {errors.ToddlerAssessments}
                                    </div>
                                  ) : null}
                                  {
                                    // Toddler Functioning
                                  }
                                  <React.Fragment>
                                    <div className="m-2 row">
                                      <div className="col-6 py-2">
                                        <div className="h5 text-white">
                                          0-5 Child Functioning
                                        </div>
                                      </div>
                                      <div className="col-1 bg-body py-2">
                                        {getTableScores(
                                          "ToddlerAssessments",
                                          "toddlerFunctioning",
                                          index
                                        )}
                                      </div>
                                      <div className="col"></div>
                                    </div>
                                    <div className="mx-2 row border-bottom bg-body">
                                      <div className="col py-2 bg-light">
                                        For the Child Functioning, use the
                                        following categories and action levels:
                                        <br />
                                        0 - No evidence of any needs; no need
                                        for action.
                                        <br />
                                        1 - Identified need that requires
                                        monitoring, watchful waiting, or
                                        preventive action based on history,
                                        suspicion or disagreement.
                                        <br />
                                        2 - Action is required to ensure that
                                        the identified need is addressed; need
                                        is interfering with functioning.
                                        <br />3 - Need is dangerous or
                                        disabling; requires immediate and/or
                                        intensive action.
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`EarlyEducation${index}`}
                                      >
                                        Early Education
                                        <Tooltip
                                          className="w-50"
                                          id={`EarlyEducation${index}`}
                                        >
                                          {getTooltip("ToddlerEarlyEducation")}
                                        </Tooltip>
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <FormInput
                                          name="EarlyEducation"
                                          value={item.EarlyEducation}
                                          error={item.Errors?.EarlyEducation}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "ToddlerAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                          type="number"
                                          min="0"
                                          max="3"
                                        />
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`SocialFunctioning${index}`}
                                      >
                                        Social and Emotional Functioning
                                        <Tooltip
                                          className="w-50"
                                          id={`SocialFunctioning${index}`}
                                        >
                                          {getTooltip(
                                            "ToddlerSocialFunctioning"
                                          )}
                                        </Tooltip>
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <FormInput
                                          name="SocialFunctioning"
                                          value={item.SocialFunctioning}
                                          error={item.Errors?.SocialFunctioning}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "ToddlerAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                          type="number"
                                          min="0"
                                          max="3"
                                        />
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`Developmental${index}`}
                                      >
                                        Developmental/Intellectual
                                        <Tooltip
                                          className="w-50"
                                          id={`Developmental${index}`}
                                        >
                                          {getTooltip("ToddlerDevelopmental")}
                                        </Tooltip>
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <FormInput
                                          name="Developmental"
                                          value={item.Developmental}
                                          error={item.Errors?.Developmental}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "ToddlerAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                          type="number"
                                          min="0"
                                          max="3"
                                        />
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`Medical${index}`}
                                      >
                                        Medical/Physical
                                        <Tooltip
                                          className="w-50"
                                          id={`Medical${index}`}
                                        >
                                          {getTooltip("ToddlerMedical")}
                                        </Tooltip>
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <FormInput
                                          name="Medical"
                                          value={item.Medical}
                                          error={item.Errors?.Medical}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "ToddlerAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                          type="number"
                                          min="0"
                                          max="3"
                                        />
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom bg-body">
                                      <div className="col pt-2">
                                        <FormTextArea
                                          name="FunctioningRationale"
                                          label="Please write a rationale for any item in the 0-5 Child Functioning rated actionable ('2' or '3')."
                                          value={item.FunctioningRationale}
                                          error={
                                            item.Errors?.FunctioningRationale
                                          }
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "ToddlerAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </div>
                                    </div>
                                  </React.Fragment>
                                  {
                                    // Toddler Strengths
                                  }
                                  <React.Fragment>
                                    <div className="m-2 row">
                                      <div className="col-6">
                                        <div className="h5 text-white">
                                          0-5 Child Strengths
                                        </div>
                                      </div>
                                      <div className="col-1 bg-body py-2">
                                        {getTableScores(
                                          "ToddlerAssessments",
                                          "toddlerStrengths",
                                          index
                                        )}
                                      </div>
                                      <div className="col"></div>
                                    </div>
                                    <div className="mx-2 row border-bottom bg-body">
                                      <div className="col py-2 bg-light">
                                        For Child Strengths, use the following
                                        categories and action levels:
                                        <br />
                                        0 - Well-developed, centerpiece
                                        strength; may be used as a focus of an
                                        intervention/action plan.
                                        <br />
                                        1 - Identified and useful strength.
                                        Strength will be used, maintained or
                                        built upon as part of the plan. May
                                        require some effort to develop strength
                                        into a centerpiece strength.
                                        <br />
                                        2 - Strengths have been identified but
                                        require strength-building efforts before
                                        they can be effectively utilized as part
                                        of a plan. Identified but not useful.
                                        <br />3 - An area in which no current
                                        strength is identified; efforts may be
                                        recommended to develop a strength in
                                        this area.
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`FamilyStrengths${index}`}
                                      >
                                        Family Strengths
                                        <Tooltip
                                          className="w-50"
                                          id={`FamilyStrengths${index}`}
                                        >
                                          {getTooltip("ToddlerFamilyStrengths")}
                                        </Tooltip>
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <FormInput
                                          name="FamilyStrengths"
                                          value={item.FamilyStrengths}
                                          error={item.Errors?.FamilyStrengths}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "ToddlerAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                          type="number"
                                          min="0"
                                          max="3"
                                        />
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`Interpersonal${index}`}
                                      >
                                        Interpersonal
                                        <Tooltip
                                          className="w-50"
                                          id={`Interpersonal${index}`}
                                        >
                                          {getTooltip("ToddlerInterpersonal")}
                                        </Tooltip>
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <FormInput
                                          name="Interpersonal"
                                          value={item.Interpersonal}
                                          error={item.Errors?.Interpersonal}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "ToddlerAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                          type="number"
                                          min="0"
                                          max="3"
                                        />
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`Playfulness${index}`}
                                      >
                                        Playfulness
                                        <Tooltip
                                          className="w-50"
                                          id={`Playfulness${index}`}
                                        >
                                          {getTooltip("ToddlerPlayfulness")}
                                        </Tooltip>
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <FormInput
                                          name="Playfulness"
                                          value={item.Playfulness}
                                          error={item.Errors?.Playfulness}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "ToddlerAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                          type="number"
                                          min="0"
                                          max="3"
                                        />
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`Adaptability${index}`}
                                      >
                                        Persistence & Adaptability
                                        <Tooltip
                                          className="w-50"
                                          id={`Adaptability${index}`}
                                        >
                                          {getTooltip("ToddlerAdaptability")}
                                        </Tooltip>
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <FormInput
                                          name="Adaptability"
                                          value={item.Adaptability}
                                          error={item.Errors?.Adaptability}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "ToddlerAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                          type="number"
                                          min="0"
                                          max="3"
                                        />
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom bg-body">
                                      <div className="col pt-2">
                                        <FormTextArea
                                          name="StrengthsRationale"
                                          label="Please write a rationale for any item in 0-5 Child Strengths items rated actionable ('2' or '3'), and useful strengths ('0' and '1')."
                                          value={item.StrengthsRationale}
                                          error={
                                            item.Errors?.StrengthsRationale
                                          }
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "ToddlerAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </div>
                                    </div>
                                  </React.Fragment>
                                </React.Fragment>
                              )
                            )
                          : null}
                        {!formDisabled ? (
                          <button
                            className="btn btn-primary mt-2"
                            onClick={() => {
                              handleAddRow("ToddlerAssessments", {
                                IsEditable: true,
                              });
                            }}
                          >
                            Add
                          </button>
                        ) : null}
                      </FieldTable>
                    </div>
                  ) : null}
                </ExpandDiv>
              )}
              {data.ChildAssessmentsNA["@value"] === "true" ? (
                <div className="d-flex">
                  <div className="h4">Child Assessment: Ages 6-21</div>
                  <FormCheckbox
                    name="ChildAssessmentsNA"
                    label="N/A"
                    checked={data.ChildAssessmentsNA["@value"] === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    divClassName="mt-2 ms-3 h6"
                  />
                </div>
              ) : (
                <ExpandDiv
                  id="ChildAssessment"
                  expandStatus={expandStatus}
                  className="mt-2"
                  title="Child Assessment: Ages 6-21"
                >
                  <FormCheckbox
                    name="ChildAssessmentsNA"
                    label="N/A"
                    checked={getFieldValue(data.ChildAssessmentsNA) === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    divClassName="ms-3"
                  />
                  {getFieldValue(data.ChildAssessmentsNA) !== "true" ? (
                    <div className="container bg-secondary p-2">
                      <FieldTable
                        name="ChildAssessments"
                        commentsHandler={handleComments}
                      >
                        {data.ChildAssessments["@value"]
                          ? JSON.parse(data.ChildAssessments["@value"]).map(
                              (item, index) => (
                                <React.Fragment
                                  key={`ChildAssessments.${item._id || index}`}
                                >
                                  <div className="m-2 row border-bottom bg-body">
                                    <div className="col pb-2">
                                      <FormInput
                                        name="Name"
                                        label={
                                          <>
                                            {index + 1}. Child's Name{" "}
                                            {!formDisabled ? (
                                              <i
                                                className="far fa-trash-alt me-2 d-print-none"
                                                onClick={() => {
                                                  handleDeleteRow(
                                                    index,
                                                    "ChildAssessments"
                                                  );
                                                }}
                                              />
                                            ) : null}
                                          </>
                                        }
                                        value={item.Name}
                                        error={item.Errors?.Name}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "ChildAssessments"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    </div>
                                    <div className="col pb-2">
                                      <FormInput
                                        name="DateCompleted"
                                        label="Date Completed"
                                        value={getFieldValue(
                                          data.DateCompleted
                                        )}
                                        onChange={handleChange}
                                        identical={compare("DateCompleted")}
                                        commentsHandler={handleComments}
                                        disabled
                                        type="date"
                                      />
                                    </div>
                                  </div>
                                  {errors.ChildAssessments !== undefined ? (
                                    <div className="alert alert-danger">
                                      {errors.ChildAssessments}
                                    </div>
                                  ) : null}
                                  {
                                    // Child Needs
                                  }
                                  <React.Fragment>
                                    <div className="m-2 row">
                                      <div className="col-6 py-2">
                                        <div className="h5 text-white">
                                          6-21 Child Needs
                                        </div>
                                      </div>
                                      <div className="col-1 bg-body py-2">
                                        {getTableScores(
                                          "ChildAssessments",
                                          "childNeeds",
                                          index
                                        )}
                                      </div>
                                      <div className="col"></div>
                                    </div>
                                    <div className="mx-2 row border-bottom bg-body">
                                      <div className="col py-2 bg-light">
                                        For the 6-21 Child Functioning, , use
                                        the following categories and action
                                        levels:
                                        <br />
                                        0 - No evidence of any needs; no need
                                        for action.
                                        <br />
                                        1 - Identified need that requires
                                        monitoring, watchful waiting, or
                                        preventive action based on history,
                                        suspicion or disagreement.
                                        <br />
                                        2 - Action is required to ensure that
                                        the identified need is addressed; need
                                        is interfering with functioning.
                                        <br />3 - Need is dangerous or
                                        disabling; requires immediate and/or
                                        intensive action.
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`SocialFuntioning${index}`}
                                      >
                                        Social Functioning
                                        <Tooltip
                                          className="w-50"
                                          id={`SocialFuntioning${index}`}
                                        >
                                          {getTooltip("ChildSocialFuntioning")}
                                        </Tooltip>
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <FormInput
                                          name="SocialFuntioning"
                                          value={item.SocialFuntioning}
                                          error={item.Errors?.SocialFuntioning}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "ChildAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                          type="number"
                                          min="0"
                                          max="3"
                                        />
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`Medical${index}`}
                                      >
                                        Medical/Physical
                                        <Tooltip
                                          className="w-50"
                                          id={`Medical${index}`}
                                        >
                                          {getTooltip("ChildMedical")}
                                        </Tooltip>
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <FormInput
                                          name="Medical"
                                          value={item.Medical}
                                          error={item.Errors?.Medical}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "ChildAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                          type="number"
                                          min="0"
                                          max="3"
                                        />
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`Sleep${index}`}
                                      >
                                        Sleep
                                        <Tooltip
                                          className="w-50"
                                          id={`Sleep${index}`}
                                        >
                                          {getTooltip("ChildSleep")}
                                        </Tooltip>
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <FormInput
                                          name="Sleep"
                                          value={item.Sleep}
                                          error={item.Errors?.Sleep}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "ChildAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                          type="number"
                                          min="0"
                                          max="3"
                                        />
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`Developmental${index}`}
                                      >
                                        Developmental/Intellectual
                                        <Tooltip
                                          className="w-50"
                                          id={`Developmental${index}`}
                                        >
                                          {getTooltip("ChildDevelopmental")}
                                        </Tooltip>
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <FormInput
                                          name="Developmental"
                                          value={item.Developmental}
                                          error={item.Errors?.Developmental}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "ChildAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                          type="number"
                                          min="0"
                                          max="3"
                                        />
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`SexualDevelopment${index}`}
                                      >
                                        Sexual Development
                                        <Tooltip
                                          className="w-50"
                                          id={`SexualDevelopment${index}`}
                                        >
                                          {getTooltip("ChildSexualDevelopment")}
                                        </Tooltip>
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <FormInput
                                          name="SexualDevelopment"
                                          value={item.SexualDevelopment}
                                          error={item.Errors?.SexualDevelopment}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "ChildAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                          type="number"
                                          min="0"
                                          max="3"
                                        />
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`School${index}`}
                                      >
                                        School
                                        <Tooltip
                                          className="w-50"
                                          id={`School${index}`}
                                        >
                                          {getTooltip("ChildSchool")}
                                        </Tooltip>
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <FormInput
                                          name="School"
                                          value={item.School}
                                          error={item.Errors?.School}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "ChildAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                          type="number"
                                          min="0"
                                          max="3"
                                        />
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`Behavioral${index}`}
                                      >
                                        Behavioral/Mental Health Needs
                                        <Tooltip
                                          className="w-50"
                                          id={`Behavioral${index}`}
                                        >
                                          {getTooltip("ChildBehavioral")}
                                        </Tooltip>
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <FormInput
                                          name="Behavioral"
                                          value={item.Behavioral}
                                          error={item.Errors?.Behavioral}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "ChildAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                          type="number"
                                          min="0"
                                          max="3"
                                        />
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`RiskBehaviors${index}`}
                                      >
                                        Risk Behaviors
                                        <Tooltip
                                          className="w-50"
                                          id={`RiskBehaviors${index}`}
                                        >
                                          {getTooltip("ChildRiskBehaviors")}
                                        </Tooltip>
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <FormInput
                                          name="RiskBehaviors"
                                          value={item.RiskBehaviors}
                                          error={item.Errors?.RiskBehaviors}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "ChildAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                          type="number"
                                          min="0"
                                          max="3"
                                        />
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`Adjustment${index}`}
                                      >
                                        Adjustment to Trauma
                                        <Tooltip
                                          className="w-50"
                                          id={`Adjustment${index}`}
                                        >
                                          {getTooltip("ChildAdjustment")}
                                        </Tooltip>
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <FormInput
                                          name="Adjustment"
                                          value={item.Adjustment}
                                          error={item.Errors?.Adjustment}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "ChildAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                          type="number"
                                          min="0"
                                          max="3"
                                        />
                                      </div>
                                    </div>
                                    {parseInt(item.Behavioral) > 0 ? (
                                      <>
                                        <div className="mx-2 row border-bottom">
                                          <div className="col py-2 text-white">
                                            <span className="h5">
                                              D{index + 1} Behavioral/Mental
                                              Health Needs Module
                                            </span>{" "}
                                            <i>
                                              (Needs to be completed when the
                                              Behavioral/Mental Health Needs
                                              item is rated '1', '2' or '3'.)
                                            </i>
                                          </div>
                                        </div>
                                        <div className="mx-2 row border-bottom">
                                          <div
                                            className="col py-2 bg-body"
                                            data-tooltip-id={`Psychosis${index}`}
                                          >
                                            Psychosis (Thought Disorder)
                                            <Tooltip
                                              className="w-50"
                                              id={`Psychosis${index}`}
                                            >
                                              {getTooltip("ChildPsychosis")}
                                            </Tooltip>
                                          </div>
                                          <div className="col py-2 bg-body border-start border-5">
                                            <FormInput
                                              name="Psychosis"
                                              value={item.Psychosis}
                                              error={item.Errors?.Psychosis}
                                              onChange={(e) => {
                                                handleRowChange(
                                                  e,
                                                  index,
                                                  "ChildAssessments"
                                                );
                                              }}
                                              disabled={formDisabled}
                                              type="number"
                                              min="0"
                                              max="3"
                                            />
                                          </div>
                                        </div>
                                        <div className="mx-2 row border-bottom">
                                          <div
                                            className="col py-2 bg-body"
                                            data-tooltip-id={`Impulsivity${index}`}
                                          >
                                            Impulsivity/Hyperactivity
                                            <Tooltip
                                              className="w-50"
                                              id={`Impulsivity${index}`}
                                            >
                                              {getTooltip("ChildImpulsivity")}
                                            </Tooltip>
                                          </div>
                                          <div className="col py-2 bg-body border-start border-5">
                                            <FormInput
                                              name="Impulsivity"
                                              value={item.Impulsivity}
                                              error={item.Errors?.Impulsivity}
                                              onChange={(e) => {
                                                handleRowChange(
                                                  e,
                                                  index,
                                                  "ChildAssessments"
                                                );
                                              }}
                                              disabled={formDisabled}
                                              type="number"
                                              min="0"
                                              max="3"
                                            />
                                          </div>
                                        </div>
                                        <div className="mx-2 row border-bottom">
                                          <div
                                            className="col py-2 bg-body"
                                            data-tooltip-id={`Depression${index}`}
                                          >
                                            Depression
                                            <Tooltip
                                              className="w-50"
                                              id={`Depression${index}`}
                                            >
                                              {getTooltip("ChildDepression")}
                                            </Tooltip>
                                          </div>
                                          <div className="col py-2 bg-body border-start border-5">
                                            <FormInput
                                              name="Depression"
                                              value={item.Depression}
                                              error={item.Errors?.Depression}
                                              onChange={(e) => {
                                                handleRowChange(
                                                  e,
                                                  index,
                                                  "ChildAssessments"
                                                );
                                              }}
                                              disabled={formDisabled}
                                              type="number"
                                              min="0"
                                              max="3"
                                            />
                                          </div>
                                        </div>
                                        <div className="mx-2 row border-bottom">
                                          <div
                                            className="col py-2 bg-body"
                                            data-tooltip-id={`Anxiety${index}`}
                                          >
                                            Anxiety
                                            <Tooltip
                                              className="w-50"
                                              id={`Anxiety${index}`}
                                            >
                                              {getTooltip("ChildAnxiety")}
                                            </Tooltip>
                                          </div>
                                          <div className="col py-2 bg-body border-start border-5">
                                            <FormInput
                                              name="Anxiety"
                                              value={item.Anxiety}
                                              error={item.Errors?.Anxiety}
                                              onChange={(e) => {
                                                handleRowChange(
                                                  e,
                                                  index,
                                                  "ChildAssessments"
                                                );
                                              }}
                                              disabled={formDisabled}
                                              type="number"
                                              min="0"
                                              max="3"
                                            />
                                          </div>
                                        </div>
                                        <div className="mx-2 row border-bottom">
                                          <div
                                            className="col py-2 bg-body"
                                            data-tooltip-id={`Oppositional${index}`}
                                          >
                                            Oppositional
                                            <Tooltip
                                              className="w-50"
                                              id={`Oppositional${index}`}
                                            >
                                              {getTooltip("ChildOppositional")}
                                            </Tooltip>
                                          </div>
                                          <div className="col py-2 bg-body border-start border-5">
                                            <FormInput
                                              name="Oppositional"
                                              value={item.Oppositional}
                                              error={item.Errors?.Oppositional}
                                              onChange={(e) => {
                                                handleRowChange(
                                                  e,
                                                  index,
                                                  "ChildAssessments"
                                                );
                                              }}
                                              disabled={formDisabled}
                                              type="number"
                                              min="0"
                                              max="3"
                                            />
                                          </div>
                                        </div>
                                        <div className="mx-2 row border-bottom">
                                          <div
                                            className="col py-2 bg-body"
                                            data-tooltip-id={`Conduct${index}`}
                                          >
                                            Conduct
                                            <Tooltip
                                              className="w-50"
                                              id={`Conduct${index}`}
                                            >
                                              {getTooltip("ChildConduct")}
                                            </Tooltip>
                                          </div>
                                          <div className="col py-2 bg-body border-start border-5">
                                            <FormInput
                                              name="Conduct"
                                              value={item.Conduct}
                                              error={item.Errors?.Conduct}
                                              onChange={(e) => {
                                                handleRowChange(
                                                  e,
                                                  index,
                                                  "ChildAssessments"
                                                );
                                              }}
                                              disabled={formDisabled}
                                              type="number"
                                              min="0"
                                              max="3"
                                            />
                                          </div>
                                        </div>
                                        <div className="mx-2 row border-bottom">
                                          <div
                                            className="col py-2 bg-body"
                                            data-tooltip-id={`SubstanceUse${index}`}
                                          >
                                            Substance Use
                                            <Tooltip
                                              className="w-50"
                                              id={`SubstanceUse${index}`}
                                            >
                                              {getTooltip("ChildSubstanceUse")}
                                            </Tooltip>
                                          </div>
                                          <div className="col py-2 bg-body border-start border-5">
                                            <FormInput
                                              name="SubstanceUse"
                                              value={item.SubstanceUse}
                                              error={item.Errors?.SubstanceUse}
                                              onChange={(e) => {
                                                handleRowChange(
                                                  e,
                                                  index,
                                                  "ChildAssessments"
                                                );
                                              }}
                                              disabled={formDisabled}
                                              type="number"
                                              min="0"
                                              max="3"
                                            />
                                          </div>
                                        </div>
                                        <div className="mx-2 row border-bottom">
                                          <div
                                            className="col py-2 bg-body"
                                            data-tooltip-id={`EatingDisturbance${index}`}
                                          >
                                            Eating Disturbances
                                            <Tooltip
                                              id={`EatingDisturbance${index}`}
                                            >
                                              {getTooltip(
                                                "ChildEatingDisturbance"
                                              )}
                                            </Tooltip>
                                          </div>
                                          <div className="col py-2 bg-body border-start border-5">
                                            <FormInput
                                              name="EatingDisturbance"
                                              value={item.EatingDisturbance}
                                              error={
                                                item.Errors?.EatingDisturbance
                                              }
                                              onChange={(e) => {
                                                handleRowChange(
                                                  e,
                                                  index,
                                                  "ChildAssessments"
                                                );
                                              }}
                                              disabled={formDisabled}
                                              type="number"
                                              min="0"
                                              max="3"
                                            />
                                          </div>
                                        </div>
                                        <div className="mx-2 row border-bottom">
                                          <div
                                            className="col py-2 bg-body"
                                            data-tooltip-id={`Anger${index}`}
                                          >
                                            Anger Control
                                            <Tooltip
                                              className="w-50"
                                              id={`Anger${index}`}
                                            >
                                              {getTooltip("ChildAnger")}
                                            </Tooltip>
                                          </div>
                                          <div className="col py-2 bg-body border-start border-5">
                                            <FormInput
                                              name="Anger"
                                              value={item.Anger}
                                              error={item.Errors?.Anger}
                                              onChange={(e) => {
                                                handleRowChange(
                                                  e,
                                                  index,
                                                  "ChildAssessments"
                                                );
                                              }}
                                              disabled={formDisabled}
                                              type="number"
                                              min="0"
                                              max="3"
                                            />
                                          </div>
                                        </div>
                                      </>
                                    ) : null}
                                    {parseInt(item.RiskBehaviors) > 0 ? (
                                      <>
                                        <div className="mx-2 row border-bottom">
                                          <div className="col py-2 text-white">
                                            <span className="h5">
                                              E{index + 1} Risk Behaviors Module
                                            </span>{" "}
                                            <i>
                                              (Needs to be completed when the
                                              Risk Behaviors item is rated '1',
                                              '2' or '3'.)
                                            </i>
                                          </div>
                                        </div>
                                        <div className="mx-2 row border-bottom">
                                          <div
                                            className="col py-2 bg-body"
                                            data-tooltip-id={`SuicideRisk${index}`}
                                          >
                                            Suicide Risk
                                            <Tooltip
                                              className="w-50"
                                              id={`SuicideRisk${index}`}
                                            >
                                              {getTooltip("ChildSuicideRisk")}
                                            </Tooltip>
                                          </div>
                                          <div className="col py-2 bg-body border-start border-5">
                                            <FormInput
                                              name="SuicideRisk"
                                              value={item.SuicideRisk}
                                              error={item.Errors?.SuicideRisk}
                                              onChange={(e) => {
                                                handleRowChange(
                                                  e,
                                                  index,
                                                  "ChildAssessments"
                                                );
                                              }}
                                              disabled={formDisabled}
                                              type="number"
                                              min="0"
                                              max="3"
                                            />
                                          </div>
                                        </div>
                                        <div className="mx-2 row border-bottom">
                                          <div
                                            className="col py-2 bg-body"
                                            data-tooltip-id={`SelfInjurious${index}`}
                                          >
                                            Non-Suicidal Self-Injurious Behavior
                                            <Tooltip
                                              className="w-50"
                                              id={`SelfInjurious${index}`}
                                            >
                                              {getTooltip("ChildSelfInjurious")}
                                            </Tooltip>
                                          </div>
                                          <div className="col py-2 bg-body border-start border-5">
                                            <FormInput
                                              name="SelfInjurious"
                                              value={item.SelfInjurious}
                                              error={item.Errors?.SelfInjurious}
                                              onChange={(e) => {
                                                handleRowChange(
                                                  e,
                                                  index,
                                                  "ChildAssessments"
                                                );
                                              }}
                                              disabled={formDisabled}
                                              type="number"
                                              min="0"
                                              max="3"
                                            />
                                          </div>
                                        </div>
                                        <div className="mx-2 row border-bottom">
                                          <div
                                            className="col py-2 bg-body"
                                            data-tooltip-id={`Recklessness${index}`}
                                          >
                                            Other Self-Harm (Recklessness)
                                            <Tooltip
                                              className="w-50"
                                              id={`Recklessness${index}`}
                                            >
                                              {getTooltip("ChildRecklessness")}
                                            </Tooltip>
                                          </div>
                                          <div className="col py-2 bg-body border-start border-5">
                                            <FormInput
                                              name="Recklessness"
                                              value={item.Recklessness}
                                              error={item.Errors?.Recklessness}
                                              onChange={(e) => {
                                                handleRowChange(
                                                  e,
                                                  index,
                                                  "ChildAssessments"
                                                );
                                              }}
                                              disabled={formDisabled}
                                              type="number"
                                              min="0"
                                              max="3"
                                            />
                                          </div>
                                        </div>
                                        <div className="mx-2 row border-bottom">
                                          <div
                                            className="col py-2 bg-body"
                                            data-tooltip-id={`DangerToOthers${index}`}
                                          >
                                            Danger to Others
                                            <Tooltip
                                              className="w-50"
                                              id={`DangerToOthers${index}`}
                                            >
                                              {getTooltip(
                                                "ChildDangerToOthers"
                                              )}
                                            </Tooltip>
                                          </div>
                                          <div className="col py-2 bg-body border-start border-5">
                                            <FormInput
                                              name="DangerToOthers"
                                              value={item.DangerToOthers}
                                              error={
                                                item.Errors?.DangerToOthers
                                              }
                                              onChange={(e) => {
                                                handleRowChange(
                                                  e,
                                                  index,
                                                  "ChildAssessments"
                                                );
                                              }}
                                              disabled={formDisabled}
                                              type="number"
                                              min="0"
                                              max="3"
                                            />
                                          </div>
                                        </div>
                                        <div className="mx-2 row border-bottom">
                                          <div
                                            className="col py-2 bg-body"
                                            data-tooltip-id={`SexualAggression${index}`}
                                          >
                                            Sexual Aggression
                                            <Tooltip
                                              id={`SexualAggression${index}`}
                                            >
                                              {getTooltip(
                                                "ChildSexualAggression"
                                              )}
                                            </Tooltip>
                                          </div>
                                          <div className="col py-2 bg-body border-start border-5">
                                            <FormInput
                                              name="SexualAggression"
                                              value={item.SexualAggression}
                                              error={
                                                item.Errors?.SexualAggression
                                              }
                                              onChange={(e) => {
                                                handleRowChange(
                                                  e,
                                                  index,
                                                  "ChildAssessments"
                                                );
                                              }}
                                              disabled={formDisabled}
                                              type="number"
                                              min="0"
                                              max="3"
                                            />
                                          </div>
                                        </div>
                                        <div className="mx-2 row border-bottom">
                                          <div
                                            className="col py-2 bg-body"
                                            data-tooltip-id={`Runaway${index}`}
                                          >
                                            Runaway
                                            <Tooltip
                                              className="w-50"
                                              id={`Runaway${index}`}
                                            >
                                              {getTooltip("ChildRunaway")}
                                            </Tooltip>
                                          </div>
                                          <div className="col py-2 bg-body border-start border-5">
                                            <FormInput
                                              name="Runaway"
                                              value={item.Runaway}
                                              error={item.Errors?.Runaway}
                                              onChange={(e) => {
                                                handleRowChange(
                                                  e,
                                                  index,
                                                  "ChildAssessments"
                                                );
                                              }}
                                              disabled={formDisabled}
                                              type="number"
                                              min="0"
                                              max="3"
                                            />
                                          </div>
                                        </div>
                                        <div className="mx-2 row border-bottom">
                                          <div
                                            className="col py-2 bg-body"
                                            data-tooltip-id={`Delinquent${index}`}
                                          >
                                            Delinquent Behavior
                                            <Tooltip
                                              className="w-50"
                                              id={`Delinquent${index}`}
                                            >
                                              {getTooltip("ChildDelinquent")}
                                            </Tooltip>
                                          </div>
                                          <div className="col py-2 bg-body border-start border-5">
                                            <FormInput
                                              name="Delinquent"
                                              value={item.Delinquent}
                                              error={item.Errors?.Delinquent}
                                              onChange={(e) => {
                                                handleRowChange(
                                                  e,
                                                  index,
                                                  "ChildAssessments"
                                                );
                                              }}
                                              disabled={formDisabled}
                                              type="number"
                                              min="0"
                                              max="3"
                                            />
                                          </div>
                                        </div>
                                        <div className="mx-2 row border-bottom">
                                          <div
                                            className="col py-2 bg-body"
                                            data-tooltip-id={`FireSetting${index}`}
                                          >
                                            Fire Setting
                                            <Tooltip
                                              className="w-50"
                                              id={`FireSetting${index}`}
                                            >
                                              {getTooltip("ChildFireSetting")}
                                            </Tooltip>
                                          </div>
                                          <div className="col py-2 bg-body border-start border-5">
                                            <FormInput
                                              name="FireSetting"
                                              value={item.FireSetting}
                                              error={item.Errors?.FireSetting}
                                              onChange={(e) => {
                                                handleRowChange(
                                                  e,
                                                  index,
                                                  "ChildAssessments"
                                                );
                                              }}
                                              disabled={formDisabled}
                                              type="number"
                                              min="0"
                                              max="3"
                                            />
                                          </div>
                                        </div>
                                        <div className="mx-2 row border-bottom">
                                          <div
                                            className="col py-2 bg-body"
                                            data-tooltip-id={`Misbehavior${index}`}
                                          >
                                            Intentional Misbehavior
                                            <Tooltip
                                              className="w-50"
                                              id={`Misbehavior${index}`}
                                            >
                                              {getTooltip("ChildMisbehavior")}
                                            </Tooltip>
                                          </div>
                                          <div className="col py-2 bg-body border-start border-5">
                                            <FormInput
                                              name="Misbehavior"
                                              value={item.Misbehavior}
                                              error={item.Errors?.Misbehavior}
                                              onChange={(e) => {
                                                handleRowChange(
                                                  e,
                                                  index,
                                                  "ChildAssessments"
                                                );
                                              }}
                                              disabled={formDisabled}
                                              type="number"
                                              min="0"
                                              max="3"
                                            />
                                          </div>
                                        </div>
                                        <div className="mx-2 row border-bottom">
                                          <div
                                            className="col py-2 bg-body"
                                            data-tooltip-id={`Bullying${index}`}
                                          >
                                            Bullying
                                            <Tooltip
                                              className="w-50"
                                              id={`Bullying${index}`}
                                            >
                                              {getTooltip("ChildBullying")}
                                            </Tooltip>
                                          </div>
                                          <div className="col py-2 bg-body border-start border-5">
                                            <FormInput
                                              name="Bullying"
                                              value={item.Bullying}
                                              error={item.Errors?.Bullying}
                                              onChange={(e) => {
                                                handleRowChange(
                                                  e,
                                                  index,
                                                  "ChildAssessments"
                                                );
                                              }}
                                              disabled={formDisabled}
                                              type="number"
                                              min="0"
                                              max="3"
                                            />
                                          </div>
                                        </div>
                                        <div className="mx-2 row border-bottom">
                                          <div
                                            className="col py-2 bg-body"
                                            data-tooltip-id={`Exploitation${index}`}
                                          >
                                            Victimization/Exploitation
                                            <Tooltip
                                              className="w-50"
                                              id={`Exploitation${index}`}
                                            >
                                              {getTooltip("ChildExploitation")}
                                            </Tooltip>
                                          </div>
                                          <div className="col py-2 bg-body border-start border-5">
                                            <FormInput
                                              name="Exploitation"
                                              value={item.Exploitation}
                                              error={item.Errors?.Exploitation}
                                              onChange={(e) => {
                                                handleRowChange(
                                                  e,
                                                  index,
                                                  "ChildAssessments"
                                                );
                                              }}
                                              disabled={formDisabled}
                                              type="number"
                                              min="0"
                                              max="3"
                                            />
                                          </div>
                                        </div>
                                      </>
                                    ) : null}
                                    {parseInt(item.Adjustment) > 0 ? (
                                      <>
                                        <div className="mx-2 row border-bottom">
                                          <div className="col py-2 text-white">
                                            <span className="h5">
                                              F{index + 1} Trauma Module
                                            </span>{" "}
                                            <i>
                                              (Needs to be completed when the
                                              Adjustment to Trauma item is rated
                                              '1', '2' or '3'.)
                                            </i>
                                          </div>
                                        </div>
                                        <div className="mx-2 row border-bottom bg-body">
                                          <div className="col py-2 bg-light">
                                            For the Trauma Module: Potentially
                                            Traumatic/Adverse Childhood
                                            Experiences, use the following
                                            categories and ratings:
                                            <br />
                                            No - No evidence of any trauma of
                                            this type.
                                            <br />
                                            Yes - Individual has had experience
                                            or there is suspicion that the
                                            individual has experienced this type
                                            of trauma—one incident, multiple
                                            incidents, or chronic, on-going
                                            experiences.
                                          </div>
                                        </div>
                                        <div className="mx-2 row border-bottom">
                                          <div
                                            className="col py-2 bg-body"
                                            data-tooltip-id={`SexualAbuse${index}`}
                                          >
                                            Sexual Abuse
                                            <Tooltip
                                              className="w-50"
                                              id={`SexualAbuse${index}`}
                                            >
                                              {getTooltip("ChildSexualAbuse")}
                                            </Tooltip>
                                          </div>
                                          <div className="col py-2 bg-body border-start border-5">
                                            <Select
                                              name="SexualAbuse"
                                              value={item.SexualAbuse}
                                              error={item.Errors?.SexualAbuse}
                                              options={yesNoOptions}
                                              onChange={(e) => {
                                                handleRowChange(
                                                  e,
                                                  index,
                                                  "ChildAssessments"
                                                );
                                              }}
                                              disabled={formDisabled}
                                            />
                                          </div>
                                        </div>
                                        <div className="mx-2 row border-bottom">
                                          <div
                                            className="col py-2 bg-body"
                                            data-tooltip-id={`PhysicalAbuse${index}`}
                                          >
                                            Physical Abuse
                                            <Tooltip
                                              className="w-50"
                                              id={`PhysicalAbuse${index}`}
                                            >
                                              {getTooltip("ChildPhysicalAbuse")}
                                            </Tooltip>
                                          </div>
                                          <div className="col py-2 bg-body border-start border-5">
                                            <Select
                                              name="PhysicalAbuse"
                                              value={item.PhysicalAbuse}
                                              error={item.Errors?.PhysicalAbuse}
                                              options={yesNoOptions}
                                              onChange={(e) => {
                                                handleRowChange(
                                                  e,
                                                  index,
                                                  "ChildAssessments"
                                                );
                                              }}
                                              disabled={formDisabled}
                                            />
                                          </div>
                                        </div>
                                        <div className="mx-2 row border-bottom">
                                          <div
                                            className="col py-2 bg-body"
                                            data-tooltip-id={`Neglect${index}`}
                                          >
                                            Neglect
                                            <Tooltip
                                              className="w-50"
                                              id={`Neglect${index}`}
                                            >
                                              {getTooltip("ChildNeglect")}
                                            </Tooltip>
                                          </div>
                                          <div className="col py-2 bg-body border-start border-5">
                                            <Select
                                              name="Neglect"
                                              value={item.Neglect}
                                              error={item.Errors?.Neglect}
                                              options={yesNoOptions}
                                              onChange={(e) => {
                                                handleRowChange(
                                                  e,
                                                  index,
                                                  "ChildAssessments"
                                                );
                                              }}
                                              disabled={formDisabled}
                                            />
                                          </div>
                                        </div>
                                        <div className="mx-2 row border-bottom">
                                          <div
                                            className="col py-2 bg-body"
                                            data-tooltip-id={`EmotionalAbuse${index}`}
                                          >
                                            Emotional Abuse
                                            <Tooltip
                                              className="w-50"
                                              id={`EmotionalAbuse${index}`}
                                            >
                                              {getTooltip(
                                                "ChildEmotionalAbuse"
                                              )}
                                            </Tooltip>
                                          </div>
                                          <div className="col py-2 bg-body border-start border-5">
                                            <Select
                                              name="EmotionalAbuse"
                                              value={item.EmotionalAbuse}
                                              error={
                                                item.Errors?.EmotionalAbuse
                                              }
                                              options={yesNoOptions}
                                              onChange={(e) => {
                                                handleRowChange(
                                                  e,
                                                  index,
                                                  "ChildAssessments"
                                                );
                                              }}
                                              disabled={formDisabled}
                                            />
                                          </div>
                                        </div>
                                        <div className="mx-2 row border-bottom">
                                          <div
                                            className="col py-2 bg-body"
                                            data-tooltip-id={`MedicalTrauma${index}`}
                                          >
                                            Medical Trauma
                                            <Tooltip
                                              className="w-50"
                                              id={`MedicalTrauma${index}`}
                                            >
                                              {getTooltip("ChildMedicalTrauma")}
                                            </Tooltip>
                                          </div>
                                          <div className="col py-2 bg-body border-start border-5">
                                            <Select
                                              name="MedicalTrauma"
                                              value={item.MedicalTrauma}
                                              error={item.Errors?.MedicalTrauma}
                                              options={yesNoOptions}
                                              onChange={(e) => {
                                                handleRowChange(
                                                  e,
                                                  index,
                                                  "ChildAssessments"
                                                );
                                              }}
                                              disabled={formDisabled}
                                            />
                                          </div>
                                        </div>
                                        <div className="mx-2 row border-bottom">
                                          <div
                                            className="col py-2 bg-body"
                                            data-tooltip-id={`DisasterTrauma${index}`}
                                          >
                                            Natural or Manmade Disaster
                                            <Tooltip
                                              className="w-50"
                                              id={`DisasterTrauma${index}`}
                                            >
                                              {getTooltip(
                                                "ChildDisasterTrauma"
                                              )}
                                            </Tooltip>
                                          </div>
                                          <div className="col py-2 bg-body border-start border-5">
                                            <Select
                                              name="DisasterTrauma"
                                              value={item.DisasterTrauma}
                                              error={
                                                item.Errors?.DisasterTrauma
                                              }
                                              options={yesNoOptions}
                                              onChange={(e) => {
                                                handleRowChange(
                                                  e,
                                                  index,
                                                  "ChildAssessments"
                                                );
                                              }}
                                              disabled={formDisabled}
                                            />
                                          </div>
                                        </div>
                                        <div className="mx-2 row border-bottom">
                                          <div
                                            className="col py-2 bg-body"
                                            data-tooltip-id={`FamilyViolence${index}`}
                                          >
                                            Witness to Family Violence
                                            <Tooltip
                                              className="w-50"
                                              id={`FamilyViolence${index}`}
                                            >
                                              {getTooltip(
                                                "ChildFamilyViolence"
                                              )}
                                            </Tooltip>
                                          </div>
                                          <div className="col py-2 bg-body border-start border-5">
                                            <Select
                                              name="FamilyViolence"
                                              value={item.FamilyViolence}
                                              error={
                                                item.Errors?.FamilyViolence
                                              }
                                              options={yesNoOptions}
                                              onChange={(e) => {
                                                handleRowChange(
                                                  e,
                                                  index,
                                                  "ChildAssessments"
                                                );
                                              }}
                                              disabled={formDisabled}
                                            />
                                          </div>
                                        </div>
                                        <div className="mx-2 row border-bottom">
                                          <div
                                            className="col py-2 bg-body"
                                            data-tooltip-id={`SchoolViolence${index}`}
                                          >
                                            Witness to Comm./School Violence
                                            <Tooltip
                                              className="w-50"
                                              id={`SchoolViolence${index}`}
                                            >
                                              {getTooltip(
                                                "ChildSchoolViolence"
                                              )}
                                            </Tooltip>
                                          </div>
                                          <div className="col py-2 bg-body border-start border-5">
                                            <Select
                                              name="SchoolViolence"
                                              value={item.SchoolViolence}
                                              error={
                                                item.Errors?.SchoolViolence
                                              }
                                              options={yesNoOptions}
                                              onChange={(e) => {
                                                handleRowChange(
                                                  e,
                                                  index,
                                                  "ChildAssessments"
                                                );
                                              }}
                                              disabled={formDisabled}
                                            />
                                          </div>
                                        </div>
                                        <div className="mx-2 row border-bottom">
                                          <div
                                            className="col py-2 bg-body"
                                            data-tooltip-id={`WarTrauma${index}`}
                                          >
                                            War/Terrorism Affected
                                            <Tooltip
                                              className="w-50"
                                              id={`WarTrauma${index}`}
                                            >
                                              {getTooltip("ChildWarTrauma")}
                                            </Tooltip>
                                          </div>
                                          <div className="col py-2 bg-body border-start border-5">
                                            <Select
                                              name="WarTrauma"
                                              value={item.WarTrauma}
                                              error={item.Errors?.WarTrauma}
                                              options={yesNoOptions}
                                              onChange={(e) => {
                                                handleRowChange(
                                                  e,
                                                  index,
                                                  "ChildAssessments"
                                                );
                                              }}
                                              disabled={formDisabled}
                                            />
                                          </div>
                                        </div>
                                        <div className="mx-2 row border-bottom">
                                          <div
                                            className="col py-2 bg-body"
                                            data-tooltip-id={`CriminalActsTrauma${index}`}
                                          >
                                            Witness/Victim of Criminal Acts
                                            <Tooltip
                                              id={`CriminalActsTrauma${index}`}
                                            >
                                              {getTooltip(
                                                "ChildCriminalActsTrauma"
                                              )}
                                            </Tooltip>
                                          </div>
                                          <div className="col py-2 bg-body border-start border-5">
                                            <Select
                                              name="CriminalActsTrauma"
                                              value={item.CriminalActsTrauma}
                                              error={
                                                item.Errors?.CriminalActsTrauma
                                              }
                                              options={yesNoOptions}
                                              onChange={(e) => {
                                                handleRowChange(
                                                  e,
                                                  index,
                                                  "ChildAssessments"
                                                );
                                              }}
                                              disabled={formDisabled}
                                            />
                                          </div>
                                        </div>
                                        <div className="mx-2 row border-bottom">
                                          <div
                                            className="col py-2 bg-body"
                                            data-tooltip-id={`ParentalCriminalTrauma${index}`}
                                          >
                                            Parental Criminal Behavior
                                            {/* <Tooltip className="w-50" id={`ParentalCriminalTrauma${index}`}>{getTooltip("ChildParentalCriminalTrauma")}</Tooltip> */}
                                          </div>
                                          <div className="col py-2 bg-body border-start border-5">
                                            <Select
                                              name="ParentalCriminalTrauma"
                                              value={
                                                item.ParentalCriminalTrauma
                                              }
                                              error={
                                                item.Errors
                                                  ?.ParentalCriminalTrauma
                                              }
                                              options={yesNoOptions}
                                              onChange={(e) => {
                                                handleRowChange(
                                                  e,
                                                  index,
                                                  "ChildAssessments"
                                                );
                                              }}
                                              disabled={formDisabled}
                                            />
                                          </div>
                                        </div>
                                        <div className="mx-2 row border-bottom">
                                          <div
                                            className="col py-2 bg-body"
                                            data-tooltip-id={`AttachmentTrauma${index}`}
                                          >
                                            Disrupt. in Caregiving/Attachment
                                            <Tooltip
                                              id={`AttachmentTrauma${index}`}
                                            >
                                              {getTooltip(
                                                "ChildAttachmentTrauma"
                                              )}
                                            </Tooltip>
                                          </div>
                                          <div className="col py-2 bg-body border-start border-5">
                                            <Select
                                              name="AttachmentTrauma"
                                              value={item.AttachmentTrauma}
                                              error={
                                                item.Errors?.AttachmentTrauma
                                              }
                                              options={yesNoOptions}
                                              onChange={(e) => {
                                                handleRowChange(
                                                  e,
                                                  index,
                                                  "ChildAssessments"
                                                );
                                              }}
                                              disabled={formDisabled}
                                            />
                                          </div>
                                        </div>
                                      </>
                                    ) : null}
                                    <div className="mx-2 row border-bottom bg-body">
                                      <div className="col pt-2">
                                        <FormTextArea
                                          name="NeedsRationale"
                                          label="Please write a rationale for any item in 6-21 Child Needs and Modulesrated actionable ('2' or '3')."
                                          value={item.NeedsRationale}
                                          error={item.Errors?.NeedsRationale}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "ChildAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </div>
                                    </div>
                                  </React.Fragment>
                                  {
                                    // Child Strengths
                                  }
                                  <React.Fragment>
                                    <div className="m-2 row">
                                      <div className="col-6 py-2">
                                        <div className="h5 text-white">
                                          6-21 Child Strengths
                                        </div>
                                      </div>
                                      <div className="col-1 bg-body py-2">
                                        {getTableScores(
                                          "ChildAssessments",
                                          "childStrengths",
                                          index
                                        )}
                                      </div>
                                      <div className="col"></div>
                                    </div>
                                    <div className="mx-2 row border-bottom bg-body">
                                      <div className="col py-2 bg-light">
                                        For Child Strengths, use the following
                                        categories and action levels:
                                        <br />
                                        0 - Well-developed, centerpiece
                                        strength; may be used as a focus of an
                                        intervention/action plan.
                                        <br />
                                        1 - Identified and useful strength.
                                        Strength will be used, maintained or
                                        built upon as part of the plan. May
                                        require some effort to develop strength
                                        into a centerpiece strength.
                                        <br />
                                        2 - Strengths have been identified but
                                        require strength-building efforts before
                                        they can be effectively utilized as part
                                        of a plan. Identified but not useful.
                                        <br />3 - An area in which no current
                                        strength is identified; efforts may be
                                        recommended to develop a strength in
                                        this area.
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`FamilyStrengths${index}`}
                                      >
                                        Family Strengths
                                        <Tooltip
                                          className="w-50"
                                          id={`FamilyStrengths${index}`}
                                        >
                                          {getTooltip("ChildFamilyStrengths")}
                                        </Tooltip>
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <FormInput
                                          name="FamilyStrengths"
                                          value={item.FamilyStrengths}
                                          error={item.Errors?.FamilyStrengths}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "ChildAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                          type="number"
                                          min="0"
                                          max="3"
                                        />
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`CommunityLife${index}`}
                                      >
                                        Community Life
                                        <Tooltip
                                          className="w-50"
                                          id={`CommunityLife${index}`}
                                        >
                                          {getTooltip("ChildCommunityLife")}
                                        </Tooltip>
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <FormInput
                                          name="CommunityLife"
                                          value={item.CommunityLife}
                                          error={item.Errors?.CommunityLife}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "ChildAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                          type="number"
                                          min="0"
                                          max="3"
                                        />
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`Talents${index}`}
                                      >
                                        Talents and Interests
                                        <Tooltip
                                          className="w-50"
                                          id={`Talents${index}`}
                                        >
                                          {getTooltip("ChildTalents")}
                                        </Tooltip>
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <FormInput
                                          name="Talents"
                                          value={item.Talents}
                                          error={item.Errors?.Talents}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "ChildAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                          type="number"
                                          min="0"
                                          max="3"
                                        />
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom">
                                      <div
                                        className="col py-2 bg-body"
                                        data-tooltip-id={`Optimism${index}`}
                                      >
                                        Optimism
                                        <Tooltip
                                          className="w-50"
                                          id={`Optimism${index}`}
                                        >
                                          {getTooltip("ChildOptimism")}
                                        </Tooltip>
                                      </div>
                                      <div className="col py-2 bg-body border-start border-5">
                                        <FormInput
                                          name="Optimism"
                                          value={item.Optimism}
                                          error={item.Errors?.Optimism}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "ChildAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                          type="number"
                                          min="0"
                                          max="3"
                                        />
                                      </div>
                                    </div>
                                    <div className="mx-2 row border-bottom bg-body">
                                      <div className="col pt-2">
                                        <FormTextArea
                                          name="StrengthsRationale"
                                          label="Please write a rationale for any item in 6-21 Child Strengths items rated actionable ('2' or '3'), and useful strengths ('0' and '1')."
                                          value={item.StrengthsRationale}
                                          error={
                                            item.Errors?.StrengthsRationale
                                          }
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "ChildAssessments"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </div>
                                    </div>
                                  </React.Fragment>
                                </React.Fragment>
                              )
                            )
                          : null}
                        {!formDisabled ? (
                          <button
                            className="btn btn-primary mt-2"
                            onClick={() => {
                              handleAddRow("ChildAssessments", {
                                IsEditable: true,
                              });
                            }}
                          >
                            Add
                          </button>
                        ) : null}
                      </FieldTable>
                    </div>
                  ) : null}
                </ExpandDiv>
              )}
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={helperConfiguration.finalSignType}
              initialSignatureType={helperConfiguration.signatureType}
            />
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            />
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              />
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default SCFRCPSFASTAssessment;

const getTooltip = (fieldName) => {
  return tooltips[fieldName];
};

const tooltips = {
  FamilyConflict: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">FAMILY CONFLICT</div>
          This item refers to how much fighting and arguing occurs between
          family members. Domestic violence refers to physical fighting in which
          family members might get hurt.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>Does anyone in the family have conflict with one another?</li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          0 - <i>No evidence of any needs; no need for action. </i>
          <div className="ms-2">
            Family has minimal conflict, gets along well and negotiates
            disagreements appropriately.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          1 -{" "}
          <i>
            Identified need that requires monitoring, watchful waiting, or
            preventive action based on history, suspicion or disagreement.{" "}
          </i>
          <div className="ms-2">
            Family generally gets along fairly well, but when conflicts arise
            resolution is difficult or there is a history of significant
            conflict or domestic violence.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          2 -{" "}
          <i>
            Action is required to ensure that the identified need is addressed;
            need is interfering with functioning.
          </i>
          <div className="ms-2">
            Family is generally argumentative and significant conflict is a
            fairly constant theme in family communications.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          3 -{" "}
          <i>
            Need is dangerous or disabling; requires immediate and/or intensive
            action.
          </i>
          <div className="ms-2">
            Family experiences domestic violence. There is threat or occurrence
            of physical, verbal or emotional altercations. If the family has a
            current restraining order against one member, then they would be
            rated here.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <b>Supplemental Information:</b> All families have conflict.
          Occasional arguments are not only normal but can be healthy if
          resolved eventually. This level of conflict would be rated a ‘0’. Only
          when the conflict begins to create notable problems within the family
          system would a rating of ‘1’ or higher be used.
        </div>
      </div>
    </>
  ),
  FamilyRoleAppropriateness: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">FAMILY ROLE APPROPRIATENESS</div>
          This item refers to boundaries and hierarchies within the family.
          Boundaries are the ability of family members to separate themselves as
          individuals and maintain role-appropriate communication among family
          members. Hierarchies refer to the organization of decision-making
          authority in the family.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>What are the boundaries like within the family?</li>
            <li>What is the nature of the family hierarchy?</li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          0 - <i>No evidence of any needs; no need for action.</i>
          <div className="ms-2">
            Adaptive boundaries. Family has strong appropriate boundaries among
            members. Clear inter-generational hierarchies are maintained.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          1 -{" "}
          <i>
            Identified need that requires monitoring, watchful waiting, or
            preventive action based on history, suspicion or disagreement.{" "}
          </i>
          <div className="ms-2">
            Mostly adaptive boundaries. Family has generally appropriate
            boundaries and hierarchies. May experience some minor blurring of
            roles.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          2 -{" "}
          <i>
            Action is required to ensure that the identified need is addressed;
            need is interfering with functioning.
          </i>
          <div className="ms-2">
            Limited adaptive boundaries. Family has difficulty maintaining
            appropriate boundaries and/or hierarchies. Some significant role
            problems exist.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          3 -{" "}
          <i>
            Need is dangerous or disabling; requires immediate and/or intensive
            action.
          </i>
          <div className="ms-2">
            Significant difficulties with boundaries. Family has significant
            problems with establishing and maintaining reasonable boundaries and
            hierarchies. Significant role confusion or reversals may exist.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <b>Supplemental Information:</b> This item is used to describe when
          family roles get out of line with healthy development. For example, in
          some single parent families, particularly when parental mental health
          or substance use needs are evident, older children step into
          developmentally-inappropriate parent roles (i.e., the parentified
          child). This role can place an enormous stress on the development of
          that child. Sometimes lonely parents will seek friendship and
          companionship from their children. This can be healthy to a point, but
          since parents have to maintain their role as supervisor and
          disciplinarian, too much of a friendship model can be unhealthy,
          particularly for younger children.
        </div>
      </div>
    </>
  ),
  FamilySafety: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">FAMILY SAFETY</div>
          This item refers to the degree to which family members are safe from
          being injured in the home. This item describes whether individuals in
          the home present a danger to the child. This item does not describe
          situations in which caregivers are unable to prevent a child from
          hurting themselves despite well-intentioned efforts.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Is the family able to protect the family members from harm in the
              home?
            </li>
            <li>
              Are there individuals living in the home or visiting the home that
              may be pose risks to the family?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          0 - <i>No evidence of any needs; no need for action. </i>
          <div className="ms-2">
            Family provides a safe home environment and the child is not at risk
            from others.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          1 -{" "}
          <i>
            Identified need that requires monitoring, watchful waiting, or
            preventive action based on history, suspicion or disagreement.{" "}
          </i>
          <div className="ms-2">
            Family home environment is safe, but concerns exist about the safety
            of the child due to family history or others in the household who
            present safety concerns.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          2 -{" "}
          <i>
            Action is required to ensure that the identified need is addressed;
            need is interfering with functioning.
          </i>
          <div className="ms-2">
            Family home environment presents some danger from one or more
            individuals with access to the household.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          3 -{" "}
          <i>
            Need is dangerous or disabling; requires immediate and/or intensive
            action.
          </i>
          <div className="ms-2">
            Family home environment presents a clear and immediate risk of harm
            to the child from one or more individuals.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <b>Supplemental Information:</b> A family living in a shelter is less
          safe than a family living in an apartment. A family living in an
          apartment in which gang members or drug abusers routinely come and go
          is less safe than a family living in an apartment which no such
          intrusions. In most cases, risks of domestic violence indicate an
          immediate/intensive level of need (i.e. ‘3’) on this item.
        </div>
      </div>
    </>
  ),
  FinancialResources: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">FINANCIAL RESOURCES</div>
          This item refers to the income and other sources of money available to
          family members (particularly caregivers) that can be used to address
          family needs.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Does the family have sufficient funds necessary to meet the
              family’s needs?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          0 - <i>No evidence of any needs; no need for action. </i>
          <div className="ms-2">
            Family has financial resources necessary to meet needs.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          1 -{" "}
          <i>
            Identified need that requires monitoring, watchful waiting, or
            preventive action based on history, suspicion or disagreement.{" "}
          </i>
          <div className="ms-2">
            Family has financial resources necessary to meet most needs, but the
            family has a history of financial hardship or there is reason to
            believe that mild difficulties might exist.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          2 -{" "}
          <i>
            Action is required to ensure that the identified need is addressed;
            need is interfering with functioning.
          </i>
          <div className="ms-2">
            Family has moderate financial difficulties that limit their ability
            to meet significant family needs.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          3 -{" "}
          <i>
            Need is dangerous or disabling; requires immediate and/or intensive
            action.
          </i>
          <div className="ms-2">
            Family is experiencing significant financial hardship or poverty.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <b>Supplemental Information:</b> Similar to the issue described with
          Family Conflict, few families have as many financial resources as they
          would like. Fewer still consider themselves as having sufficient
          resources. So, the ‘0’ level is used to indicate a ‘good enough’ level
          of financial resources. The family may not be rich, but that have
          enough money to take care of basic needs.
        </div>
      </div>
    </>
  ),
  ResidentialStability: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">RESIDENTIAL STABILITY</div>
          This item refers to the stability of the family’s housing. This does
          not refer to the risk of placement outside of the familyhome for any
          member of the family.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>Is the family’s current housing situation stable?</li>
            <li>
              Are there concerns that they might have to move in the near
              future?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          0 - <i>No evidence of any needs; no need for action. </i>
          <div className="ms-2">
            Family has stable housing for the foreseeable future.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          1 -{" "}
          <i>
            Identified need that requires monitoring, watchful waiting, or
            preventive action based on history, suspicion or disagreement.{" "}
          </i>
          <div className="ms-2">
            Family currently has stable housing; however, the family has a
            history of housing instability or there is reason to believe that
            there may be mild difficulties maintaining housing due to things
            such as difficulty paying rent or utilities or conflict with a
            landlord.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          2 -{" "}
          <i>
            Action is required to ensure that the identified need is addressed;
            need is interfering with unctioning.
          </i>
          <div className="ms-2">
            Family has had to move in the past year or will have to move in the
            near future due to housing difficulties.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          3 -{" "}
          <i>
            Need is dangerous or disabling; requires immediate and/or intensive
            action.
          </i>
          <div className="ms-2">
            Family has experienced homelessness in the past six months.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <b>Supplemental Information:</b> A ‘3’ indicates problems of recent
          homelessness. A ‘1’ indicates concerns about instability in the
          immediate future. A family having difficulty paying utilities, rent or
          a mortgage might be rated as a ‘1’. This item refers exclusively to
          the housing stability of the caregiver and should not reflect whether
          the child might be placed outside of the home.
        </div>
      </div>
    </>
  ),
  FamilySocialIdentity: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">FAMILY SOCIAL IDENTITY</div>
          This item describes a central, shared and guiding worldview that is
          inclusive of family cultural and/or ethnic identity. Signs of family
          social identity include a shared feeling of belonging and pride in
          ethnic group(s), a strong shared family narrative that includes
          knowledge of family history (how we got through, etc.) and a shared
          guiding narrative about values and expectations that is reflected in
          how families talk about their history. Families members share positive
          belief systems associated with their ability to band together to
          overcome adversity.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Does the family have a shared feeling of belonging and view of
              itself that organizes the family?
            </li>
            <li>
              How consistent is the sense of family social identity across the
              family members?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          0 -{" "}
          <i>
            Well-developed, centerpiece strength; may be used as a centerpiece
            in an intervention/ action plan.{" "}
          </i>
          <div className="ms-2">
            Family has a strong social identity. The family’s consistent and
            coherent view of itself as a family is a powerful organizing factor
            for the family. There is unanimity in the family of the importance
            of the family in all family member’s lives.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          1 -{" "}
          <i>
            Identified and useful strength. Strength will be used, maintained or
            built upon as part of the plan. May require some effort to develop
            strength into a centerpiece strength.
          </i>
          <div className="ms-2">
            Family has a social identity. There is a consistent feeling among
            all family members that the family has a unique identity. There is a
            consistent recognition that the family is relevant in all family
            member’s lives.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          2 -{" "}
          <i>
            Strengths have been identified but require strength-building efforts
            before they can be effectively utilized as part of a plan.
            Identified but not useful.
          </i>
          <div className="ms-2">
            At least one member of the family has a sense of a family social
            identity. However, this sense is not consistent across family
            members.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          3 -{" "}
          <i>
            An area in which no current strength is identified; efforts may be
            recommended to develop a strength in this area.
          </i>
          <div className="ms-2">
            No evidence that family has a family social identity. There does not
            appear to be a coherent and consistent view of itself as a family
            unit among any members of the family.
          </div>
        </div>
      </div>
    </>
  ),
  FamilyCollaboration: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">PARENTAL/CAREGIVER COLLABORATION</div>
          This item refers to the relationship between parents (or other primary
          caregivers) and the skills related to working together in
          child-rearing activities.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              How do caregivers handle disagreements between each other as they
              relate to parenting?
            </li>
            <li>How well do caregivers communicate?</li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          0 -{" "}
          <i>
            Well-developed, centerpiece strength; may be used as a centerpiece
            in an intervention/ action plan.
          </i>
          <div className="ms-2">
            Adaptive collaboration. Parents can work together regarding family
            issues and issues of the development and well-being of the
            child(ren). They are able to negotiate disagreements and work on
            challenges together.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          1 -{" "}
          <i>
            Identified and useful strength. Strength will be used, maintained or
            built upon as part of the plan. May require some effort to develop
            strength into a centerpiece strength.
          </i>
          <div className="ms-2">
            Mostly adaptive collaboration. Generally good parental
            collaboration. While there may be occasional difficulties
            negotiating, or miscommunications and misunderstandings, the
            caregivers are able to these resolve issues and remain focused on
            the well-being of the child(ren).
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          2 -{" "}
          <i>
            Strengths have been identified but require strength-building efforts
            before they can be effectively utilized as part of a plan.
            Identified but not useful.
          </i>
          <div className="ms-2">
            Caregivers need supports to strengthen the communication and
            collaboration skills of the caregiving system.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          3 -{" "}
          <i>
            An area in which no current strength is identified; efforts may be
            recommended to develop a strength in this area.
          </i>
          <div className="ms-2">
            Caregivers cannot currently manage family disagreements, effectively
            communicate about supporting the child(ren)’s well-being and develop
            shared approaches to parenting.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <b>Supplemental Information:</b> Families that do not have two parents
          can also have caregiving alliances. Anytime more than one caregiver is
          involved in a family, the degree to which the caregivers work together
          in support of each other is an important family characteristic.
          Divorced parents can maintain good collaborations in their efforts to
          parent their shared children despite living separate adult lives.
        </div>
      </div>
    </>
  ),
  SiblingRelationships: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">RELATIONS AMONG SIBLINGS</div>
          This item refers to how the children in the family (brothers and
          sisters as well as step-siblings, half-siblings and foster siblings)
          get along with each other.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>Describe the relationship among the siblings.</li>
            <li>How do the siblings currently handle disagreements?</li>
            <li>
              How does the sibling respond if they recognize their sibling is
              facing a challenge?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          0 -{" "}
          <i>
            Well-developed, centerpiece strength; may be used as a centerpiece
            in an intervention/action plan.
          </i>
          <div className="ms-2">
            Adaptive relationships. Siblings generally get along well and have a
            supportiverelationship. They can identify their siblings’ emotional
            needs and are responsive to thoseneeds.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          1 -{" "}
          <i>
            Identified and useful strength. Strength will be used, maintained or
            built upon as part of the plan. May require some effort to develop
            strength into a centerpiece strength.
          </i>
          <div className="ms-2">
            Mostly adaptive relationships. Siblings generally get along and
            there is a healthy and supportive relationship among siblings.
            Siblings will have the occasional disagreement or fight that they
            are able to resolve.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          2 -{" "}
          <i>
            Strengths have been identified but require strength-building efforts
            before they can be effectively utilized as part of a plan.
            Identified but not useful.
          </i>
          <div className="ms-2">
            Siblings need support to further develop a healthy and supportive
            sibling relationship. Siblings generally attempt to resolve any
            disagreements or fights but often rely on supports to do so.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          3 -{" "}
          <i>
            An area in which no current strength is identified; efforts may be
            recommended to develop a strength in this area.
          </i>
          <div className="ms-2">
            No evidence that siblings currently have a healthy and supportive
            relationship.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <b>Supplemental Information:</b> This item is rated based on whether
          any problems exist that require intervention. Thus a ‘2’ could be used
          even in circumstances where the sibling problems just involve a
          relationship between two children in a much larger sibling group.
        </div>
      </div>
    </>
  ),
  ExtendedRelationships: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">EXTENDED FAMILY RELATIONS</div>
          This item refers to the family’s relationship with other relatives who
          do not currently live with the family but do live in the same
          geographic area. This can include relatives who are not blood
          relatives (e.g., related by marriage).
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>Does extended family play a role with the family?</li>
            <li>
              What are the relationships like between the family members and the
              extended family?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          0 -{" "}
          <i>
            Well-developed, centerpiece strength; may be used as a centerpiece
            in an intervention/action plan.
          </i>
          <div className="ms-2">
            Extended family members play a central role in the functioning and
            well-being of the family. They have predominately positive
            relationships with members of the extended family and conflicts are
            resolved quickly.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          1 -{" "}
          <i>
            Identified and useful strength. Strength will be used, maintained or
            built upon as part of the plan. May require some effort to develop
            strength into a centerpiece strength.
          </i>
          <div className="ms-2">
            Extended family members play a supportive role in family
            functioning. They generally have positive relationships with members
            of the extended family. Conflicts may linger but eventually are
            resolved.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          2 -{" "}
          <i>
            Strengths have been identified but require strength-building efforts
            before they can be effectively utilized as part of a plan.
            Identified but not useful.
          </i>
          <div className="ms-2">
            Extended family members can be identified but are currently
            marginally involved in the functioning and well-being of the family.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          3 -{" "}
          <i>
            An area in which no current strength is identified; efforts may be
            recommended to develop a strength in this area.
          </i>
          <div className="ms-2">
            Family either does not have extended family or current relationships
            are not positive and/or supportive.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <b>Supplemental Information:</b> A family who has recently immigrated
          to the United States may be estranged from their extended family
          simply because of geographic distance. This estrangement can be
          stressful and would be rated in this item.
        </div>
      </div>
    </>
  ),
  NaturalSupports: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">NATURAL SUPPORTS</div>
          This item rates the level of support that the family can access from
          unpaid others. This item generally excludes immediate and extended
          family as well as professionals who provide support as a part of their
          role.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Does the family have friends, connections from community groups
              (e.g., church, volunteer group, book club, mothers’ group), or
              others who provide support?
            </li>
            <li>
              Can the family call on this support network to help in times of
              need?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          0 -{" "}
          <i>
            Well-developed, centerpiece strength; may be used as a centerpiece
            in an intervention/ action plan.
          </i>
          <div className="ms-2">
            Family has substantial natural supports to assist in addressing most
            family and child needs.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          1 -{" "}
          <i>
            Identified and useful strength. Strength will be used, maintained or
            built upon as part of the plan. May require some effort to develop
            strength into a centerpiece strength.
          </i>
          <div className="ms-2">Family has natural supports.</div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          2 -{" "}
          <i>
            Strengths have been identified but require strength-building efforts
            before they can be effectively utilized as part of a plan.
            Identified but not useful.
          </i>
          <div className="ms-2">
            Family has limited natural supports. Natural supports can be
            identified but they are not currently supportive.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          3 -{" "}
          <i>
            An area in which no current strength is identified; efforts may be
            recommended to develop a strength in this area.
          </i>
          <div className="ms-2">
            No evidence of natural supports or family has no natural supports.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <b>Supplemental Information:</b> The existing body of research in
          system of care indicates that this is the single hardest aspect of
          wraparound philosophy to develop. However, it is also one of the most
          powerful aspects of intensive community-based intervention. The
          concept of natural supports comes from the African parable that ‘it
          takes a village’ to raise a child. Paid individuals only count as
          natural supports if it is someone who would remain involved with the
          family even if they were not receiving a check.
        </div>
      </div>
    </>
  ),
  FamilyTraditions: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">TRADITIONS AND RITUALS</div>
          This item rates the family’s access to and participation in cultural
          traditions, rituals and practices, including the celebration of
          culturally specific holidays such as Kwanza, Día de los Muertos, Yom
          Kippur, Quinceanera, etc. This also may include daily activities that
          are culturally specific (e.g., wearing a hijab, praying toward Mecca
          at specific times, eating a specific diet, access to media), and
          traditions and activities to include newer cultural identities.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>What holidays does the family celebrate?</li>
            <li>What traditions are important to the family?</li>
            <li>
              Does the family fear discrimination for practicing their
              traditions and rituals?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          0 -{" "}
          <i>
            Well-developed, centerpiece strength; may be used as a centerpiece
            in an intervention/ action plan.
          </i>
          <div className="ms-2">
            Family consistently practices their chosen traditions and rituals
            consistent with their cultural identity.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          1 -{" "}
          <i>
            Identified and useful strength. Strength will be used, maintained or
            built upon as part of the plan. May require some effort to develop
            strength into a centerpiece strength.
          </i>
          <div className="ms-2">
            Family generally practices their chosen traditions and rituals
            consistent with their cultural identity; however, they sometimes
            experience some obstacles to the performance of these practices.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          2 -{" "}
          <i>
            Strengths have been identified but require strength-building efforts
            before they can be effectively utilized as part of a plan.
            Identified but not useful.
          </i>
          <div className="ms-2">
            Family experiences significant barriers and are sometimes prevented
            from practicing their chosen traditions and rituals consistent with
            their cultural identity.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          3 -{" "}
          <i>
            An area in which no current strength is identified; efforts may be
            recommended to develop a strength in this area.
          </i>
          <div className="ms-2">
            Family is unable to practice their chosen traditions and rituals
            consistent with their cultural identity.
          </div>
        </div>
      </div>
    </>
  ),
  KnowledgeOfNeeds: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">KNOWLEDGE OF FAMILY AND CHILD NEEDS</div>
          This item refers to the caregiver’s ability to recognize the needs of
          the family and individual family members.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Does the caregiver understand what types of service options are
              available to their family?
            </li>
            <li>
              Is the caregiver able to identify the needs of the family? Of the
              individual family members?
            </li>
            <li>
              Is the caregiver’s lack of understanding of the family’s needs
              interfering with the family’s functioning?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          0 - <i>Mastery, could teach others. Maintain mastery.</i>
          <div className="ms-2">
            The caregiver has a strong understanding of family and child needs.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          1 - <i>Comfortable and routine. Develop mastery.</i>
          <div className="ms-2">
            The caregiver has an understanding of family and child needs, but
            may require some help in learning about certain aspects of these
            needs or there is a history of caregiver lack of knowledge.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          2 - <i>Comfortable, but not routine. Build into a routine.</i>
          <div className="ms-2">
            The caregiver sometimes identifies and understands family and/or
            child needs, but occasionally still needs assistance to identify or
            understand needs.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          3 - <i>Tried; Not yet comfortable. Develop comfort.</i>
          <div className="ms-2">
            The caregiver requires some assistance in identifying and
            understanding family and child needs.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          4 - <i>Have never done. Try skill.</i>
          <div className="ms-2">
            The caregiver requires substantial assistance in identifying and
            understanding family and child needs.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <b>Supplemental Information:</b> A family who is pushing for the
          residential placement of a child when that child’s needs are not
          sufficiently complex to warrant out-of-community treatment would be
          rated a ‘3’ on this item.
        </div>
      </div>
    </>
  ),
  KnowledgeOfOptions: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">KNOWLEDGE OF SERVICE OPTIONS</div>
          This item refers to the choices the family might have for specific
          treatments, interventions or other services that might help the family
          address their needs or the needs of one of the family’s members.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Does the caregiver understand what types of service options are
              available to their family?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          0 - <i>Mastery, could teach others. Maintain mastery.</i>
          <div className="ms-2">
            The caregiver has a strong understanding of service options.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          1 - <i>Comfortable and routine. Develop mastery.</i>
          <div className="ms-2">
            The caregiver has an understanding of service options, but may
            require some help in learning about certain aspects of these
            services or there is a history of caregiver lack of understanding of
            service options.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          2 - <i>Comfortable, but not routine. Build into a routine.</i>
          <div className="ms-2">
            The caregiver sometimes understands service options, but
            occasionally still needs assistance to understand available service
            options.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          3 - <i>Tried; Not yet comfortable. Develop comfort.</i>
          <div className="ms-2">
            The caregiver requires some assistance in identifying and
            understanding service options.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          4 - <i>Have never done. Try skill.</i>
          <div className="ms-2">
            The caregiver requires substantial assistance in identifying and
            understanding service options.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <b>Supplemental Information:</b> A family who is pushing for the
          residential placement of a child when that child’s needs are not
          sufficiently complex to warrant out of community treatment would be
          described on this item.
        </div>
      </div>
    </>
  ),
  KnowledgeOfRights: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">KNOWLEDGE OF RIGHTS & RESPONSIBILITIES</div>
          This item refers to the caregiver’s ability to understand and
          acknowledge the legal and societal expectations and responsibilities
          of their caregiver roles.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Does the individual understand their rights and responsibilities
              as a caregiver?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          0 - <i>Mastery, could teach others. Maintain mastery.</i>
          <div className="ms-2">
            The caregiver has a strong understanding of rights and
            responsibilities.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          1 - <i>Comfortable and routine. Develop mastery.</i>
          <div className="ms-2">
            The caregiver has an understanding of rights and responsibilities,
            but may require some help in learning about certain aspects of these
            rights and responsibilities or there is a history of caregiver lack
            of understanding of rights and responsibilities.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          2 - <i>Comfortable, but not routine. Build into a routine.</i>
          <div className="ms-2">
            The caregiver sometimes understands rights and responsibilities, but
            occasionally still needs assistance to understand some rights and
            responsibilities.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          3 - <i>Tried; Not yet comfortable. Develop comfort.</i>
          <div className="ms-2">
            The caregiver requires some assistance in identifying and
            understanding rights and responsibilities.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          4 - <i>Have never done. Try skill.</i>
          <div className="ms-2">
            The caregiver requires substantial assistance in identifying and
            understanding rights and responsibilities.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <b>Supplemental Information:</b> Lack of mastery, or action at any
          level, should be addressed primarily using educational resources.
        </div>
      </div>
    </>
  ),
  AbilityToListen: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">ABILITY TO LISTEN</div>
          This item refers to the caregiver’s ability to hear both positive and
          negative feedback about themselves and family members. This item would
          include the caregiver asking clarifying questions.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Is the caregiver able to listen and understand news regarding
              their family?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          0 - <i>Mastery, could teach others. Maintain mastery.</i>
          <div className="ms-2">
            The caregiver is able to listen carefully and understand both good
            and bad news regarding family and child issues.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          1 - <i>Comfortable and routine. Develop mastery.</i>
          <div className="ms-2">
            The caregiver has generally effective listening skills but sometimes
            struggles to hear either good or bad news regarding family and child
            issues.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          2 - <i>Comfortable, but not routine. Build into a routine.</i>
          <div className="ms-2">
            The caregiver sometimes listens effectively, but occasionally still
            needs help to listen effectively.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          3 - <i>Tried; Not yet comfortable. Develop comfort.</i>
          <div className="ms-2">
            The caregiver requires some help to learn to listen effectively.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          4 - <i>Have never done. Try skill.</i>
          <div className="ms-2">
            The caregiver requires substantial help to learn to listen
            effectively.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <b>Supplemental Information:</b> Listening can be extremely difficult
          when you are hearing your child described in a negative light.
          However, it is an important skill to sit and listen even when the news
          is hard to take and even when you are convinced that the person
          speaking is absolutely wrong. Talking over people or not letting
          people finish their thoughts becomes problematic when it comes your
          turn to speak.
        </div>
      </div>
    </>
  ),
  AbilityToCommunicate: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">ABILITY TO COMMUNICATE</div>
          This item refers to the caregiver’s ability to effectively describe
          their needs and the needs of other family members in a manner that
          others can understand.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Is the caregiver able to express their thoughts and feelings
              effectively?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          0 - <i>Mastery, could teach others. Maintain mastery.</i>
          <div className="ms-2">
            The caregiver is able to express feelings and thoughts effectively
            with regard to family and child issues. Others hear, understand, and
            respond.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          1 - <i>Comfortable and routine. Develop mastery.</i>
          <div className="ms-2">
            The caregiver is generally able to express feelings and thoughts
            effectively but sometimes struggles to express them in a way that
            others can listen and/or understand.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          2 - <i>Comfortable, but not routine. Build into a routine.</i>
          <div className="ms-2">
            The caregiver sometimes expresses feelings and thoughts effectively
            with regard to family and child issues, but occasionally still needs
            help to learn to express feelings and thoughts effectively.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          3 - <i>Tried; Not yet comfortable. Develop comfort.</i>
          <div className="ms-2">
            The caregiver requires some help to learn to express feelings and
            thoughts effectively with regard to family and child issues.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          4 - <i>Have never done. Try skill.</i>
          <div className="ms-2">
            The caregiver requires substantial help to learn to express feelings
            and thoughts effectively with regard to family and child issues.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <b>Supplemental Information:</b> The FAST can be seen as a strategy to
          help develop exactly what family leadership needs to communicate to
          the system so that their family’s needs are effectively addressed.
          Teaching parents and family leaders to be able to communicate
          effectively with professionals is an important goal in advocacy
          development.
        </div>
      </div>
    </>
  ),
  FamilyLanguage: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">LANGUAGE</div>
          This item looks at whether the family needs help with communication to
          obtain the necessary resources, supports and accommodations (e.g.,
          interpreter). This item includes spoken, written, and sign language,
          as well as issues of literacy.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>What language does the family speak at home?</li>
            <li>
              Is there a child interpreting for the family in situations that
              may compromise the child or family’s care?
            </li>
            <li>
              Does the child or significant family members have any special
              needs related to communication (e.g., ESL, ASL, Braille, or
              assisted technology)?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          0 - <i>No evidence of any needs; no need for action. </i>
          <div className="ms-2">
            No evidence that there is a need or preference for an interpreter
            and/or the family speaks and reads the primary language where they
            live.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          1 -{" "}
          <i>
            Identified need that requires monitoring, watchful waiting, or
            preventive action based on history, suspicion or disagreement.
          </i>
          <div className="ms-2">
            Family speaks or reads the primary language where they live, but
            potential communication problems exist because of limited vocabulary
            or comprehension of the nuances of the language.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          2 -{" "}
          <i>
            Action is required to ensure that the identified need is addressed;
            need is interfering with functioning.
          </i>
          <div className="ms-2">
            Significant family members do not speak the primary language where
            the family lives. Translator or family’s native language speaker is
            needed for successful intervention; a qualified individual(s) can be
            identified within natural supports.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          3 -{" "}
          <i>
            Need is dangerous or disabling; requires immediate and/or intensive
            action.
          </i>
          <div className="ms-2">
            Significant family members do not speak the primary language where
            the family lives. Translator or family’s native language speaker is
            needed for successful intervention; no such individual is available
            from among natural supports.
          </div>
        </div>
      </div>
    </>
  ),
  FamilyCulturalStress: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">CULTURAL STRESS</div>
          This item identifies circumstances in which the family’s cultural
          identity is met with hostility or other problems within their
          environment due to differences in attitudes, behavior, or beliefs of
          others (this includes cultural differences that are causing stress
          between the child and their family). Racism, negativity toward sexual
          orientation, gender identity and expression (SOGIE) and other forms of
          discrimination would be rated here.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              What does the family believe is their reality of discrimination?
              How do they describe discrimination or oppression?
            </li>
            <li>
              Does this impact their functioning as both individuals and as a
              family?
            </li>
            <li>
              How does the caregiver support the child’s identity and
              experiences if different from the caregiver’s own?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          0 - <i>No evidence of any needs; no need for action. </i>
          <div className="ms-2">
            No evidence of stress between the family’s cultural identity and
            current environment or living situation.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          1 -{" "}
          <i>
            Identified need that requires monitoring, watchful waiting, or
            preventive action based on history, suspicion or disagreement.
          </i>
          <div className="ms-2">
            Some mild or occasional stress resulting from friction between the
            family’s cultural identity and current environment or living
            situation.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          2 -{" "}
          <i>
            Action is required to ensure that the identified need is addressed;
            need is interfering with functioning.
          </i>
          <div className="ms-2">
            Family is experiencing cultural stress that is causing problems of
            functioning in at least one life domain. Family needs support to
            learn how to manage culture stress.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          3 -{" "}
          <i>
            Need is dangerous or disabling; requires immediate and/or intensive
            action.
          </i>
          <div className="ms-2">
            Family is experiencing a high level of cultural stress that is
            making functioning in any life domain difficult under the present
            circumstances. Family needs immediate plan to reduce culture stress.
          </div>
        </div>
      </div>
    </>
  ),
  CaregiverMonitoring: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">MONITORING</div>
          This item refers to the success with which the caregiver is able to
          monitor children in their care. This item should be rated consistent
          with the developmental needs of the child.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Does the caregiver provide consistent supervision to the children?
            </li>
            <li>
              Does the caregiver think they need some help with these issues?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          0 - <i>No evidence of any needs; no need for action.</i>
          <div className="ms-2">
            There is no evidence that the caregiver has difficulties monitoring
            the children in their care. The caregiver demonstrates consistent
            ability to monitor children in their care according to their
            developmental needs.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          1 -{" "}
          <i>
            Identified need that requires monitoring, watchful waiting, or
            preventive action based on history, suspicion or disagreement.
          </i>
          <div className="ms-2">
            The caregiver demonstrates generally good ability to monitor
            children in their care but some problems may occur occasionally or
            there is a history of inadequate monitoring.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          2 -{" "}
          <i>
            Need is interfering with the provision of care; action is required
            to ensure that the identified need is addressed.
          </i>
          <div className="ms-2">
            The caregiver has difficulty maintaining an appropriate level of
            monitoring of children in their care.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          3 -{" "}
          <i>
            Need prevents the provision of care; requires immediate and/or
            intensive action.
          </i>
          <div className="ms-2">
            The caregiver has significant problems maintaining any monitoring of
            children in their care.
          </div>
        </div>
      </div>
    </>
  ),
  CaregiverKnowledge: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">KNOWLEDGE</div>
          This item identifies the caregiver’s knowledge of the child’s
          strengths and needs, and their ability to understand the rationale for
          the treatment or management of these problems.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Does the caregiver understand the child’s current mental health
              diagnosis and/or symptoms?
            </li>
            <li>
              Does the caregiver’s expectations of the child reflect an
              understanding of the child’s mental or physical challenges?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          0 - <i>No evidence of any needs; no need for action.</i>
          <div className="ms-2">
            No evidence of caregiver knowledge issues. Caregiver is fully
            knowledgeable about the child's psychological strengths and
            weaknesses, talents and limitations.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          1 -{" "}
          <i>
            Identified need that requires monitoring, watchful waiting, or
            preventive action based on history, suspicion or disagreement.
          </i>
          <div className="ms-2">
            Caregiver, while being generally knowledgeable about the child, has
            some mild deficits in knowledge or understanding of the child's
            psychological condition, talents, skills and assets.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          2 -{" "}
          <i>
            Need is interfering with the provision of care; action is required
            to ensure that the identified need is addressed.
          </i>
          <div className="ms-2">
            Caregiver does not know or understand the child well and significant
            deficits exist in the caregiver's ability to relate to the child's
            problems and strengths.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          3 -{" "}
          <i>
            Need prevents the provision of care; requires immediate and/or
            intensive action.
          </i>
          <div className="ms-2">
            Caregiver has little or no understanding of the child's current
            condition. Caregiver’s lack of knowledge about the child’s strengths
            and needs place the child at risk of significant negative outcomes.
          </div>
        </div>
      </div>
    </>
  ),
  CaregiverBoundaries: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">BOUNDARIES</div>
          This item refers to the caregiver’s ability to maintain appropriate
          boundaries. This item may include physical separation, respecting
          privacy, and preventing children from being exposed to
          developmentally-inappropriate information.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              What is the nature of the caregiver’s boundaries in relation to
              others?
            </li>
            <li>
              Does the caregiver have any difficulties in maintaining their
              boundaries? Or are the caregiver’s boundaries overly rigid?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          0 - <i>No evidence of any needs; no need for action.</i>
          <div className="ms-2">
            Adaptive boundaries. Caregiver has strong, appropriate boundaries
            between themselves and their children.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          1 -{" "}
          <i>
            Identified need that requires monitoring, watchful waiting, or
            preventive action based on history, suspicion or disagreement.
          </i>
          <div className="ms-2">
            Mostly adaptive boundaries. Caregiver has generally appropriate
            boundaries between themselves and their children. Mild boundary
            violations may occur at times. Minor problems of rigidity of
            boundaries may occur.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          2 -{" "}
          <i>
            Need is interfering with the provision of care; action is required
            to ensure that the identified need is addressed.
          </i>
          <div className="ms-2">
            Limited adaptive boundaries. Caregiver has problems maintaining
            appropriate boundaries between themselves and their children. Mild
            boundary violations may be routine or significant boundary
            violations may be occasional. Boundaries may be rigid.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          3 -{" "}
          <i>
            Need prevents the provision of care; requires immediate and/or
            intensive action.
          </i>
          <div className="ms-2">
            Significant difficulties with boundaries. Caregiver has significant
            and consistent problems maintaining appropriate boundaries between
            themselves and their children or is excessively rigid in their
            boundaries.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <b>Supplemental Information:</b> A parent who confides their secrets
          to the children is violating boundaries. A parent who tells the
          children about how badly the other parent treats them (e.g.
          infidelity) is violating boundaries. A parent who cannot stop a child
          from entering the bathroom on them is experiencing problems with
          boundaries.
        </div>
      </div>
    </>
  ),
  CaregiverDiscipline: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">DISCIPLINE</div>
          Discipline refers to the caregiver’s ability to encourage positive
          behaviors by children in their care through the use of a variety of
          different techniques including, but not limited to, praise,
          redirection, and punishment.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Is the caregiver able to provide appropriate limits to the
              children?
            </li>
            <li>
              Does the caregiver provide appropriate support to the childto meet
              the caregiver’s expectations?
            </li>
            <li>
              Does the caregiver think they need some help with these issues?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          0 - <i>No evidence of any needs; no need for action.</i>
          <div className="ms-2">
            There is no evidence that the caregiver has difficulty with
            discipline. The caregiver generally demonstrates the ability to
            discipline children in their care in a consistent and benevolent
            manner. They usually are able to set age-appropriate limits and to
            enforce hem.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          1 -{" "}
          <i>
            Identified need that requires monitoring, watchful waiting, or
            preventive action based on history, suspicion or disagreement.
          </i>
          <div className="ms-2">
            The caregiver is often able to set age-appropriate limits and to
            enforce them. On occasion their interventions may be either too
            harsh or too lenient but at other times their expectations of
            children in their care may be too high or too low, or there is a
            history of inappropriate discipline.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          2 -{" "}
          <i>
            Need is interfering with the provision of care; action is required
            to ensure that the identified need is addressed.
          </i>
          <div className="ms-2">
            The caregiver demonstrates limited ability to discipline children in
            their care in a consistent and benevolent manner. They are rarely
            able to set age-appropriate limits and to enforce them. Their
            interventions may be erratic and overly harsh but not physically
            harmful. Their expectations of children in their care are frequently
            unrealistic.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          3 -{" "}
          <i>
            Need prevents the provision of care; requires immediate and/or
            intensive action.
          </i>
          <div className="ms-2">
            The caregiver disciplines children in their care in an unpredictable
            fashion. There is either an absence of limit setting and
            disciplinary interventions or the limit setting and disciplinary
            interventions are rigid, extreme, or physically harmful.
          </div>
        </div>
      </div>
    </>
  ),
  CaregiverMedical: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">MEDICAL/PHYSICAL</div>
          This item refers to medical problems and/or physical limitations that
          the caregiver(s) may be experiencing that prevent or limit their
          ability to provide care for the child. This item does not rate
          depression or other mental health issues.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>How is the caregiver’s health?</li>
            <li>
              Does the caregiver have any health problems that limit their
              ability to care for the family?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          0 - <i>No evidence of any needs; no need for action.</i>
          <div className="ms-2">
            No evidence of medical or physical health problems. Caregiver is
            generally healthy.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          1 -{" "}
          <i>
            Identified need that requires monitoring, watchful waiting, or
            preventive action based on history, suspicion or disagreement.
          </i>
          <div className="ms-2">
            There is a history or suspicion of, and/or caregiver is in recovery
            from medical/physical problems.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          2 -{" "}
          <i>
            Need is interfering with the provision of care; action is required
            to ensure that the identified need is addressed.
          </i>
          <div className="ms-2">
            Caregiver has medical/physical problems that interfere with the
            capacity to provide care for the child.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          3 -{" "}
          <i>
            Need prevents the provision of care; requires immediate and/or
            intensive action.
          </i>
          <div className="ms-2">
            Caregiver has medical/physical problems that make providing care for
            the child impossible at this time.
          </div>
        </div>
      </div>
    </>
  ),
  CaregiverMentalHealth: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">MENTAL HEALTH*</div>
          This item refers to any serious mental health issues (not including
          substance abuse) among caregivers that might limit their capacity for
          parenting/caregiving to the child.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>Does the caregiver have any mental health needs?</li>
            <li>
              Are the caregiver’s mental health needs interfering with their
              functioning?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          0 - <i>No evidence of any needs; no need for action.</i>
          <div className="ms-2">
            No evidence of caregiver mental health difficulties.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          1 -{" "}
          <i>
            Identified need that requires monitoring, watchful waiting, or
            preventive action based on history, suspicion or disagreement.
          </i>
          <div className="ms-2">
            There is a history or suspicion of mental health difficulties,
            and/or caregiver is in recovery from mental health difficulties.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          2 -{" "}
          <i>
            Need is interfering with the provision of care; action is required
            to ensure that the identified need is addressed.
          </i>
          <div className="ms-2">
            Caregiver’s mental health difficulties interfere with their capacity
            to parent.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          3 -{" "}
          <i>
            Need prevents the provision of care; requires immediate and/or
            intensive action.
          </i>
          <div className="ms-2">
            Caregiver has mental health difficulties that make it impossible to
            parent the child at this time.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="pt-1 fw-bold bg-secondary text-center">
            *A rating of ‘1,’ ‘2,’ or ‘3’ on this item triggers the completion
            of the [A] Mental Health Module.
          </div>
        </div>
      </div>
    </>
  ),
  CaregiverDevelopmental: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">DEVELOPMENTAL</div>
          This item describes the presence of limited cognitive capacity or
          developmental disabilities that challenges the caregiver’s ability to
          parent.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Has the caregiver been identified with any developmental
              disabilities or intellectual disabilities?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          0 - <i>No evidence of any needs; no need for action.</i>
          <div className="ms-2">
            No evidence of caregiver developmental disabilities or challenges.
            Caregiver has no developmental needs.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          1 -{" "}
          <i>
            Identified need that requires monitoring, watchful waiting, or
            preventive action based on history, suspicion or disagreement.
          </i>
          <div className="ms-2">
            Caregiver has developmental challenges. The developmental challenges
            do not currently interfere with parenting.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          2 -{" "}
          <i>
            Need is interfering with the provision of care; action is required
            to ensure that the identified need is addressed.
          </i>
          <div className="ms-2">
            Caregiver has developmental challenges that interfere with the
            capacity to parent the child.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          3 -{" "}
          <i>
            Need prevents the provision of care; requires immediate and/or
            intensive action.
          </i>
          <div className="ms-2">
            Caregiver has severe developmental challenges that make it
            impossible to parent the child at this time.
          </div>
        </div>
      </div>
    </>
  ),
  CaregiverSubstanceUse: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">SUBSTANCE USE*</div>
          This item rates the impact of any notable substance use by caregivers
          that might limit their capacity to provide care for the child.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Do caregivers have any substance use needs that make parenting
              difficult?
            </li>
            <li>
              Does anyone else in the family have a serious substance use need
              that is impacting the resources for caregiving?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          0 - <i>No evidence of any needs; no need for action.</i>
          <div className="ms-2">
            No evidence of caregiver substance use issues.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          1 -{" "}
          <i>
            Identified need that requires monitoring, watchful waiting, or
            preventive action based on history, suspicion or disagreement.
          </i>
          <div className="ms-2">
            There is a history of, suspicion or mild use of substances and/or
            caregiver is in recovery from substance use difficulties where there
            is no interference in their ability to parent.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          2 -{" "}
          <i>
            Need is interfering with the provision of care; action is required
            to ensure that the identified need is addressed.
          </i>
          <div className="ms-2">
            Caregiver has some substance abuse difficulties that interfere with
            their capacity to parent.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          3 -{" "}
          <i>
            Need prevents the provision of care; requires immediate and/or
            intensive action.
          </i>
          <div className="ms-2">
            Caregiver has substance abuse difficulties that make it impossible
            to parent the child at this time.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="pt-1 fw-bold bg-secondary text-center">
            *A rating of ‘1,’ ‘2,’ or ‘3’ on this item triggers the completion
            of the [B] Substance Use Disorder Module.
          </div>
        </div>
      </div>
    </>
  ),
  CaregiverTraumaticReaction: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">POSTTRAUMATIC REACTIONS*</div>
          This item describes posttraumatic reactions faced by caregiver(s),
          including emotional numbing and avoidance,nightmares and flashbacks,
          that are related to their child’s or their own traumatic experiences.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>Has the caregiver experienced a traumatic event?</li>
            <li>Does the caregiver experience frequent nightmares?</li>
            <li>Are they troubled by flashbacks?</li>
            <li>What are the caregiver’s current coping skills?</li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          0 - <i>No evidence of any needs; no need for action. </i>
          <div className="ms-2">
            Caregiver has not experienced any significant trauma or has adjusted
            to traumatic experiences without notable posttraumatic stress
            reactions.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          1 -{" "}
          <i>
            Identified need that requires monitoring, watchful waiting, or
            preventive action based on history, suspicion or disagreement.
          </i>
          <div className="ms-2">
            Caregiver has some adjustment problems related to their child’s or
            their own traumatic experiences. Caregiver may exhibit some guilt
            about their child’s trauma or become somewhat detached or estranged
            from others. These symptoms may mildly impact their ability to
            provide child care.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          2 -{" "}
          <i>
            Need is interfering with the provision of care; action is required
            to ensure that the identified need is addressed.
          </i>
          <div className="ms-2">
            Caregiver has adjustment difficulties related to traumatic
            experiences, and these difficulties impact their ability to provide
            child care. Caregiver may have nightmares or flashbacks of the
            trauma.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          3 -{" "}
          <i>
            Need prevents the provision of care; requires immediate and/or
            intensive action.
          </i>
          <div className="ms-2">
            Caregiver has significant adjustment difficulties associated with
            traumatic experiences, and these difficulties make the caregiver
            unable to provide child care. Symptoms might include intrusive
            thoughts, hypervigilance, and constant anxiety.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="pt-1 fw-bold bg-secondary text-center">
            *A rating of ‘1,’ ‘2,’ or ‘3’ on this item triggers the completion
            of the [C] Trauma Module.
          </div>
          <br />
          <b>Supplemental Information:</b> This is one item where speculation
          about why a person is displaying a certain behavior is considered.
          There should be an inferred link between the trauma and behavior.
        </div>
      </div>
    </>
  ),
  CaregiverPsychosis: (
    <>
      <div className="row border">
        <div className="col">
          <div className="fw-bold">MH1. PSYCHOSIS (THOUGHT DISORDER)</div>
          This item rates the symptoms of psychiatric disorders with a known
          neurological base, including schizophrenia spectrum and other
          psychotic disorders.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>Does the individual display any signs of psychosis?</li>
            <li>Does the individual have any hallucinations?</li>
            <li>Is the individual diagnosed with a psychotic disorder?</li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            0 - No evidence of psychotic symptoms. Both thought processes and
            content are within normal range.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            1 - Evidence of disruption in thought processes or content.
            Individual may be somewhat tangential in speech or evidence somewhat
            illogical thinking (age-inappropriate). This also includes an
            individual with a history of hallucinations but none currently. Use
            this category for an individual who is below the threshold for one
            of the DSM diagnoses listed above.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            2 - Evidence of disturbance in thought process or content that may
            be impairing the individual’s functioning in at least one life
            domain. Individual may be somewhat delusional or have brief
            intermittent hallucinations. Speech may be at times quite tangential
            or illogical.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            3 - Clear evidence of dangerous hallucinations, delusions, or
            bizarre behavior that might be associated with some form of
            psychotic disorder that places the individual or others at risk of
            physical harm.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <b>Supplemental Information:</b> The common symptoms of these
          disorders include hallucinations (i.e. experiencing things others do
          not experience), delusions (i.e. a false belief or an incorrect
          inference about reality that is firmly sustained despite the fact that
          nearly everybody thinks the belief is false or proof exists of its
          inaccuracy), disorganized thinking, and bizarre/idiosyncratic
          behavior.
        </div>
      </div>
    </>
  ),
  CaregiverMoodDisturbance: (
    <>
      <div className="row border">
        <div className="col">
          <div className="fw-bold">MH2. MOOD DISTURBANCE</div>
          This item captures problems related to mood, including symptoms of
          depressed mood, hypomania, or mania.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Does the individual have periods of feeling super happy/excited
              for hours or days at a time? Periods of feeling very angry/cranky
              for hours or days at a time?
            </li>
            <li>
              Does the individual have periods of time where they feel they
              don’t need to sleep or eat?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            0 - Individual with no prolonged emotional/mood problems. No
            evidence of depression, hypomania, or mania.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            1 - Individual with prolonged emotional/mood problems. Evidence of
            depression, irritability, or other issues of mood, including mood
            swings with some evidence of hypomania. These problems are not yet
            impacting the individual’s functioning.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            2 - Individual with mood disturbance problems that interfere with
            their functioning. This would include episodes of mania, depression,
            social withdrawal, school avoidance, or inability to experience
            happiness.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            3 - Individual with mood disturbance problems that are dangerous or
            disabling. This would include an individual whose emotional symptoms
            prevent appropriate participation in school, friendship groups, or
            family life.
          </div>
        </div>
      </div>
    </>
  ),
  CaregiverDepression: (
    <>
      <div className="row border">
        <div className="col">
          <div className="fw-bold">MH3. DEPRESSION</div>
          This item rates symptoms such as irritable or depressed mood, social
          withdrawal, sleep disturbances, weight/eating disturbances, and loss
          of motivation, interest or pleasure in daily activities.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>Does the individual display any symptoms of depression?</li>
            <li>Does the individual have a diagnosis of depression?</li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            0 - No evidence of problems with depression.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            1 - History or suspicion of depression or evidence of depression
            associated with a recent negative life event with minimal impact on
            life domain functioning. Brief duration of depression, irritability,
            or impairment of peer, family, or academic functioning that does not
            lead to pervasive avoidance behavior.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            2 - Clear evidence of depression associated with either depressed
            mood or significant irritability. Depression has interfered
            significantly in individual’s ability to function in at least one
            life domain.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            3 - Clear evidence of disabling level of depression that makes it
            virtually impossible for the individual to function in any life
            domain. This rating is given to an individual with a severe level of
            depression. This would include an individual who stays at home or in
            bed all day due to depression or one whose emotional symptoms
            prevent any participation in school, friendship groups, or family
            life. Disabling forms of depressive diagnoses would be rated here.
          </div>
        </div>
      </div>
    </>
  ),
  CaregiverImpulseControl: (
    <>
      <div className="row border">
        <div className="col">
          <div className="fw-bold">MH4. IMPULSE CONTROL</div>
          Problems with impulse control and impulsive behaviors, including
          motoric disruptions, are rated here.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>Does the individual act impulsively?</li>
            <li>Does the individual display hyperactive behaviors?</li>
            <li>Does the individual have a diagnosis of ADHD?</li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            0 - No evidence of symptoms of loss of control of behavior.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            1 - There is a history or evidence of mild levels of impulsivity
            evident in action or thought that place the individual at risk of
            future functioning difficulties. The individual may exhibit limited
            impulse control, e.g., individual may yell out answers to questions
            or may have difficulty waiting one’s turn. Some motor difficulties
            may be present as well, such as pushing or shoving others.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            2 - Clear evidence of problems with impulsive, distractible, or
            hyperactive behavior that interferes with the individual’s
            functioning in at least one life domain. An individual who often
            intrudes on others and often exhibits aggressive impulses would be
            rated here.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            3 - Clear evidence of a dangerous level of hyperactivity and/or
            impulsive behavior that places the individual at risk of physical
            harm. This indicates an individual with frequent and significant
            levels of impulsive behavior that carries considerable safety risk
            (e.g., running into the street, dangerous driving or bike riding).
            The individual may be impulsive on a nearly continuous basis. The
            individual endangers self or others without thinking.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <b>Supplemental Information:</b> This item includes behavioral
          symptoms associated with Attention-Deficit Hyperactivity Disorder
          (ADHD), Impulse-Control Disorders and mania as indicated in the DSM-5.
          Individuals with impulse problems tend to engage in behavior without
          thinking, regardless of the consequences. This can include compulsions
          to engage in gambling, violent behavior (e.g., road rage), and sexual
          behavior, fire-starting or stealing. Manic behavior is also rated
          here.
        </div>
      </div>
    </>
  ),
  CaregiverAnxiety: (
    <>
      <div className="row border">
        <div className="col">
          <div className="fw-bold">MH5. ANXIETY</div>
          This item rates symptoms associated with DSM-5 anxiety disorders
          characterized by excessive fear and anxiety and related behavioral
          disturbances (including avoidance behaviors).
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>Does the individual worry?</li>
            <li>Does the individual have excessive fear?</li>
            <li>Does the individual avoid situations?</li>
            <li>Is the individual diagnosed with an anxiety disorder?</li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">0 - No evidence of anxiety symptoms.</div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            1 - There is a history, suspicion, or evidence of mild anxiety
            associated with a recent negative life event. This level is used to
            rate either a mild phobia or anxiety problem that is not yet causing
            the individual significant distress or markedly impairing
            functioning in any important context.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            2 - Clear evidence of anxiety associated with either anxious mood or
            significant fearfulness. Anxiety has interfered in the individual’s
            ability to function in at least one life domain.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            3 - Clear evidence of debilitating level of anxiety that makes it
            virtually impossible for the individual to function in any life
            domain.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <b>Supplemental Information:</b> Panic attacks can be a prominent type
          of fear response.
        </div>
      </div>
    </>
  ),
  CaregiverProblems: (
    <>
      <div className="row border">
        <div className="col">
          <div className="fw-bold">MH6. INTERPERSONAL PROBLEMS</div>
          This item identifies problems with relating to other people including
          significant manipulative behavior, social isolation, or significant
          conflictual relationships. The presence of any DSM personality
          disorder may be rated here.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>Does the individual have any manipulative behaviors?</li>
            <li>Does the individual socially isolate themselves?</li>
            <li>Is the individual diagnosed with personality disorders?</li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            0 - No evidence of notable interpersonal problems identified.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            1 - History or evidence of some interpersonal problems; behavior is
            probably sub-threshold for the diagnosis of personality disorder.
            Mild but consistent antisocial or narcissistic behavior is rated
            here.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            2 - Individual’s relationship problems are beginning to interfere
            with their life functioning and may warrant a DSM personality
            disorder diagnosis.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            3 - Individual’s interpersonal problems have a significant impact on
            the individual’s long-term functioning. Interpersonal problems are
            disabling and block the individual’s ability to function
            independently.
          </div>
        </div>
      </div>
    </>
  ),
  CaregiverAntisocial: (
    <>
      <div className="row border">
        <div className="col">
          <div className="fw-bold">MH7. ANTISOCIAL BEHAVIOR</div>
          This item rates the degree to which an individual engages in behavior
          that is consistent with the presence of an Antisocial Personality
          Disorder.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>Does the individual vandalize?</li>
            <li>Does the individual steal?</li>
            <li>Is the individual violent?</li>
            <li>
              Is the individual diagnosed with an antisocial behaviordisorder?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            0 - Individual shows no evidence of antisocial behavior.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            1 - There is a history, suspicion or evidence of some problems
            associated with antisocial behavior including but not limited to
            lying, stealing, manipulation of others, acts of sexual aggression,
            or violence towards people, property or animals. The individual may
            have some difficulties in school and home behavior. Problems are
            recognizable but not notably deviant for age, sex and community.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            2 - Clear evidence of antisocial behavior including but not limited
            to lying, stealing, manipulating others, sexual aggression, violence
            towards people, property, or animals. An individual rated at this
            level will likely meet criteria for a diagnosis of Antisocial
            Personality Disorder.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            3 - Evidence of a severe level of aggressive or antisocial behavior,
            as described above, that places the individual or community at
            significant risk of physical harm due to these behaviors. This could
            include frequent episodes of unprovoked, planned aggressive or other
            antisocial behavior.
          </div>
        </div>
      </div>
    </>
  ),
  CaregiverAnger: (
    <>
      <div className="row border">
        <div className="col">
          <div className="fw-bold">MH8. ANGER CONTROL</div>
          This item captures the individual’s ability to identify and manage
          their anger when frustrated.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>How does the individual manage feelings of anger?</li>
            <li>Does the individual get violent when angry?</li>
            <li>
              Does the individualhave a hard time dealing withcriticism
              orrejection?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            0 - No evidence of any anger control problems.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            1 - History, suspicion of, or evidence of some problems with
            controlling anger. Individual may sometimes become verbally
            aggressive when frustrated. Peers and family are aware of and may
            attempt to avoid stimulating angry outbursts.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            2 - Individual’s difficulties with controlling anger are impacting
            functioning in at least one life domain. Individual’s temper has
            resulted in significant trouble with peers, family and/or school.
            Anger may be associated with physical violence. Others are likely
            quite aware of anger potential.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            3 - Individual’s temper or anger control problem is dangerous.
            Individual frequently gets into fights that are often physical.
            Others likely fear the individual.
          </div>
        </div>
      </div>
    </>
  ),
  CaregiverEatingDisturbance: (
    <>
      <div className="row border">
        <div className="col">
          <div className="fw-bold">MH9. EATING DISTURBANCES</div>
          This item includes problems with eating including disturbances in body
          image, refusal to maintain normal body weight and recurrent episodes
          of binge eating. These ratings are consistent with DSM Eating
          Disorders.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>Does the individual haveany issues with eating?</li>
            <li>Is the individual overlypicky?</li>
            <li>Does the individual haveany eating rituals?</li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            0 - This rating is for an individual with no evidence of eating
            disturbances.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            1 - Some evidence of eating disturbance is present. This could
            include some preoccupation with weight, calorie intake, or body size
            or type when of normal weight or below weight. This could also
            include some binge eating patterns.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            2 - Evidence of eating disturbance that is impacting the
            individual’s functioning. This could include a more intense
            preoccupation with weight gain or becoming fat when underweight,
            restrictive eating habits or excessive exercising in order to
            maintain below normal weight, and/or emaciated body appearance. This
            level could also include more notable binge eating episodes that are
            followed by compensatory behaviors in order to prevent weight gain
            (e.g., vomiting, use of laxatives, excessive exercising). This
            individual may meet criteria for a DSM Eating Disorder (Anorexia or
            Bulimia Nervosa).
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            3 - Dangerous level of eating disturbance. This could include
            significantly low weight where hospitalization is required or
            excessive binge-purge behaviors (at least once per day).
          </div>
        </div>
      </div>
    </>
  ),
  CaregiverSUSeverity: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">SUD1. SEVERITY OF USE</div>
          This item rates the frequency and severity of the individual’s current
          substance use.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Is the individual currently using substances? If so, how
              frequently?
            </li>
            <li>Is there evidence of physical dependence on substances?</li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            0 - Individual is currently abstinent and has maintained abstinence
            for at least six months.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            1 - Individual is currently abstinent but only in the past 30 days
            or individual has been abstinent for more than 30 days but is living
            in an environment that makes substance use difficult.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            2 - Individual actively uses alcohol or drugs but not daily.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            3 - Individual uses alcohol and/or drugs on a daily basis.
          </div>
        </div>
      </div>
    </>
  ),
  CaregiverSUDuration: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">SUD2. DURATION OF USE</div>
          This item identifies the length of time that the individual has been
          using drugs or alcohol.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              How long has the individual been using drugs and/or alcohol?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            0 - Individual has begun use in the past year.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            1 - Individual has been using alcohol or drugs for at least one year
            but has had periods of at east 30 days where the individual did not
            have any use.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            2 - Individual has been using alcohol or drugs for at least one year
            (but less than five years), but not daily.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            3 - Individual has been using alcohol or drugs daily for more than
            the past year or intermittently for at least five years.
          </div>
        </div>
      </div>
    </>
  ),
  CaregiverRecoveryStage: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">SUD3. STAGE OF RECOVERY</div>
          This item identifies where the individual is in their recovery
          process.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              In relation to stopping substance use, at what stage of change is
              the individual?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            0 - Individual is in maintenance stage of recovery. Individual is
            abstinent and able to recognize and avoid risk factors for future
            alcohol or drug use.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            1 - Individual is actively trying to use treatment to remain
            abstinent.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            2 - Individual is in contemplation phase, recognizing a problem but
            not willing to take steps for recovery.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            3 - Individual is in denial regarding the existence of any substance
            use problem.
          </div>
        </div>
      </div>
    </>
  ),
  CaregiverPeerInfluences: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">SUD4. PEER INFLUENCES</div>
          This item rates the impact that the individual’s social group has on
          their alcohol and drug use.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              What role do the individual’s peers play in their alcohol and drug
              use?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            0 - Individual’s primary peer social network does not engage in
            alcohol and/or drug use.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            1 - Individual has peers in their primary peer network who do not
            engage in alcohol and/or drug use, but has some peers who do.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            2 - Individual predominantly has peers who engage in alcohol and/or
            drug use.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            3 - Individual is a member of a peer group that consistently engages
            in alcohol and/or drug use.
          </div>
        </div>
      </div>
    </>
  ),
  CaregiverEnvInfluences: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">SUD5. ENVIRONMENTAL INFLUENCES</div>
          This item rates the impact of the individual’s community environment
          on their alcohol and drug use.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Are there factors in the individual’s community that impacts their
              alcohol and drug use?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            0 - No evidence that the individual's environment stimulates or
            exposes them to any alcohol or drug use.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            1 - Suspicion that individual's environment might expose them to
            alcohol or drug use.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            2 - Individual's environment clearly exposes them to alcohol or drug
            use.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            3 - Individual's environment encourages or enables them to engage in
            alcohol or drug use.
          </div>
        </div>
      </div>
    </>
  ),
  CaregiverRecoverySupport: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">SUD6. RECOVERY SUPPORT IN COMMUNITY</div>
          This item describes the individual’s participation in recovery
          programs such as AA, NA, or other types of recovery groups or
          activities that are community-based.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Does the individual attend or participate in recovery groups or
              activities?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            0 - No problems with maintaining social connectivity through
            recovery support groups or activities. Individual attends recovery
            support groups and meetings regularly.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            1 - Problems with maintaining social connectivity through recovery
            support groups or activities. Individual may attend meetings
            irregularly.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            2 - Individual struggles with maintaining social connectivity
            through recovery support groups or activities. Individual has
            attended recovery support groups in the past but is no longer
            attending meetings.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            3 - Individual is unable to maintain social connectivity through
            recovery support groups or activities. Individual has never
            participated in recovery support groups or activities.
          </div>
        </div>
      </div>
    </>
  ),
  CaregiverAcuteIntoxication: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">SUD7. ACUTE INTOXICATION</div>
          This item describes reversible, substance-related, maladaptive
          psychological or behavioral changes causing physiological effects of
          the central nervous system by recent ingestion of or exposure to a
          substance: alcohol, illicit drug, medication, or toxin (Medical
          Dictionary.com).
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Is there evidence of acute intoxication (e.g., withdrawal
              symptoms)?
            </li>
            <li>
              Are substance intoxication difficulties interfering with
              functioning?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            0 - Individual has no identified substance intoxication difficulties
            at the present time.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            1 - Individual has occasional intoxication which requires preventive
            activities. History of occasional intoxication and/or withdrawal
            symptoms without evidence of current problems would be rated here.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            2 - Evidence of acute intoxication interferes with individual’s
            ability to function with moderate risks, requiring preventive or
            withdrawal management services.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            3 - Individual has a substance use problem with complications that
            may result in danger to self or detoxification (e.g., managing acute
            alcohol poisoning after binge drinking, overdose, or significant
            risk of withdrawal symptoms, seizures, or medical complications
            based on withdrawal history and substance use: amount, frequency,
            duration, and recent discontinuation).
          </div>
        </div>
      </div>
    </>
  ),
  CaregiverWithdrawalHistory: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">SUD8. WITHDRAWAL HISTORY</div>
          Withdrawal refers to a psychological and/or physical syndrome caused
          by abruptly stopping or reducing substance use in a habituated person.
          Specific symptoms and risks differ based on the substance. Withdrawal
          history, important in assessing current risk and planning care,
          considers past substance use and withdrawal experience.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Is there evidence of withdrawal symptoms related to substance use?
            </li>
            <li>
              Do they impact functioning or affect the individual’s health?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            0 - No evidence of prior withdrawal symptoms related to substance
            use, medications, or toxins.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            1 - History of occasional acute withdrawal symptoms following
            substance use (e.g., mild nausea, mild tactile disturbances or
            sensitivity to light, slight headache, cannot do serial additions or
            uncertain about date, mild anxiety or irritability, chills or
            flushing, restless).
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            2 - History of withdrawal symptoms after decreasing or discontinuing
            substance use or medications (e.g., anxiety, nausea, fever, tremor)
            that impact the individual’s functioning. OR, chronic physical
            health problems could be worsened by withdrawal symptoms.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            3 - History of significant withdrawal symptoms after decreasing or
            discontinuing substance use or medications (e.g., seizures, delirium
            tremens, rapid heartbeat). Individual may have medical condition
            which could be worsened due to withdrawal.
          </div>
        </div>
      </div>
    </>
  ),
  CaregiverWithdrawalRisks: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">SUD9. WITHDRAWAL RISKS</div>
          This item describes the current risk of withdrawal from alcohol and/or
          other substance use and need for withdrawal management services.
          Severity of withdrawal risk varies by type of substance(s) used,
          duration and frequency of use, withdrawal history, concurrent mental
          and/or physical health conditions, involvement in recovery, and
          family/natural and environmental supports. Higher risks occur with
          withdrawal from alcohol and benzodiazepines or the use of multiple
          substances.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>How does the individual manage withdrawal symptoms?</li>
            <li>
              Is the individual’s health or safety at risk from the withdrawal
              symptoms?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            0 - The individual is fully functioning. Individual is able to
            tolerate and deal with mild withdrawal discomfort.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            1 - Individual has minimal risk of severe withdrawal. Sustained
            withdrawal management services without evidence of current problems
            could be rated here. Examples include an individual using alcohol or
            benzodiazepines with mild withdrawal symptoms (anxiety, sweating,
            and insomnia, but no tremors); not withdrawing from another
            substance; previously stopped using in the past year without severe
            withdrawal symptoms; no more than mild, stable physical health
            conditions; motivated to complete the withdrawal process;
            understands and willing to engage in treatment, and has a positive
            support system with safe housing.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            2 - Evidence of moderate level of withdrawal risks includes symptoms
            (sweating, anxiety, nausea, fever, and tremor), current physical
            symptoms (nausea or vomiting at no more than moderate intensity); no
            withdrawal from other substances; no more than mild, stable mental
            or physical health conditions; understanding, commitment, and
            cooperation in withdrawal management process; and at least minimally
            supportive family/friends and access to safe housing OR withdrawal
            symptoms with no tremor, but barrier to effective withdrawal
            management related to history of severe withdrawal symptoms,
            moderate or unstable mental or physical health condition(s), limited
            commitment, high relapse risk, or unsupportive friends/family.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            3 - Individual has significant or severe risk of withdrawal
            symptoms, seizures, or medical complications. Significant withdrawal
            risk is characterized by significant anxiety with moderate to severe
            tremor; possible concurrent withdrawal from other substances; OR
            moderate symptoms and not withdrawing from another substance, but
            with other problems that complicate withdrawal management (history
            of severe withdrawal symptoms, moderate to severe physical or mental
            health conditions, high relapse risk, questionable cooperation,
            significant others not supportive of the process or inadequate
            housing). Severe risk of withdrawal is characterized by confusion;
            new onset of hallucinations; seizure; or inability to understand OR
            severe anxiety; moderate to severe tremor; concurrent withdrawal
            from another substance; and either history of seizure or delirium
            tremens; severe, unstable physical health condition(s);
            uncooperative; or requiring more than hourly medical monitoring.
          </div>
        </div>
      </div>
    </>
  ),
  CaregiverTriggerAwareness: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">SUD10. AWARENESS OF RELAPSE TRIGGERS</div>
          Relapse refers to resuming substance use after a period of recovery.
          This item refers to the individual’s awareness of potential triggers
          (emotional stresses or circumstances: exposure to rewarding substances
          and behaviors, environmental cues for use) that increase the
          likelihood of using substances.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>Is the individual aware of what triggers their relapses?</li>
            <li>
              If so, does the individual use strategies to manage challenges?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            0 - Individual is aware of potential relapse triggers and actively
            uses recovery strategies (e.g., developed resilience and support to
            cope with stressors and manage challenges: craving, behavioral
            control, problems in relationships).
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            1 - Individual is aware of relapse triggers and usually engages
            recovery strategies to address recovery challenges, but requires
            some effort to maximize and sustain efforts. Awareness might be used
            and built upon in treatment.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            2 - Individual is aware of some, but not all, relapse triggers or
            seldom uses recovery strategies to address challenges.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            3 - Individual is unaware of relapse triggers and does not use
            recovery strategies to address challenges.
          </div>
        </div>
      </div>
    </>
  ),
  CaregiverSexualAbuse: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">ACE1. SEXUAL ABUSE</div>
          This item describes whether or not the individual has experienced
          sexual abuse.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>Has the individual disclosed sexual abuse?</li>
            <li>
              Is there suspicion or evidence that the individual has been
              sexually abused?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            No - There is no evidence that the individual has experienced sexual
            abuse.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            Yes - Individual has experienced sexual abuse, or there is a
            suspicion that they have experienced sexual abuse – including single
            or multiple episodes, or chronic over an extended period of time.
            The abuse may have involved penetration, multiple perpetrators,
            and/or associated physical injury. Individual with exposure to
            secondary sexual abuse (e.g., witnessing sexual abuse, having a
            sibling sexually abused) should be rated here.
          </div>
        </div>
      </div>
    </>
  ),
  CaregiverPhysicalAbuse: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">ACE2. PHYSICAL ABUSE</div>
          This item describes whether or not the individual has experienced
          physical abuse.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>Is physical discipline used in the home? What forms?</li>
            <li>
              Has the individual ever received bruises, marks, or injury from
              discipline?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            No - There is no evidence that the individual has experienced
            physical abuse.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            Yes - Individual has experienced or there is a suspicion that they
            experienced physical abuse – mild to severe, or repeated physical
            abuse with sufficient physical harm requiring medical treatment.
          </div>
        </div>
      </div>
    </>
  ),
  CaregiverNeglect: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">ACE3. NEGLECT</div>
          This item describes whether or not the individual has experienced
          neglect. Neglect can refer to a lack of food, shelter or supervision
          (physical neglect), lack of access to needed medical care (medical
          neglect), or failure to receive academic instruction (educational
          neglect).
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>Is the individual receiving adequate supervision?</li>
            <li>
              Has the individual been denied their needs for food and shelter?
            </li>
            <li>
              Is the individual allowed access to necessary medical care?
              Education?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            No - There is no evidence that the individual has experienced
            neglect.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            Yes - Individual has experienced neglect, or there is a suspicion
            that they experienced neglect. This includes occasional neglect
            (e.g., individual left home alone for a short period of time when
            developmentally inappropriate and with no adult supervision, or
            occasional failure to provide adequate supervision of the
            individual); multiple and/or prolonged absences of adults, with
            minimal supervision; or failure to provide basic necessities of life
            (adequate food, shelter, or clothing) on a regular basis.
          </div>
        </div>
      </div>
    </>
  ),
  CaregiverEmotionalAbuse: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">ACE4. EMOTIONAL ABUSE</div>
          This item describes whether or not the individual has experienced
          verbal and/or nonverbal emotional abuse, including belittling,
          shaming, and humiliating an individual, calling names, making negative
          comparisons to others, or telling an individual that they are “no
          good.” This item includes both “emotional abuse,” which would include
          psychological maltreatment such as insults or humiliation, and
          “emotional neglect,” described as the denial of emotional attention
          and/or support from others.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Is the individual subject to name calling or shaming in their
              home?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            No - There is no evidence that individual has experienced emotional
            abuse.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            Yes - Individual has experienced emotional abuse, or there is a
            suspicion that they have experienced emotional abuse (mild to
            severe, for any length of time) including: insults or occasionally
            being referred to in a derogatory manner, being denied emotional
            attention or completely ignored, or threatened/terrorized by others.
          </div>
        </div>
      </div>
    </>
  ),
  CaregiverMedicalTrauma: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">ACE5. MEDICAL TRAUMA</div>
          This item describes whether or not the individual has experienced
          medically-related trauma, resulting from, for example, inpatient
          hospitalizations, outpatient procedures, and significant injuries.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Has the individual had any broken bones, stitches or other medical
              procedures?
            </li>
            <li>
              Has the individual had to go to the emergency room, or stay
              overnight in the hospital?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            No - There is no evidence that the individual has experienced any
            medical trauma.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            Yes - Individual has had a medical experience that was perceived as
            emotionally or mentally overwhelming. This includes events that were
            acute in nature and did not result in ongoing medical needs;
            associated distress such as minor surgery, stitches or bone setting;
            acute injuries and moderately invasive medical procedures such as
            major surgery that required only short-term hospitalization; events
            that may have been life threatening and may have resulted in chronic
            health problems that alter the individual’s physical functioning. A
            suspicion that an individual has had a medical experience that was
            perceived as emotionally or mentally overwhelming should be rated
            here.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <b>Supplemental Information:</b> This item takes into account the
          impact of the event on the individual. It describes experiences in
          which the individual is subjected to medical procedures that are
          experienced as upsetting and overwhelming. An individual born with
          physical deformities who is subjected to multiple surgeries could be
          included. An individual who must experience chemotherapy or radiation
          could also be included. Individuals who experience an accident and
          require immediate medical intervention that results in on-going
          physical limitations or deformities (e.g., burn victims) could be
          included here. Common medical procedures, which are generally not
          welcome or pleasant but are also not emotionally or psychologically
          overwhelming for individuals (e.g., shots, pills) would generally not
          be rated here.
        </div>
      </div>
    </>
  ),
  CaregiverDisasterTrauma: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">ACE6. NATURAL OR MANMADE DISASTER</div>
          This item describes the individual’s exposure to either natural or
          manmade disasters.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Has the individual been present during a natural or manmade
              disaster?
            </li>
            <li>
              Does the individual watch television shows containing these
              themes?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            No - There is no evidence that the individual has experienced, been
            exposed to or witnessed natural or manmade disasters.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            Yes - Individual has experienced, been exposed to or witnessed
            natural or manmade disasters either directly or second-hand (e.g.,
            on television, hearing others discuss disasters). This includes
            disasters such as a fire or earthquake or manmade disaster; car
            accident, plane crashes, or bombings; observing a caregiver who has
            been injured in a car accident or fire or watching a neighbor’s
            house burn down; a disaster that caused significant harm or death to
            a loved one; or there is an ongoing impact or life disruption due to
            the disaster (e.g. caregiver loses job). A suspicion that the
            individual has experienced, been exposed to or witnessed natural or
            manmade disasters either directly or second-hand would be rated
            here.
          </div>
        </div>
      </div>
    </>
  ),
  CaregiverFamilyViolence: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">ACE7. WITNESS TO FAMILY VIOLENCE</div>
          This item describes exposure to violence within the individual’s home
          or family.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>Is there frequent fighting in the individual’s family?</li>
            <li>Does the fighting ever become physical?</li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            No - There is no evidence the individual has witnessed family
            violence.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            Yes - Individual has witnessed, or there is a suspicion that they
            witnessed family violence –single, repeated, or severe episodes.
            This includes episodes of family violence but no significant
            injuries (i.e. requiring emergency medical attention) and episodes
            in which significant injuries have occurred as a direct result of
            the violence.
          </div>
        </div>
      </div>
    </>
  ),
  CaregiverSchoolViolence: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">
            ACE8. WITNESS TO COMMUNITY/SCHOOL VIOLENCE
          </div>
          This item describes the exposure to incidents of violence the
          individual has witnessed or experienced in their community. This
          includes witnessing violence at the individual’s school, educational
          or work setting.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Does the individual live in a neighborhood with frequent violence?
            </li>
            <li>
              Has the individual witnessed or directly experienced violence at
              their school or work?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            No - There is no evidence that the individual has witnessed violence
            in their community or school or work setting.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            Yes - Individual has witnessed or experienced violence in their
            community, school or work etting, such as: fighting; friends/family
            injuries as a result of violence; severe and repeated instances of
            violence and/or the death of another person in their
            community/school/work as a result of violence; is the direct victim
            of violence/criminal activity in the community/school/work that was
            life threatening; or has experienced chronic/ongoing impact as a
            result of community/school/work violence (e.g., family member
            injured and no longer able to work). A suspicion that the individual
            has witnessed or experienced violence in the community would be
            rated here.
          </div>
        </div>
      </div>
    </>
  ),
  CaregiverWarTrauma: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">ACE10. WAR/TERRORISM AFFECTED</div>
          This item describes the individual’s exposure to war, political
          violence, torture or terrorism.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Has the individual or their family lived in a war-torn region?
            </li>
            <li>
              How close were they to war or political violence, torture or
              terrorism?
            </li>
            <li>Was the individual displaced?</li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            No - No evidence that the individual has been exposed to war,
            political violence, torture or terrorism.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            Yes - Individual has experienced, or there is suspicion that they
            have experienced or been affected by war, terrorism or political
            violence. Examples include: Family members directly related to the
            individual may have been exposed to war, political violence, or
            torture resulting in displacement, injury or disability, or death;
            parents may have been physically or psychologically disabled from
            the war and are unable to adequately care for the individual;
            individual may have spent an extended amount of time in a refugee
            camp, or feared for their own life during war or terrorism due to
            bombings or shelling very near to them; individual may have been
            directly injured, tortured, or kidnapped in a terrorist attack;
            individual may have served as a soldier, guerrilla, or other
            combatant in their home country. Also included is an individual who
            did not live in war or terrorism-affected region or refugee camp,
            but whose family was affected by war.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <b>Supplemental Information:</b> Terrorism is defined as “the
          calculated use of violence or the threat of violence to inculcate
          fear, intended to coerce or to intimidate governments or societies in
          the pursuit of goals that are generally political, religious or
          ideological.” Terrorism includes attacks by individuals acting in
          isolation (e.g. sniper attacks).
        </div>
      </div>
    </>
  ),
  CaregiverCriminalActsTrauma: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">
            ACE9. WITNESS/VICTIM TO CRIMINAL ACTIVITY
          </div>
          This item describes the individual’s exposure to criminal activity.
          Criminal behavior includes any behavior for which an adult could go to
          prison including drug dealing, prostitution, assault, or battery.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Has the individual or someone in their family ever been the victim
              of a crime?
            </li>
            <li>
              Has the individual seen criminal activity in the community or
              home?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            No - There is no evidence that the individual has been victim of or
            a witness to criminal activity.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            Yes - Individual has been victimized, or there is suspicion that
            they have been victimized or have witnessed criminal activity. This
            includes a single instance, multiple instances, or chronic and
            severe instances of criminal activity that was life threatening or
            caused significant physical harm, or individual has witnessed the
            death of a family friend or loved one.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <b>Supplemental Information:</b> Any behavior that could result in
          incarceration is considered criminal activity. An individual who has
          been sexually abused or witnesses a sibling being sexually abused or
          physically abused to the extent that assault charges could be filed
          would be rated here and on the appropriate abuse-specific items. An
          individual who has witnessed drug dealing, prostitution, assault or
          battery would also be rated on this item.
        </div>
      </div>
    </>
  ),
  CaregiverParentalCriminalTrauma: (
    <>
      {
        // nothing to show
      }
    </>
  ),
  CaregiverAttachmentTrauma: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">
            ACE11. DISRUPTIONS IN CAREGIVING/ATTACHMENT LOSSES
          </div>
          This item documents the extent to which an individual has had one or
          more major changes in caregivers, potentially resulting in disruptions
          in attachment.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>Has the individual ever lived apart from their caregivers?</li>
            <li>
              What happened that resulted in the individual living apart from
              their caregivers?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            No - There is no evidence that the individual has experienced
            disruptions in caregiving and/or attachment losses.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            Yes - Individual has been exposed to, or there is suspicion that
            they were exposed to, at least one disruption in caregiving with
            familiar alternative caregivers or unknown caregivers (this includes
            placement in foster or other out-of-home care such as residential
            care facilities). Individual may or may not have had ongoing contact
            with primary attachment figure(s) during this disruption. Shift in
            caregiving may have been temporary or permanent.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <b>Supplemental Information:</b> Individuals who have been exposed to
          disruptions in caregiving involving separation from primary attachment
          figure(s) and/or attachment losses would be rated here. Individuals
          who have had placement changes, including stays in foster care,
          residential treatment facilities or juvenile justice settings, can be
          rated here. Short-term hospital stays or brief juvenile detention
          stays, during which the individual's caregiver remains the same, would
          not be rated on this item.
        </div>
      </div>
    </>
  ),
  CaregiverInvolvement: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">INVOLVEMENT WITH CAREGIVING</div>
          This item refers to the degree to which the caregiver is actively
          involved in being a parent/caregiver.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              How actively involved is the caregiver in the daily life of the
              family?
            </li>
            <li>Is the caregiver an advocate for the child?</li>
            <li>Would they like any help to become more involved?</li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          0 -{" "}
          <i>
            Well-developed, centerpiece strength; may be used as a centerpiece
            in an intervention/ action plan.
          </i>
          <div className="ms-2">
            There is no evidence that the caregiver is not involved with
            caregiving functions. The caregiver is actively and fully involved
            in daily family life.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          1 -{" "}
          <i>
            Identified and useful strength. Strength will be used, maintained or
            built upon as part of the plan. May require some effort to develop
            strength into a centerpiece strength.
          </i>
          <div className="ms-2">
            The caregiver is generally involved in daily family life. The
            caregiver may occasionally be less involved for brief periods of
            time because they are distracted by internal stressors and/or other
            external events or responsibilities or there is a history of
            caregiver un-involvement.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          2 -{" "}
          <i>
            Strengths have been identified but require strength-building efforts
            before they can be effectively utilized as part of a plan.
            Identified but not useful.
          </i>
          <div className="ms-2">
            The caregiver is involved in daily family life but only maintains
            minimal daily interactions for extended periods of time.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          3 -{" "}
          <i>
            An area in which no current strength is identified; efforts may be
            recommended to develop a strength in this area.
          </i>
          <div className="ms-2">
            The caregiver is mostly uninvolved in daily family life. The
            caregiver may not interact with their children on a daily basis.
          </div>
        </div>
      </div>
    </>
  ),
  CaregiverResponsiveness: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">EMOTIONAL RESPONSIVENESS</div>
          This item refers to the caregiver’s ability to understand and respond
          appropriately to the joys, sorrows, anxieties andother feelings of
          their children.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Is the caregiver’semotional responseappropriate towards thechild?
            </li>
            <li>Is caregiver empathictowards the child?</li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          0 -{" "}
          <i>
            Well-developed, centerpiece strength; may be used as a centerpiece
            in an intervention/ action plan.
          </i>
          <div className="ms-2">
            Adaptive emotional responsiveness. Parents/caregivers are
            emotionally empathic and attend to child's emotional needs.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <i>
            1 -Identified and useful strength. Strength will be used, maintained
            or built upon as part of the plan. May require some effort to
            develop strength into a centerpiece strength.
          </i>
          <div className="ms-2">
            Parents/caregivers are generally emotionally empathic and typically
            attend to child's emotional needs.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          2 -{" "}
          <i>
            Strengths have been identified but require strength-building efforts
            before they can be effectively utilized as part of a plan.
            Identified but not useful.
          </i>
          <div className="ms-2">
            Parents/caregivers have limited adaptive emotional responsiveness.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          3 -{" "}
          <i>
            An area in which no current strength is identified; efforts may be
            recommended to develop a strength in this area.
          </i>
          <div className="ms-2">
            No evidence that parents/caregivers currently possess any capacity
            with regards to emotional responsiveness.
          </div>
        </div>
      </div>
    </>
  ),
  CaregiverOrganization: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">ORGANIZATION</div>
          This item is used to rate the caregiver’s ability to organize and
          manage their household within the context of intensive community
          services.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>Do caregivers need or want help with managing their home?</li>
            <li>Can they get to appointments or manage a schedule?</li>
            <li>Do they get their child to appointments or school on time?</li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          0 -{" "}
          <i>
            Well-developed, centerpiece strength; may be used as a centerpiece
            in an intervention/ action plan.
          </i>
          <div className="ms-2">
            Caregiver is well organized and efficient. Caregiver organizational
            skills can drive any plan.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          1 -{" "}
          <i>
            Identified and useful strength. Strength will be used, maintained or
            built upon as part of the plan. May require some effort to develop
            strength into a centerpiece strength.
          </i>
          <div className="ms-2">
            Caregiver has adequate organizational skills to ensure that
            children’s needs are routinely met.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          2 -{" "}
          <i>
            Strengths have been identified but require strength-building efforts
            before they can be effectively utilized as part of a plan.
            Identified but not useful.
          </i>
          <div className="ms-2">
            Caregiver has limited ability to stay organized.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          3 -{" "}
          <i>
            An area in which no current strength is identified; efforts may be
            recommended to develop a strength in this area.
          </i>
          <div className="ms-2">
            No evidence that caregiver is able to organize household to support
            needed services.CHILD ASSESSMENTThis section describes the needs and
            strengths of each of the children in the family. Sometimes
            circumstances exist that make it difficult to decide whether a
            family member is best rated as a caregiver or a child. In general,
            every family member under the age of 18 would be rated in the child
            section. Young adults could be rated as caregivers if their family
            roles are more congruent with that designation. All of these items
            should be rated using a developmental framework.
          </div>
        </div>
      </div>
    </>
  ),
  ToddlerEarlyEducation: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">EARLY EDUCATION</div>
          This item rates the child’s experiences in educational settings (such
          as daycare and preschool) and the child’s ability to get their needs
          met in these settings. This item also considers the presence of
          problems within these environments in terms of attendance, progress,
          support from the school staff to meet the child’s needs, and the
          child’s behavioral response to these environments. Children under 5
          who are not in any congregate learning settings (EHS, HS, Preschool,
          Pre-K) would be rated a ‘0’ here.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>What is the child’s experience in preschool/daycare?</li>
            <li>
              Does the child have difficulties with learning new skills, social
              relationships or behavior?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          0 - <i>No evidence of any needs; no need for action.</i>
          <div className="ms-2">
            No evidence of problem with functioning in current educational
            environment.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          1 -{" "}
          <i>
            Identified need that requires monitoring, watchful waiting, or
            preventive action based on history, suspicion or disagreement.
          </i>
          <div className="ms-2">
            History or evidence of problems with functioning in current daycare
            or preschool environment. Child may be enrolled in a special
            program.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          2 -{" "}
          <i>
            Action is required to ensure that the identified need is addressed;
            need is interfering with functioning.
          </i>
          <div className="ms-2">
            Child is experiencing difficulties maintaining their behavior,
            attendance, and/or progress in this setting.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          3 -{" "}
          <i>
            Need is dangerous or disabling; requires immediate and/or intensive
            action.
          </i>
          <div className="ms-2">
            Child’s problems with functioning in the daycare or preschool
            environment place them at immediate risk of being removed from
            program due to their behaviors, lack of progress, or unmet needs.
          </div>
        </div>
      </div>
    </>
  ),
  ToddlerSocialFunctioning: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">SOCIAL AND EMOTIONAL FUNCTIONING</div>
          This item rates the child’s social and relationship functioning. This
          includes age appropriate behavior and the ability to engage and
          interact with others. When rating this item, consider the child’s
          level of development.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>How does the child get along with others?</li>
            <li>
              Can an infant engage with and respond to adults? Can a toddler
              interact positively with peers?
            </li>
            <li>
              Does the child interact with others in an age-appropriate manner?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          0 - <i>No evidence of any needs; no need for action. </i>
          <div className="ms-2">
            No evidence of problems with social functioning; child has positive
            social relationships.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          1 -{" "}
          <i>
            Identified need that requires monitoring, watchful waiting, or
            preventive action based on history, suspicion or disagreement.
          </i>
          <div className="ms-2">
            Child is having some problems in social relationships. Infants may
            be slow to respond to adults, toddlers may need support to interact
            with peers and preschoolers may resist social situations.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          2 -{" "}
          <i>
            Action is required to ensure that the identified need is addressed;
            need is interfering with functioning.
          </i>
          <div className="ms-2">
            Child is having problems with their social relationships. Infants
            may be unresponsive to adults, and unaware of other infants.
            Toddlers may be aggressive and resist parallel play. Preschoolers
            may argue excessively with adults and peers and lack ability to play
            in groups even with adult support.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          3 -{" "}
          <i>
            Need is dangerous or disabling; requires immediate and/or intensive
            action.
          </i>
          <div className="ms-2">
            Child is experiencing disruptions in their social relationships.
            Infants show no ability to interact in a meaningful manner. Toddlers
            are excessively withdrawn and unable to relate to familiar adults.
            Preschoolers show no joy or sustained interaction with peers or
            adults, and/or aggression may be putting others at risk.
          </div>
        </div>
      </div>
    </>
  ),
  ToddlerDevelopmental: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">DEVELOPMENTAL/INTELLECTUAL</div>
          This item describes the child’s development as compared to standard
          developmental milestones, as well as rates the presence of any
          developmental or intellectual disabilities or delays. It includes
          Intellectual Developmental Disorder (IDD) and Autism Spectrum
          Disorders.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Does the child’s growth and development seem age appropriate?
            </li>
            <li>Has the child been screened for any developmental problems?</li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          0 - <i>No evidence of any needs; no need for action. </i>
          <div className="ms-2">
            No evidence of developmental delay and/or child has no developmental
            problems or intellectual disability.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          1 -{" "}
          <i>
            Identified need that requires monitoring, watchful waiting, or
            preventive action based on history, suspicion or disagreement.
          </i>
          <div className="ms-2">
            There are concerns about possible developmental delay. Child may
            have low IQ, a documented delay, or documented borderline
            intellectual disability (i.e. FSIQ 70-85). Mild deficits in adaptive
            functioning or development are indicated.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          2 -{" "}
          <i>
            Action is required to ensure that the identified need is addressed;
            need is interfering with functioning.
          </i>
          <div className="ms-2">
            Child has mild developmental delays (e.g., deficits in social
            functioning, inflexibility of behavior causing functional problems
            in one or more settings) and/or mild to moderate Intellectual
            Disability/Intellectual Disability Disorder. (If available, FSIQ
            55-69.) IDD impacts communication, social functioning, daily living
            skills, judgment, and/or risk of manipulation by others.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          3 -{" "}
          <i>
            Need is dangerous or disabling; requires immediate and/or intensive
            action.
          </i>
          <div className="ms-2">
            Child has severe to profound intellectual disability (FSIQ, if
            available, less than 55) and/or Autism Spectrum Disorder with marked
            to profound deficits in adaptive functioning in one or more areas:
            communication, social functioning and self-care across multiple
            environments.
          </div>
        </div>
      </div>
    </>
  ),
  ToddlerMedical: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">MEDICAL/PHYSICAL</div>
          This item describes both health problems and chronic/acute physical
          conditions or impediments.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>Is the child generally healthy?</li>
            <li>Does the child have any medical problems?</li>
            <li>
              How much does the health or medical issue interfere with the
              child’s life?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          0 - <i>No evidence of any needs; no need for action.</i>
          <div className="ms-2">
            No evidence that the child has any medical or physical problems,
            and/or they are healthy.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          1 -{" "}
          <i>
            Identified need that requires monitoring, watchful waiting, or
            preventive action based on history, suspicion or disagreement.
          </i>
          <div className="ms-2">
            Child has mild, transient or well-managed physical or medical
            problems. These include well-managed chronic conditions like
            juvenile diabetes or asthma.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          2 -{" "}
          <i>
            Action is required to ensure that the identified need is addressed;
            need is interfering with functioning.
          </i>
          <div className="ms-2">
            Child has serious medical or physical problems that require medical
            treatment or intervention. Or child has a chronic illness or a
            physical challenge that requires ongoingmedical intervention.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          3 -{" "}
          <i>
            Need is dangerous or disabling; requires immediate and/or intensive
            action.
          </i>
          <div className="ms-2">
            Child has life-threatening illness or medical/physical condition.
            Immediate and/or intense action should be taken due to imminent
            danger to child’s safety, health, and/or development.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <b>Supplemental Information:</b> Most transient, treatable conditions
          would be rated as a ‘1’. Most chronic conditions (e.g., diabetes,
          severe asthma, HIV) would be rated a ‘2’. The rating ‘3’ is reserved
          for life threatening medical conditions.
        </div>
      </div>
    </>
  ),
  ToddlerFamilyStrengths: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">FAMILY STRENGTHS</div>
          This item refers to the presence of a sense of family identity as well
          as love and communication among family members. Even families who are
          struggling often have a firm foundation that consists of a positive
          sense of family and strong underlying love and commitment to each
          other. These are the constructs this strength is intended to identify.
          As with Family Functioning, the definition of family comes from the
          child’s perspective (i.e., who the child describes as their family).
          If this information is not known, then we recommend a definition of
          family that includes biological/adoptive relatives and their
          significant others with whom the child is still in contact.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Does the child have good relationships with any family member?
            </li>
            <li>
              Is there potential to develop positive family relationships?
            </li>
            <li>
              Is there a family member that the child can go to in time of need
              for support? That can advocate for the child?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          0 -{" "}
          <i>
            Well-developed, centerpiece strength; may be used as a centerpiece
            in an intervention/ action plan.
          </i>
          <div className="ms-2">
            Family has strong relationships and significant family strengths.
            This level indicates a family with much love and respect for one
            another. There is at least one family member who has a strong loving
            relationship with the child and is able to provide significant
            emotional or concrete support. Child is fully included in family
            activities.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          1 -{" "}
          <i>
            Identified and useful strength. Strength will be used, maintained or
            built upon as part of the plan. May require some effort to develop
            strength into a centerpiece strength.
          </i>
          <div className="ms-2">
            Family has some good relationships and good communication. Family
            members are able to enjoy each other’s company. There is at least
            one family member who has a strong, loving relationship with the
            child and is able to provide limited emotional or concrete support.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          2 -{" "}
          <i>
            Strengths have been identified but require strength-building efforts
            before they can be effectively utilized as part of a plan.
            Identified but not useful.
          </i>
          <div className="ms-2">
            Family needs some assistance in developing relationships and/or
            communications. Family members are known, but currently none are
            able to provide emotional or concrete support.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          3 -{" "}
          <i>
            An area in which no current strength is identified; efforts may be
            recommended to develop a strength in this area.
          </i>
          <div className="ms-2">
            Family needs significant assistance in developing relationships and
            communications, or child has no identified family. Child is not
            included in normal family activities.
          </div>
        </div>
      </div>
    </>
  ),
  ToddlerInterpersonal: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">INTERPERSONAL</div>
          This item is used to identify a child’s social and relationship
          skills. Interpersonal skills are rated independently of Social
          Functioning because a child can have social skills but still struggle
          in their relationships at a particular point in time. This strength
          indicates an ability to make and maintain long-standing relationships.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>How does the child interact with other children and adults?</li>
            <li>How does the child do in social settings?</li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          0 -{" "}
          <i>
            Well-developed, centerpiece strength; may be used as a centerpiece
            in an intervention/action plan.
          </i>
          <div className="ms-2">
            Significant interpersonal strengths. Child has well-developed
            interpersonal skills and healthy friendships.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          1 -{" "}
          <i>
            Identified and useful strength. Strength will be used, maintained or
            built upon as part of the plan. May require some effort to develop
            strength into a centerpiece strength.
          </i>
          <div className="ms-2">
            Child has good interpersonal skills and has shown the ability to
            develop healthy friendships.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          2 -{" "}
          <i>
            Strengths have been identified but require strength-building efforts
            before they can be effectively utilized as part of a plan.
            Identified but not useful.
          </i>
          <div className="ms-2">
            Child requires strength building to learn to develop good
            interpersonal skills and/or healthy friendships. Child has some
            social skills that facilitate positive relationships with peers and
            adults but may not have any current healthy friendships.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          3 -{" "}
          <i>
            An area in which no current strength is identified; efforts may be
            recommended to develop a strength in this area.
          </i>
          <div className="ms-2">
            There is no evidence of observable interpersonal skills or healthy
            friendships at this time and/or child requires significant help to
            learn to develop interpersonal skills and healthy friendships.
          </div>
        </div>
      </div>
    </>
  ),
  ToddlerPlayfulness: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">PLAYFULNESS</div>
          This item rates the degree to which a child is given opportunities for
          and participates in age-appropriate play. Play should be understood
          developmentally. When rating this item, you should consider if the
          child is interested in play and/or whether the child needs adult
          support while playing. Problems with either solitary or group (e.g.
          parallel) play could be rated here.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>Is the child easily engaged in play?</li>
            <li>Does the child initiate play? Can the child sustain play?</li>
            <li>
              Does the child need adult support in initiating and sustaining
              play more than what is developmentally appropriate?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          0 -{" "}
          <i>
            Well-developed, centerpiece strength; may be used as a centerpiece
            in an intervention/action plan.
          </i>
          <div className="ms-2">
            The child consistently demonstrates the ability to make use of play
            to further their development. Their play is consistently
            developmentally appropriate, spontaneous, self-initiated and
            enjoyable.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          1 -{" "}
          <i>
            Identified and useful strength. Strength will be used, maintained or
            built upon as part of the plan. May require some effort to develop
            strength into a centerpiece strength.
          </i>
          <div className="ms-2">
            The child demonstrates play that is developmentally appropriate,
            self-initiated, spontaneous and enjoyable much of the time. Child
            needs some assistance making full use of play.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          2 -{" "}
          <i>
            Strengths have been identified but require strength-building efforts
            before they can be effectively utilized as part of a plan.
            Identified but not useful.
          </i>
          <div className="ms-2">
            The child demonstrates the ability to enjoy play and use it to
            support their development some of the time or with support of a
            caregiver. Even with this in place there does not appear to be
            investment and enjoying in the child.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          3 -{" "}
          <i>
            An area in which no current strength is identified; efforts may be
            recommended to develop a strength in this area.
          </i>
          <div className="ms-2">
            The child does not demonstrate the ability to play in a
            developmentally appropriate or quality manner.
          </div>
        </div>
      </div>
    </>
  ),
  ToddlerAdaptability: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">PERSISTENCE AND ADAPTABILITY</div>
          This item refers to how the child reacts to new situations or
          experiences, how they respond to changes in routines, as well as their
          ability to keep trying a new task/skill, even when it is difficult for
          them.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Does child show ability to hang in there even when frustrated by a
              challenging task?
            </li>
            <li>
              Does child routinely require adult support in trying a new
              skill/activity?
            </li>
            <li>
              Can child easily and willingly transition between activities?
            </li>
            <li>
              What type of support does the child require to adapt to changes in
              schedules?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          0 -{" "}
          <i>
            Well-developed, centerpiece strength; may be used as a centerpiece
            in an intervention/action plan.
          </i>
          <div className="ms-2">
            The child consistently has a strong ability to adjust to changes and
            transitions, and continue an activity when challenged or meeting
            obstacles. This supports further growth and development and can be
            incorporated into a service plan as a centerpiece strength.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          1 -{" "}
          <i>
            Identified and useful strength. Strength will be used, maintained or
            built upon as part of the plan. May require some effort to develop
            strength into a centerpiece strength.
          </i>
          <div className="ms-2">
            Child with good curiosity and some ability to continue an activity
            that is challenging. An ambulatory child who does not walk to
            interesting objects, but who will actively explore them when
            presented to them, would be rated here. The child demonstrates a
            level of adaptability and ability to continue in an activity that is
            challenging. The child could benefit from further development in
            this area before it is considered a significant strength.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          2 -{" "}
          <i>
            Strengths have been identified but require strength-building efforts
            before they can be effectively utilized as part of a plan.
            Identified but not useful.
          </i>
          <div className="ms-2">
            The child shows some ability to continue a challenging task although
            this needs to be more fully developed. Parents and caregivers need
            to be the primary support in this area.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          3 -{" "}
          <i>
            An area in which no current strength is identified; efforts may be
            recommended to develop a strength in this area.
          </i>
          <div className="ms-2">
            Child’s difficulties coping with challenges places their development
            at risk. Child may seem frightened of new information, changes or
            environments.
          </div>
        </div>
      </div>
    </>
  ),
  ChildSocialFuntioning: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">SOCIAL FUNCTIONING</div>
          This item rates social skills and relationships. It includes
          age-appropriate behavior and the ability to make and sustain
          relationships.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>Is the child pleasant and likeable?</li>
            <li>Do same age peers like the child?</li>
            <li>
              Do you feel that the child can act appropriately in social
              settings?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          0 - <i>No evidence of any needs; no need for action.</i>
          <div className="ms-2">
            No evidence of problems and/or child has developmentally appropriate
            social functioning.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          1 -{" "}
          <i>
            Identified need that requires monitoring, watchful waiting, or
            preventive action based on history, suspicion or disagreement.
          </i>
          <div className="ms-2">
            There is a history or suspicion of problems in social relationships.
            Child is having some difficulty interacting with others and building
            and/or maintaining relationships.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          2 -{" "}
          <i>
            Action is required to ensure that the identified need is addressed;
            need is interfering with functioning.
          </i>
          <div className="ms-2">
            Child is having some problems with social relationships that
            interfere with functioning in other life domains.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          3 -{" "}
          <i>
            Need is dangerous or disabling; requires immediate and/or intensive
            action.
          </i>
          <div className="ms-2">
            Child is experiencing significant disruptions in social
            relationships. Child may have no friends or have constant conflict
            in relations with others, or have maladaptive relationships with
            others. The quality of the child’s social relationships presents
            imminent danger to the child’s safety, health, and/or development.
          </div>
        </div>
      </div>
    </>
  ),
  ChildMedical: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">MEDICAL/PHYSICAL</div>
          This item describes both health problems and chronic/acute physical
          conditions or impediments.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Does the child have anything that limits their physical
              activities?
            </li>
            <li>How much does this interfere with the child’s life?</li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          0 - <i>No evidence of any needs; no need for action.</i>
          <div className="ms-2">
            No evidence that the child has any medical or physical problems,
            and/or they are healthy.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          1 -{" "}
          <i>
            Identified need that requires monitoring, watchful waiting, or
            preventive action based on history, suspicion or disagreement.
          </i>
          <div className="ms-2">
            Child has mild, transient or well-managed physical or medical
            problems. These include well-managed chronic conditions like
            juvenile diabetes or asthma.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          2 -{" "}
          <i>
            Action is required to ensure that the identified need is addressed;
            need is interfering with functioning.
          </i>
          <div className="ms-2">
            Child has serious medical or physical problems that require medical
            treatment or intervention. Or child has a chronic illness or a
            physical challenge that requires ongoingmedical intervention.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          3 -{" "}
          <i>
            Need is dangerous or disabling; requires immediate and/or intensive
            action.
          </i>
          <div className="ms-2">
            Child has life-threatening illness or medical/physical condition.
            Immediate and/or intense action should be taken due to imminent
            danger to child’s safety, health, and/or development.
          </div>
        </div>
      </div>
    </>
  ),
  ChildSleep: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">SLEEP</div>
          This item rates the child’s sleep patterns. This item is used to
          describe any problems with sleep, regardless of the cause including
          difficulties falling asleep or staying asleep as well as sleeping too
          much. Both bedwetting and nightmares should be considered sleep
          issues.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>Does the child appear rested?</li>
            <li>Is the child often sleepy during the day?</li>
            <li>
              Does the child have frequent nightmares or difficulty sleeping?
            </li>
            <li>How many hours does the child sleep each night?</li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          0 - <i>No evidence of any needs; no need for action.</i>
          <div className="ms-2">
            Child gets a full night’s sleep each night.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          1 -{" "}
          <i>
            Identified need that requires monitoring, watchful waiting, or
            preventive action based on history, suspicion or disagreement.
          </i>
          <div className="ms-2">
            Child has some problems sleeping. Generally, child gets a full
            night’s sleep but at least once a week problems arise. This may
            include occasionally awakening or bed wetting or having nightmares.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          2 -{" "}
          <i>
            Action is required to ensure that the identified need is addressed;
            need is interfering with functioning.
          </i>
          <div className="ms-2">
            Child is having problems with sleep. Sleep is often disrupted and
            child seldom obtains a full night of sleep.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          3 -{" "}
          <i>
            Need is dangerous or disabling; requires immediate and/or intensive
            action.
          </i>
          <div className="ms-2">
            Child is generally sleep deprived. Sleeping is almost always
            difficult and the child is not able to get a full night’s sleep.
          </div>
        </div>
      </div>
    </>
  ),
  ChildDevelopmental: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">DEVELOPMENTAL/INTELLECTUAL</div>
          This item describes the child’s development as compared to standard
          developmental milestones, as well as rates the presence of any
          developmental (motor, social and speech) or intellectual disabilities.
          It includes Intellectual Developmental Disorder (IDD) and Autism
          Spectrum Disorders. Rate the item depending on the significance of the
          disability and the related level of impairment in personal, social,
          family, school, or occupational functioning.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>Does the child’s growth and development seem healthy?</li>
            <li>
              Has the child reached appropriate developmental milestones (such
              as walking, talking)?
            </li>
            <li>
              Has anyone ever mentioned that the child may have developmental
              problems?
            </li>
            <li>Has the child developed like other same age peers?</li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          0 - <i>No evidence of any needs; no need for action.</i>
          <div className="ms-2">
            No evidence of developmental delay and/or child has no developmental
            problems or intellectual disability.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          1 -{" "}
          <i>
            Identified need that requires monitoring, watchful waiting, or
            preventive action based on history, suspicion or disagreement.
          </i>
          <div className="ms-2">
            There are concerns about possible developmental delay. Child may
            have low IQ, a documented delay, or documented borderline
            intellectual disability (i.e. FSIQ 70-85). Mild deficits in adaptive
            functioning are indicated.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          2 -{" "}
          <i>
            Action is required to ensure that the identified need is addressed;
            need is interfering with functioning.
          </i>
          <div className="ms-2">
            Child has mild developmental delays (e.g., deficits in social
            functioning, inflexibility of behavior causing functional problems
            in one or more settings) and/or mild to moderate Intellectual
            Disability/Intellectual Disability Disorder. (If available, FSIQ
            55-69.) IDD impacts communication, social functioning, daily living
            skills, judgment, and/or risk of manipulation by others.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          3 -{" "}
          <i>
            Need is dangerous or disabling; requires immediate and/or intensive
            action.
          </i>
          <div className="ms-2">
            Child has severe to profound intellectual disability (FSIQ, if
            available, less than 55) and/or Autism Spectrum Disorder with marked
            to profound deficits in adaptive functioning in one or more areas:
            communication, social participation and independent living across
            multiple environments.
          </div>
        </div>
      </div>
    </>
  ),
  ChildSexualDevelopment: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">SEXUAL DEVELOPMENT</div>
          This item looks at broad issues of sexual development including
          developmentally inappropriate sexual behavior or sexual concerns, and
          the reactions of others to any of these factors. The child’s sexual
          orientation, gender identity and expression (SOGIE) could be rated
          here only if they are leading to difficulties. Sexually abusive
          behaviors are rated elsewhere.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Are there concerns about the child’s healthy sexual development?
            </li>
            <li>Is the child sexually active?</li>
            <li>
              Does the child have less/more interest in sex than other same age
              peers?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          0 - <i>No evidence of any needs; no need for action.</i>
          <div className="ms-2">
            No evidence of issues with sexual development.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          1 -{" "}
          <i>
            Identified need that requires monitoring, watchful waiting, or
            preventive action based on history, suspicion or disagreement.
          </i>
          <div className="ms-2">
            History or suspicion of problems with sexual development, but does
            not interfere with functioning in other life domains. May include
            the child’s concerns about sexual orientation, gender identity and
            expression (SOGIE), or anxiety about the reaction of others.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          2 -{" "}
          <i>
            Action is required to ensure that the identified need is addressed;
            need is interfering with functioning.
          </i>
          <div className="ms-2">
            Moderate to serious problems with sexual development that interferes
            with the child’s life functioning in other life domains.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          3 -{" "}
          <i>
            Need is dangerous or disabling; requires immediate and/or intensive
            action.
          </i>
          <div className="ms-2">
            Severe problems with sexual development. This would include very
            frequent risky sexual behavior or victim of sexual exploitation.
          </div>
        </div>
      </div>
    </>
  ),
  ChildSchool: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">SCHOOL</div>
          This item refers to the child’s status with school. If the child has
          completed their schooling then use ‘0’. If child has dropped out
          without completing then use a ‘3’. This item reflects School
          Achievement, School Attendance and School Behavior.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>How is the child doing in school?</li>
            <li>
              Is the child experiencing any problems related to academic
              progress? Behavioral problems?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          0 - <i>No evidence of any needs; no need for action. </i>
          <div className="ms-2">
            There is no evidence that the child is experiencing school problems.
            Child has good educational functioning. Child is meeting or
            exceeding educational expectation at an age-expected grade level.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          1 -{" "}
          <i>
            Identified need that requires monitoring, watchful waiting, or
            preventive action based on history, suspicion or disagreement.{" "}
          </i>
          <div className="ms-2">
            Child is functioning adequately at school, mostly meeting
            educational expectations at an age-expected grade level.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          2 -{" "}
          <i>
            Action is required to ensure that the identified need is addressed;
            need is interfering with functioning.
          </i>
          <div className="ms-2">
            Child is functioning below educational expectations and/or requires
            a specialized educational setting in order to learn at an adequate
            level. Child has been truant at some point during the school year.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          3 -{" "}
          <i>
            Need is dangerous or disabling; requires immediate and/or intensive
            action.
          </i>
          <div className="ms-2">
            Child has significant difficulties with educational functioning.
            Child has significant educational problems including some behavioral
            problems related to academic difficulties (chronic truancy,
            suspensions, expulsions, being held back, etc.). Child may be placed
            in a specialized educational setting but remains unable to learn at
            an adequate level.
          </div>
        </div>
      </div>
    </>
  ),
  ChildBehavioral: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">BEHAVIORAL/MENTAL HEALTH NEEDS*</div>
          This item is used to describe the child’s current mental health and
          substance use needs. A formal mental health diagnosis is not required
          to score this item.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Does the child have any mental health-related conditions (e.g.,
              depression, anxiety, substance use, psychosis, anger)?
            </li>
            <li>
              Do the mental health-related conditions interfere with the child’s
              functioning?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          0 - <i>No evidence of any needs; no need for action.</i>
          <div className="ms-2">
            No mental health challenges. Child has no signs of any notable
            mental health conditions.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          1 -{" "}
          <i>
            Identified need that requires monitoring, watchful waiting, or
            preventive action based on history, suspicion or disagreement.
          </i>
          <div className="ms-2">
            History of mental health challenges. Or child may have problems with
            adjustment, may be somewhat depressed, withdrawn, irritable, or
            agitated, but this does not interfere with functioning.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          2 -{" "}
          <i>
            Action is required to ensure that the identified need is addressed;
            need is interfering with functioning.
          </i>
          <div className="ms-2">
            Evidence of mental health challenge or a diagnosable mental health
            condition that interferes with child’s functioning.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          3 -{" "}
          <i>
            Need is dangerous or disabling; requires immediate and/or intensive
            action.
          </i>
          <div className="ms-2">
            Child has a dangerous or disabling serious psychiatric disorder.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="pt-1 fw-bold bg-secondary text-center">
            *A rating of ‘1,’ ‘2,’ or ‘3’ on this item triggers the completion
            of the [D] Behavioral/Mental Health Needs Module.
          </div>
          <b>Supplemental Information:</b> Any mental health need would be
          indicated here regardless of its specific symptom presentation. So,
          this item combines depression, anxiety, substance use or disruptive
          behavior into a single indicator of any need to connect with specialty
          mental health treatment.
        </div>
      </div>
    </>
  ),
  ChildRiskBehaviors: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">RISK BEHAIORS*</div>
          This item is used to describe the child’s current behaviors that could
          get them into serious trouble or put them in danger of harming
          themselves or others.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Is the child in danger of harming themselves (e.g., suicidal
              thoughts or actions, self-injury, reckless behavior) or others
              (e.g., physical harm, fire setting, sexual aggression, bullying)?
            </li>
            <li>Do these behaviors interfere with functioning?</li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          0 - <i>No evidence of any needs; no need for action. </i>
          <div className="ms-2">
            No evidence of current behaviors that could get child in trouble or
            put them in danger of harming themselves or others.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          1 -{" "}
          <i>
            Identified need that requires monitoring, watchful waiting, or
            preventive action based on history, suspicion or disagreement.
          </i>
          <div className="ms-2">
            Child has a history of risk behaviors or is displaying some
            behaviors that are not yet interfering with child’s functioning such
            as verbal threats of aggression or running away, sexually
            inappropriate but not harmful behavior or socially inappropriate
            behavior, or status offenses.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          2 -{" "}
          <i>
            Action is required to ensure that the identified need is addressed;
            need is interfering with functioning.
          </i>
          <div className="ms-2">
            Evidence of current risk behaviors that interfere with child’s
            functioning. This may include suicidal ideation without a plan,
            self-injurious behavior not requiring medical attention, harm to
            others, recent running away, or fire-setting or delinquent behavior
            that did not harm others.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          3 -{" "}
          <i>
            Need is dangerous or disabling; requires immediate and/or intensive
            action.
          </i>
          <div className="ms-2">
            Dangerous or disabling risk behaviors that put child or others in
            immediate danger. This may include current suicidal ideation or
            attempt, physical harm to others including battery or rape, or
            reckless or risk-taking behavior that puts an individual in harm’s
            way.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="pt-1 fw-bold bg-secondary text-center">
            *A rating of ‘1,’ ‘2,’ or ‘3’ on this item triggers the completion
            of the [E] Risk Behaviors Module.
          </div>
        </div>
      </div>
    </>
  ),
  ChildAdjustment: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">ADJUSTMENT TO TRAUMA*</div>
          This item is used to describe the child who is having difficulties
          adjusting to a traumatic experience, as defined by the child. This is
          one item where speculation about why a person is displaying a certain
          behavior is considered. There should be an inferred link between the
          trauma and behavior.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>What was the child’s trauma?</li>
            <li>How is it connected to the current issue(s)?</li>
            <li>What are the child’s coping skills?</li>
            <li>Who is supporting the child?</li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          0 - <i>No evidence of any needs; no need for action. </i>
          <div className="ms-2">
            No evidence that child has experienced a traumatic life event, OR
            child has adjusted well to traumatic/adverse experiences.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          1 -{" "}
          <i>
            Identified need that requires monitoring, watchful waiting, or
            preventive action based on history, suspicion or disagreement.
          </i>
          <div className="ms-2">
            The child has experienced a traumatic event and there are some
            changes in their behavior that are managed or supported by
            caregivers. These symptoms are expected to ease with the passage of
            time and therefore no current intervention is warranted. Child may
            be in the process of recovering from a more extreme reaction to a
            traumatic experience, which may require a need to watch these
            symptoms or engage in preventive action.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          2 -{" "}
          <i>
            Action is required to ensure that the identified need is addressed;
            need is interfering with functioning.
          </i>
          <div className="ms-2">
            Clear evidence of adjustment problems associated with traumatic life
            event(s). Symptoms can vary widely and may include sleeping or
            eating disturbances, regressive behavior, behavior problems or
            problems with attachment. Adjustment is interfering with child’s
            functioning in at least one life domain.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          3 -{" "}
          <i>
            Need is dangerous or disabling; requires immediate and/or intensive
            action.
          </i>
          <div className="ms-2">
            Clear evidence of debilitating level of trauma symptoms that makes
            it virtually impossible for the child to function in any life domain
            including symptoms such as flashbacks, nightmares, significant
            anxiety, intrusive thoughts, and/or re-experiencing trauma
            (consistent with PTSD).
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="pt-1 fw-bold bg-secondary text-center">
            *A rating of ‘1’, ‘2’ or ‘3’ on this item triggers the completion of
            the [F] Trauma Module.
          </div>
          <b>Supplemental Information:</b> This item covers both adjustment
          disorders and posttraumatic stress disorder from the DSM. Behaviors
          which might indicate trauma reactions include
          anxiousness/hyper-vigilance, regression to behavior of younger ages
          (e.g., toileting problems, babyish speech, failure to engage in
          self-feeding, bathing, and other self-care), appetite disruption,
          withdrawal of interest from pleasurable activities, and other signs of
          emotional dysregulation after significant life events.
        </div>
      </div>
    </>
  ),
  ChildPsychosis: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">BMHN1. PSYCHOSIS (THOUGHT DISORDER)</div>
          This item rates the symptoms of psychiatric disorders with a known
          neurological base, including schizophrenia spectrum and other
          psychotic disorders. The common symptoms of these disorders include
          hallucinations (i.e. experiencing things others do not experience),
          delusions (i.e. a false belief or an incorrect inference about reality
          that is firmly sustained despite the fact that nearly everybody thinks
          the belief is false or proof exists of its inaccuracy), disorganized
          thinking, and bizarre/idiosyncratic behavior.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Does the child exhibit behaviors that are unusual or difficult to
              understand?
            </li>
            <li>Does the child engage in certain actions repeatedly?</li>
            <li>
              Are the unusual behaviors or repeated actions interfering with the
              child’s functioning?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            0 - No evidence of psychotic symptoms. Both thought processes and
            content are within normal range.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            1 - Evidence of disruption in thought processes or content. Child
            may be somewhat tangential in speech or evidence somewhat illogical
            thinking (age-inappropriate). This also includes a child with a
            history of hallucinations but none currently. Use this category for
            children who are below the threshold for one of the DSM diagnoses
            listed above.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            2 - Evidence of disturbance in thought process or content that may
            be impairing the child’s functioning in at least one life domain.
            Child may be somewhat delusional or have brief intermittent
            hallucinations. Speech may be at times quite tangential or illogical
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            3 - Clear evidence of dangerous hallucinations, delusions, or
            bizarre behavior that might be associated with some form of
            psychotic disorder that places the child or others at risk of
            physical harm.
          </div>
        </div>
      </div>
    </>
  ),
  ChildImpulsivity: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">BMHN2. IMPULSIVITY/HYPERACTIVITY</div>
          Problems with impulse control and impulsive behaviors, including
          motoric disruptions, are rated here. This includes behavioral symptoms
          associated with Attention-Deficit Hyperactivity Disorder (ADHD) and
          Impulse-Control Disorders as indicated in the DSM-5. Children with
          impulse problems tend to engage in behavior without thinking,
          regardless of the consequences. This can include compulsions to engage
          in gambling, violent behavior (e.g., road rage), sexual behavior,
          fire-starting or stealing.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>Is the child unable to sit still for any length of time?</li>
            <li>
              Does the child have trouble paying attention for more than a few
              minutes?
            </li>
            <li>Is the child able to control their behavior, talking?</li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            0 - No evidence of symptoms of loss of control of behavior.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            1 - There is a history or evidence of mild levels of impulsivity
            evident in action or thought that place the child at risk of future
            functioning difficulties. The child may exhibit limited impulse
            control, e.g., child may yell out answers to questions or may have
            difficulty waiting one’s turn. Some motor difficulties may be
            present as well, such as pushing or shoving others.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            2 - Clear evidence of problems with impulsive, distractible, or
            hyperactive behavior that interferes with the child’s functioning in
            at least one life domain. This indicates a child with impulsive
            behavior who may represent a significant management problem for
            adults (e.g., caregivers, teachers, coaches, etc.). A child who
            often intrudes on others and often exhibits aggressive impulses
            would be rated here.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            3 - Clear evidence of a dangerous level of hyperactivity and/or
            impulsive behavior that places the child at risk of physical harm.
            This indicates a child with frequent and significant levels of
            impulsive behavior that carries considerable safety risk (e.g.,
            running into the street, dangerous driving or bike riding). The
            child may be impulsive on a nearly continuous basis. The child
            endangers self or others without thinking.
          </div>
        </div>
      </div>
    </>
  ),
  ChildDepression: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">BMHN3. DEPRESSION</div>
          This item rates symptoms such as irritable or depressed mood, social
          withdrawal, sleep disturbances, weight/eating disturbances, and loss
          of motivation, interest or pleasure in daily activities. This item can
          be used to rate symptoms of the depressive disorders as specified in
          DSM-5.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Is child concerned about possible depression or chronic low mood
              and irritability?
            </li>
            <li>Has the child withdrawn from normal activities?</li>
            <li>Does the child seem lonely or not interested in others?</li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            0 - No evidence of problems with depression.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            1 - History or suspicion of depression or evidence of depression
            associated with a recent negative life event with minimal impact on
            life domain functioning. Brief duration of depression, irritability,
            or impairment of peer, family, or academic functioning that does not
            lead to pervasive avoidance behavior.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            2 - Clear evidence of depression associated with either depressed
            mood or significant irritability. Depression has interfered
            significantly in child’s ability to function in at least one life
            domain.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            3 - Clear evidence of disabling level of depression that makes it
            virtually impossible for the child to function in any life domain.
            This rating is given to a child with a severe level of depression.
            This would include a child who stays at home or in bed all day due
            to depression or one whose emotional symptoms prevent any
            participation in school, friendship groups, or family life.
            Disabling forms of depressive diagnoses would be rated here.
          </div>
        </div>
      </div>
    </>
  ),
  ChildAnxiety: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">BMHN4. ANXIETY</div>
          This item rates symptoms associated with DSM-5 Anxiety Disorders
          characterized by excessive fear and anxiety and related behavioral
          disturbances (including avoidance behaviors). Panic attacks can be a
          prominent type of fear response.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Does the child have any problems with anxiety or fearfulness?
            </li>
            <li>Is the child avoiding normal activities out of fear?</li>
            <li>Does the child act frightened or afraid?</li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">0 - No evidence of anxiety symptoms.</div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            1 - There is a history, suspicion, or evidence of mild anxiety
            associated with a recent negative life event. This level is used to
            rate either a mild phobia or anxiety problem that is not yet causing
            the child significant distress or markedly impairing functioning in
            any important context.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            2 - Clear evidence of anxiety associated with either anxious mood or
            significant fearfulness. Anxiety has interfered in the child’s
            ability to function in at least one life domain.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            3 - Clear evidence of debilitating level of anxiety that makes it
            virtually impossible for the child to function in any life domain.
          </div>
        </div>
      </div>
    </>
  ),
  ChildOppositional: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">
            BMHN5. OPPOSITIONAL (NON-COMPLIANCE WITH AUTHORITY)
          </div>
          This item rates the child’s relationship with authority figures.
          Generally oppositional behavior is displayed in response to conditions
          set by a parent, teacher or other authority figure with responsibility
          for and control over the child.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>Does the child follow their caregivers’ rules?</li>
            <li>
              Have teachers or other adults reported that the child does not
              follow rules or directions?
            </li>
            <li>
              Does the child argue with adults when they try to get the child to
              do something?
            </li>
            <li>
              Does the child do things that they have been explicitly told not
              to do?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">0 - No evidence of oppositional behaviors.</div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            1 - There is a history or evidence of mild level of defiance towards
            authority figures that has not yet begun to cause functional
            impairment. Child may occasionally talk back to teacher,
            parent/caregiver; there may be letters or calls from school.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            2 - Clear evidence of oppositional and/or defiant behavior towards
            authority figures that is currently interfering with the child’s
            functioning in at least one life domain. Behavior causes emotional
            harm to others. A child whose behavior meets the criteria for
            Oppositional Defiant Disorder in DSM-5 would be rated here.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            3 - Clear evidence of a dangerous level of oppositional behavior
            involving the threat of physical harm to others. This rating
            indicates that the child has severe problems with compliance with
            rules or adult instruction or authority.
          </div>
        </div>
      </div>
    </>
  ),
  ChildConduct: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">BMHN6. CONDUCT</div>
          This item rates the degree to which a child engages in behavior that
          is consistent with the presence of a Conduct Disorder.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Is the child seen as dishonest? How does the child handle telling
              the truth/lies?
            </li>
            <li>Has the child been part of any criminal behavior?</li>
            <li>
              Has the child ever shown violent or threatening behavior towards
              others?
            </li>
            <li>Has the child ever tortured animals?</li>
            <li>
              Does the child disregard or is unconcerned about the feelings of
              others (lack empathy)?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            0 - No evidence of serious violations of others or laws.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            1 - There is a history, suspicion or evidence of some problems
            associated with antisocial behavior including but not limited to
            lying, stealing, manipulation of others, acts of sexual aggression,
            or violence towards people, property or animals. The child may have
            some difficulties in school and home behavior. Problems are
            recognizable but not notably deviant for age, sex and community.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            2 - Clear evidence of antisocial behavior including but not limited
            to lying, stealing, manipulating others, sexual aggression, violence
            towards people, property, or animals. A child rated at this level
            will likely meet criteria for a diagnosis of Conduct Disorder.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            3 - Evidence of a severe level of aggressive or antisocial behavior,
            as described above, that places the child or community at
            significant risk of physical harm due to these behaviors. This could
            include frequent episodes of unprovoked, planned aggressive or other
            antisocial behavior.
          </div>
        </div>
      </div>
    </>
  ),
  ChildSubstanceUse: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">BMHN7. SUBSTANCE USE</div>
          This item describes problems related to the use of alcohol and illegal
          drugs, the misuse of prescription medications, and the inhalation of
          any chemical or synthetic substance by a child. This rating is
          consistent with DSM-5 Substance-Related and Addictive Disorders. This
          item does not apply to the use of tobacco or caffeine.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Has the child used alcohol or drugs on more than an experimental
              basis?
            </li>
            <li>
              Do you suspect that the child may have an alcohol or drug use
              problem?
            </li>
            <li>
              Has the child been in a recovery program for the use of alcohol or
              illegal drugs?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            0 - Child has no notable substance use difficulties at the present
            time.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            1 - Child has substance use problems that occasionally interfere
            with daily life (e.g., intoxication, loss of money, reduced
            work/school performance, parental concern). History of substance use
            problems without evidence of current problems related to use is
            rated here.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            2 - Child has a substance use problem that consistently interferes
            with the ability to function optimally, but does not completely
            preclude functioning in an unstructured setting.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            3 - Child has a substance use problem that represents complications
            to functional issues that may result in danger to self, public
            safety issues, or the need for detoxification of the child.
          </div>
        </div>
      </div>
    </>
  ),
  ChildEatingDisturbance: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">BMHN8. EATING DISTURBANCE</div>
          This item rates problems with eating, including disturbances in body
          image, refusal to maintain normal body weight, recurrent episodes of
          binge eating, and hoarding food.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>How does the child feel about their body?</li>
            <li>
              Does the child seem to be overly concerned about their weight?
            </li>
            <li>
              Does the child ever refuse to eat, binge eat, or hoard food?
            </li>
            <li>
              Has the child ever been hospitalized for eating-related issues?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">0 - No evidence of eating disturbances.</div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            1 - There is a history, suspicion or mild level of eating
            disturbance. This could include some preoccupation with weight,
            calorie intake, or body size or type when of normal weight or below
            weight. This could also include some binge eating patterns.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            2 - Eating disturbance impairs child’s functioning in at least one
            life domain. This could include a more intense preoccupation with
            weight gain or becoming fat when underweight, restrictive eating
            habits or excessive exercising in order to maintain below normal
            weight, and/or emaciated body appearance. This level could also
            include more notable binge eating episodes that are followed by
            compensatory behaviors in order to prevent weight gain (e.g.,
            vomiting, use of laxatives, excessive exercising). The child may
            meet criteria for a DSM-5 Feeding and Eating Disorders (including
            Anorexia Nervosa, Bulimia Nervosa, Avoidant/Restrictive Food Intake
            Disorder, etc.). Food hoarding also would be rated here.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            3 - Child’s eating disturbance is dangerous or puts their health at
            risk. This could include significantly low weight where
            hospitalization is required or excessive binge-purge behaviors (at
            least once per day).
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <b>Supplemental Information:</b> Anorexia Nervosa is characterized by
          refusal to maintain a body weight that is at or above the minimum
          normal weight for age and height, intense fear of gaining weight or
          becoming fat, denying the seriousness of having a low body weight, or
          having a distorted image of your appearance or shape. Repeated
          bingeing and getting rid of the extra calories from bingeing by
          vomiting, excessive exercise, fasting, or misuse of laxatives,
          diuretics, enemas or other medications characterize Bulimia Nervosa.
        </div>
      </div>
    </>
  ),
  ChildAngerControl: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">BMHN9. ANGER CONTROL</div>
          This item captures the child’s ability to identify and manage their
          anger when frustrated.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>How does the child control their emotions?</li>
            <li>Does the child get upset or frustrated easily?</li>
            <li>
              Does the child overreact if someone criticizes or rejects them?
            </li>
            <li>Does the child seem to have dramatic mood swings?</li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            0 - No evidence of any anger control problems.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            1 - History, suspicion of, or evidence of some problems with
            controlling anger. Child may sometimes become verbally aggressive
            when frustrated. Peers and family are aware of and may attempt to
            avoid stimulating angry outbursts.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            2 - Child’s difficulties with controlling anger are impacting
            functioning in at least one life domain. Child’s temper has resulted
            in significant trouble with peers, family and/or school. Anger may
            be associated with physical violence. Others are likely quite aware
            of anger potential.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            3 - Child’s temper or anger control problem is dangerous. Child
            frequently gets into fights that are often physical. Others likely
            fear the child.
          </div>
        </div>
      </div>
    </>
  ),
  ChildAttachmentDifficulties: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">BMHN10. ATTACHMENT DIFFICULTIES</div>
          This item rates the level of difficulties the child has with
          attachment and their ability to form relationships.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>Does the child struggle with separating from caregiver?</li>
            <li>Does the child approach or attach to strangers?</li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            0 - No evidence of attachment problems. Caregiver-child relationship
            is characterized by mutual satisfaction of needs and child's
            development of a sense of security and trust. Caregiver is able to
            respond to child cues in a consistent, appropriate manner, and child
            seeks age-appropriate contact with caregiver for both nurturing and
            safety needs.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            1 - Some history or evidence of insecurity in the caregiver-child
            relationship. Caregiver may have difficulty accurately reading
            child’s bids for attention and nurturance; may be inconsistent in
            response; or may be occasionally intrusive. Child may have some
            problems with separation (e.g., anxious/clingy behaviors in the
            absence of obvious cues of danger) or may avoid contact with
            caregiver in age-inappropriate way. Child may have minor
            difficulties with appropriate physical/emotional boundaries with
            others.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            2 - Problems with attachment that interfere with child’s functioning
            in at least one life domain and require intervention. Caregiver may
            consistently misinterpret child cues, act in an overly intrusive
            way, or ignore/avoid child bids for attention/nurturance. Child may
            have ongoing difficulties with separation, may consistently avoid
            contact with caregivers, and have ongoing difficulties with physical
            or emotional boundaries with others.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            3 - Child is unable to form attachment relationships with others
            (e.g., chronic dismissive/avoidant/detached behavior in care giving
            relationships) OR child presents with diffuse emotional/physical
            boundaries leading to indiscriminate attachment with others. Child
            is considered at ongoing risk due to the nature of their attachment
            behaviors. Child may have experienced significant early separation
            from or loss of caregiver, or have experienced chronic inadequate
            care from early caregivers, or child may have individual
            vulnerabilities (e.g., mental health, developmental disabilities)
            that interfere with the formation of positive attachment
            relationships.
          </div>
        </div>
      </div>
    </>
  ),
  ChildSuicideRisk: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">RB1. SUICIDE RISK</div>
          This item is intended to describe the presence of thoughts or
          behaviors aimed at taking one’s life. This rating describes both
          suicidal and significant self-injurious behavior. This item rates
          overt and covert thoughts and efforts on the part of a child to end
          their life. A rating of ‘2’ or ‘3’ would indicate the need for a
          safety plan. Notice the specific time frames for each rating.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Has the child ever talked about a wish or plan to die or to kill
              themselves?
            </li>
            <li>Has the child ever tried to commit suicide?</li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">0 - No evidence of suicidal ideation.</div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            1 - History of suicidal ideation, but no recent ideation or gesture.
            History of suicidal behaviors or significant ideation but none
            during the recent past.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            2 - Recent, but not acute, suicidal ideation or gesture.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            3 - Current suicidal ideation and intent OR command hallucinations
            that involve self-harm.
          </div>
        </div>
      </div>
    </>
  ),
  ChildSelfInjurious: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">
            RB2. NON-SUICIDAL SELF-INJURIOUS BEHAVIOR
          </div>
          This item includes repetitive, physically harmful behavior that
          generally serves as a self-soothing function to the child (e.g.,
          cutting, carving, burning self, face slapping, head banging, etc.).
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Does the behavior serve a self-soothing purpose (e.g., numb
              emotional pain, move the focus of emotional pain to the physical)?
            </li>
            <li>
              Does the child ever purposely hurt themselves (e.g., cutting)?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            0 - No evidence of any forms of self-injury.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            1 - A history or suspicion of self-injurious behavior.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            2 - Engaged in self-injurious behavior (e.g., cutting, burns,
            piercing skin with sharp objects, repeated head banging) that does
            not require medical attention.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            3 - Engaged in self-injurious behavior requiring medical
            intervention (e.g., sutures, surgery) and that is significant enough
            to put the child’s health at risk.
          </div>
        </div>
      </div>
    </>
  ),
  ChildRecklessness: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">RB3. OTHER SELF-HARM (RECKLESSNESS)</div>
          This item includes reckless and dangerous behaviors that, while not
          intended to harm self or others, place the child or others in some
          jeopardy. Suicidal or self-injurious behaviors are not rated here.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>Does the child act without thinking?</li>
            <li>
              Has the child ever talked about or acted in a way that might be
              dangerous to themselves (e.g., reckless behavior such as riding on
              top of cars, reckless driving, climbing bridges, etc.)?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            0 - No evidence of behaviors (other than suicide or self-mutilation)
            that place the child at risk of physical harm.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            1 - There is a history or suspicion of or mild reckless or
            risk-taking behavior (other than suicide or self-mutilation) that
            places child at risk of physical harm.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            2 - Engaged in reckless or intentional risk-taking behavior (other
            than suicide or self-mutilation) that places the child in danger of
            physical harm.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            3 - Engaged in reckless or intentional risk-taking behavior (other
            than suicide or self-mutilation) that places the child at immediate
            risk of death.
          </div>
        </div>
      </div>
    </>
  ),
  ChildDangerToOthers: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">RB4. DANGER TO OTHERS</div>
          This item rates the child’s violent or aggressive behavior. The
          intention of this behavior is to cause significant bodily harm to
          others. A rating of ‘2’ or ‘3’ would indicate the need for a safety
          plan. Reckless behavior that may cause physical harm to others is not
          rated on this item.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>Has the child ever injured another person on purpose?</li>
            <li>Does the child get into physical fights?</li>
            <li>
              Has the child ever threatened to kill or seriously injure others?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            0 - No evidence or history of aggressive behaviors or significant
            verbal threats of aggression towards others (including people and
            animals).
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            1 - History of aggressive behavior or verbal threats of aggression
            towards others. History of fire setting would be rated here.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            2 - Occasional or moderate level of aggression towards others. Child
            has made verbal threats of violence towards others.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            3 - Acute homicidal ideation with a plan, frequent or dangerous
            (significant harm) level of aggression to others. Child is an
            immediate risk to others.
          </div>
        </div>
      </div>
    </>
  ),
  ChildSexualAggression: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">RB5. SEXUAL AGGRESSION</div>
          This item is intended to describe both aggressive sexual behavior and
          sexual behavior in which the child takes advantage of a younger or
          less powerful child. The severity and recency of the behavior provide
          the information needed to rate this item.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Has the child ever been accused of being sexually aggressive
              towards another child?
            </li>
            <li>Has the child had sexual contact with a younger individual?</li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            0 - No evidence of sexually aggressive behavior.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            1 - History or suspicion of sexually aggressive behavior and/or
            sexually inappropriate behavior within the past year that troubles
            others such as harassing talk or public masturbation.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            2 - Child engages in sexually aggressive behavior that negatively
            impacts functioning. For example, frequent inappropriate sexual
            behavior (e.g., inappropriate touching of others). Frequent
            disrobing would be rated here only if it was sexually provocative.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            3 - Child engages in a dangerous level of sexually aggressive
            behavior. This would indicate the rape or sexual abuse of another
            person involving sexual penetration.
          </div>
        </div>
      </div>
    </>
  ),
  ChildRunaway: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">RB6. RUNAWAY</div>
          This item describes the risk of running away or actual runaway
          behavior.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Has the child ever run away from home, school, or any other place?
            </li>
            <li>
              If so, where did the child go? How long did they stay away? How
              was the child found?
            </li>
            <li>Does the child ever threaten to run away?</li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            0 - Child has no history of running away or ideation of escaping
            from current living situation.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            1 - Child has no recent history of running away but has expressed
            ideation about escaping current living situation. Child may have
            threatened running away on one or more occasions or has a history of
            running away but not in the recent past.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            2 - Child has run from home once or run from one treatment setting.
            Also rated here is a child who has run home (parental or relative).
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            3 - Child has run from home and/or treatment settings in the recent
            past and presents an imminent flight risk. A child who is currently
            a runaway is rated here.
          </div>
        </div>
      </div>
    </>
  ),
  ChildDelinquent: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">RB7. DELINQUENT BEHAVIOR</div>
          This item includes both criminal behavior and status offenses that may
          result from child failing to follow required behavioral standards
          (e.g., truancy, curfew violations, driving without a license). Sexual
          offenses should be included as criminal behavior. If caught, the child
          could be arrested for this behavior.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Do you know of laws that the child has broken (even if the child
              has not been charged or caught)?
            </li>
            <li>Has the child ever been arrested?</li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            0 - No evidence or no history of delinquent behavior.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            1 - History or suspicion of delinquent behavior, but none in the
            recent past. Status offenses would generally be rated here.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            2 - Currently engaged in delinquent behavior (e.g., vandalism,
            shoplifting, etc.) that puts the child at risk.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            3 - Serious recent acts of delinquent activity that place others at
            risk of significant loss or injury, or place the child at risk of
            adult sanctions. Examples include car theft, residential burglary
            and gang involvement.
          </div>
        </div>
      </div>
    </>
  ),
  ChildFireSetting: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">RB8. FIRE SETTING</div>
          This item refers to behavior involving the intentional setting of
          fires that might be dangerous to the child or others. This includes
          both malicious and non-malicious fire-setting. This does NOT include
          the use of candles or incense or matches to smoke or accidental
          fire-setting.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>Has the child ever started a fire?</li>
            <li>
              Has the incident of fire setting put anyone at harm or at risk of
              harm?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            0 - No evidence of fire setting by the child.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            1 - History of fire setting but not in the recent past.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            2 - Recent fire setting behavior but not of the type that has
            endangered the lives of others OR repeated fire-setting behavior in
            the recent past.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            3 - Acute threat of fire setting. Set fire that endangered the lives
            of others (e.g. attempting to burn down a house).
          </div>
        </div>
      </div>
    </>
  ),
  ChildMisbehavior: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">RB9. INTENTIONAL MISBEHAVIOR</div>
          This item describes intentional behaviors that a child engages in to
          force others to administer consequences. This item should reflect
          problematic social behaviors (socially unacceptable behavior for the
          culture and community in which the child lives) that put the child at
          some risk of consequences. It is not necessary that the child be able
          to articulate that the purpose of their misbehavior is to provide
          reactions/consequences to rate this item. There is always, however, a
          benefit to the child resulting from this unacceptable behavior even if
          it does not appear this way on the face of it (e.g., child feels more
          protected, more in control, less anxious because of the sanctions).
          This item should not be rated for children who engage in such behavior
          solely due to developmental delays.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Does the child intentionally do or say things to upset others or
              get in trouble with people in positions of authority (e.g.,
              parents or teachers)?
            </li>
            <li>
              Has the child engaged in behavior that was insulting, rude or
              obnoxious and which resulted in sanctions for the child such as
              suspension, job dismissal, etc.?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            0 - Child shows no evidence of problematic social behaviors that
            cause adults to administer consequences.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            1 - Some problematic social behaviors that force adults to
            administer consequences to the child. Provocative comments or
            behavior in social settings aimed at getting a negative response
            from adults might be included at this level.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            2 - Child may be intentionally getting in trouble in school or at
            home and the consequences, or threat of consequences, is causing
            problems in the child’s life.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            3 - Frequent seriously inappropriate social behaviors force adults
            to seriously and/or repeatedly administer consequences to the child.
            The inappropriate social behaviors may cause harm to others and/or
            place the child at risk of significant consequences (e.g. expulsion
            from school, removal from the community).
          </div>
        </div>
      </div>
    </>
  ),
  ChildBullying: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">RB10. BULLYING</div>
          This item rates behavior that involves intimidation (verbal or
          physical) of others; threatening others with harm if they do not
          comply with the individual’s demands is rated here. A victim of
          bullying is not rated here.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Are there concerns that the child might bully other children?
            </li>
            <li>
              Have there been any reports that the childhas picked on, made fun
              of, harassed or intimidated another person?
            </li>
            <li>Does the child hang around with other people who bully?</li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            0 - No evidence that the child has ever engaged in bullying at
            school or in the community.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            1 - History or suspicion of bullying, or child has engaged in
            bullying behavior or associated with groups that have bullied other
            children.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            2 - Child has bullied other children in school or in the community.
            They have either bullied the other children, or led a group that
            bullied other children.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            3 - Child has repeatedly utilized threats or actual violence when
            bullying others in school and/or in the community.
          </div>
        </div>
      </div>
    </>
  ),
  ChildExploitation: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">RB11. VICTIMIZATION/EXPLOITATION</div>
          This item describes a child who has been victimized by others. This
          item is used to examine a history and pattern of being the object of
          abuse and/or whether the child is at current risk for
          re-victimization. It would also include children who are victimized in
          other ways (e.g., sexual abuse, sexual exploitation, inappropriate
          expectations based on a child’s level of development, a child who is
          forced to take on a parental level of responsibility, etc.).
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>Has the child ever been bullied or the victim of acrime?</li>
            <li>
              Has the child traded sexual activity for goods, money, affection
              or protection?
            </li>
            <li>Has the child been a victim of human trafficking?</li>
            <li>
              Is the child parentified or has taken on parental responsibilities
              and has this impacted their functioning?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            0 - No evidence that the child has experienced victimization or
            exploitation.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            1 - Suspicion or history of victimization or exploitation, but the
            child has not been victimized to any significant degree in the past
            year. Child is not presently at risk for re-victimization or
            exploitation.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            2 - Child has been recently victimized (within the past year) and
            may be at risk of re-victimization. This might include physical or
            sexual abuse, significant psychological abuse by family or friend,
            sexual exploitation, or violent crime.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            3 - Child has been recently or is currently being victimized or
            exploited, including human trafficking (e.g., labor or sexual
            exploitation including the production of pornography, sexually
            explicit performance, sexual activity) or living in an abusive
            relationship, or constantly taking on responsibilities of being a
            parent to other family members.
          </div>
        </div>
      </div>
    </>
  ),
  ChildSexualAbuse: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">ACE1. SEXUAL ABUSE</div>
          This item describes whether or not the child has experienced sexual
          abuse.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>Has the caregiver or child disclosed sexual abuse?</li>
            <li>How often did the abuse occur?</li>
            <li>Did the abuse result in physical injury?</li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            No - There is no evidence that the child has experienced sexual
            abuse.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            Yes - Child has experienced sexual abuse, or there is a suspicion
            that they have experienced sexual abuse – single or multiple
            episodes, or chronic over an extended period of time. The abuse may
            have involved penetration, multiple perpetrators, and/or associated
            physical injury. Child with exposure to secondary sexual abuse
            (e.g., witnessing sexual abuse, having a sibling sexually abused)
            should be rated here.
          </div>
        </div>
      </div>
    </>
  ),
  ChildPhysicalAbuse: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">ACE2. PHYSICAL ABUSE</div>
          This item describes whether or not the child has experienced physical
          abuse.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>Is physical discipline used in the home? What forms?</li>
            <li>
              Has the child ever received bruises, marks, or injury from
              discipline?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            No - There is no evidence that the child has experienced physical
            abuse.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            Yes - Child has experienced or there is a suspicion that they have
            experienced physical abuse – mild to severe, or repeated physical
            abuse with sufficient physical harm requiring medical treatment.
          </div>
        </div>
      </div>
    </>
  ),
  ChildNeglect: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">ACE3. NEGLECT</div>
          This item describes whether or not the child has experienced neglect.
          Neglect can refer to a lack of food, shelter or supervision (physical
          neglect), lack of access to needed medical care (medical neglect), or
          failure to receive academic instruction (educational neglect).
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>Is the child receiving adequate supervision?</li>
            <li>Are basic needs for food and shelter being met?</li>
            <li>
              Is the child allowed access to necessary medical care? Education?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            No - There is no evidence that the child has experienced neglect.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            Yes - Child has experienced neglect, or there is a suspicion that
            they have experienced neglect. This includes occasional neglect
            (e.g., child left home alone for a short period of time when
            developmentally inappropriate and with no adult supervision, or
            occasional failure to provide adequate supervision); multiple and/or
            prolonged absences of adults, with minimal supervision; or failure
            to provide basic necessities of life (adequate food, shelter, or
            clothing) on a regular basis.
          </div>
        </div>
      </div>
    </>
  ),
  ChildEmotionalAbuse: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">ACE4. EMOTIONAL ABUSE</div>
          This item rates whether the child has experienced verbal and nonverbal
          emotional abuse, including belittling, shaming, and humiliating,
          calling names, making negative comparisons to others, or telling the
          child that they are “no good.” This item includes both “emotional
          abuse,” which would include psychological maltreatment such as insults
          or humiliation towards a child and “emotional neglect,” described as
          the denial of emotional attention and/or support from caregivers.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>How does the caregiver talk to/interact with the child?</li>
            <li>Is there name calling or shaming in the home?</li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            No - There is no evidence that child has experienced emotional
            abuse.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            Yes - Child has experienced emotional abuse, or there is a suspicion
            that they have experienced emotional abuse (mild to severe, for any
            length of time) including: insults or occasionally being referred to
            in a derogatory manner by caregivers, being denied emotional
            attention or completely ignored, or threatened/terrorized by others.
          </div>
        </div>
      </div>
    </>
  ),
  ChildMedicalTrauma: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">ACE5. MEDICAL TRAUMA</div>
          This item describes whether or not the child has experienced
          medically-related trauma, resulting from, for example, inpatient
          hospitalizations, outpatient procedures, and significant injuries.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Has the child had any broken bones, stitches or other medical
              procedures?
            </li>
            <li>
              Has the child had to go to the emergency room, or stay overnight
              in the hospital?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            No - There is no evidence that the child has experienced any medical
            trauma.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            Yes - Child has had a medical experience that was perceived as
            emotionally or mentally overwhelming. This includes events that were
            acute in nature and did not result in ongoing medical needs;
            associated distress such as minor surgery, stitches or bone setting;
            acute injuries and moderately invasive medical procedures such as
            major surgery that required only short-term hospitalization; events
            that may have been life threatening and may have resulted in chronic
            health problems that alter the child’s physical functioning. A
            suspicion that a child has had a medical experience that was
            perceived as emotionally or mentally overwhelming should be rated
            here.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <b>Supplemental Information:</b> This item takes into account the
          impact of the event on the child. It describes experiences in which
          the child is subjected to medical procedures that are experienced as
          upsetting and overwhelming. A child born with physical deformities who
          is subjected to multiple surgeries could be included. A child who must
          experience chemotherapy or radiation could also be included. Children
          who experience an accident and require immediate medical intervention
          that results in on-going physical limitations or deformities (e.g.,
          burn victims) could be in included here. Common medical procedures,
          which are generally not welcome or pleasant but are also not
          emotionally or psychologically overwhelming for children (e.g., shots,
          pills) would generally not be rated here.
        </div>
      </div>
    </>
  ),
  ChildDisasterTrauma: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">CE6. NATURAL OR MANMADE DISASTER</div>
          This item describes the child’s exposure to either natural or manmade
          disasters.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Has the child been present during a natural or manmade disaster?
            </li>
            <li>
              Does the child watch television shows containing these themes or
              overhear adults talking about these kinds of disasters?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            No - There is no evidence that the child has experienced, been
            exposed to or witnessed natural or manmade disasters.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            Yes - Child has experienced, been exposed to or witnessed natural or
            manmade disasters either directly or second-hand (i.e. on
            television, hearing others discuss disasters). This includes
            disasters such as a fire or earthquake or manmade disaster; car
            accident, plane crashes, or bombings; observing a caregiver who has
            been injured in a car accident or fire or watching a neighbor’s
            house burn down; a disaster that caused significant harm or death to
            a loved one; or there is an ongoing impact or life disruption due to
            the disaster (e.g. caregiver loses job). A suspicion that the child
            has experienced, been exposed to or witnessed natural or manmade
            disasters either directly or second-hand would be rated here.
          </div>
        </div>
      </div>
    </>
  ),
  ChildFamilyViolence: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">ACE7. WITNESS TO FAMILY VIOLENCE</div>
          This item describes exposure to violence within the child’s home or
          family.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>Is there frequent fighting in the child’s family?</li>
            <li>Does the fighting ever become physical?</li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            No - There is no evidence the child has witnessed family violence.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            Yes - Child has witnessed, or there is a suspicion that they have
            witnessed family violence –single, repeated, or severe episodes.
            This includes episodes of family violence but no significant
            injuries (i.e. requiring emergency medical attention) and episodes
            in which significant injuries have occurred as a direct result of
            the violence.
          </div>
        </div>
      </div>
    </>
  ),
  ChildSchoolViolence: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">
            ACE8. WITNESS TO COMMUNITY/SCHOOL VIOLENCE
          </div>
          This item describes the exposure to incidents of violence the child
          has witnessed or experienced in their community. This includes
          witnessing violence at the child’s school or educational setting.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Does the child live in a neighborhood with frequent violence?
            </li>
            <li>
              Has the child witnessed or directly experienced violence at their
              school?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            No - There is no evidence that the child has witnessed violence in
            the community or in school.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            Yes - Child has witnessed or experienced violence in the community
            or in school, such as: fighting; friends/family injuries as a result
            of violence; severe and repeated instances of violence and/or the
            death of another person in their community/school as a result of
            violence; is the direct victim of violence/criminal activity in the
            community/school that was life threatening; or has experienced
            chronic/ongoing impact as a result of community/school violence
            (e.g., family member injured and no longer able to work). A
            suspicion that the child has witnessed or experienced violence in
            the community would be rated here.
          </div>
        </div>
      </div>
    </>
  ),
  ChildCriminalActsTrauma: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">
            ACE9. VICTIM/WITNESS TO CRIMINAL ACTIVITY
          </div>
          This item describes the child’s exposure to criminal activity.
          Criminal behavior includes any behavior for which an adult could go to
          prison including drug dealing, assault or battery.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Has the child or someone in their family ever been the victim of a
              crime?
            </li>
            <li>
              Has the child seen criminal activity in the community or home?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            No - There is no evidence that the child has been victim or a
            witness to criminal activity.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            Yes - Child has been victimized, or there is suspicion that they
            have been victimized or witnessed criminal activity. This includes a
            single instance, multiple instances, or chronic and severe instances
            of criminal activity that was life threatening or caused significant
            physical harm, or child has witnessed the death of a family friend,
            loved one.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <b>Supplemental Information:</b> Any behavior that could result in
          incarceration is considered criminal activity. A child who has been
          sexually abused or witnesses a sibling being sexually abused or
          physically abused to the extent that assault charges could be filed
          would be rated here and on the appropriate abuse-specific items. A
          child who has witnessed drug dealing, assault or battery would also be
          rated on this item.
        </div>
      </div>
    </>
  ),
  ChildWarTrauma: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">ACE10. WAR/TERRORISM AFFECTED</div>
          This item describes the child’s exposure to war, political violence,
          torture or terrorism.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>Has the child or their family lived in a war-torn region?</li>
            <li>
              How close was the child to war or political violence, torture or
              terrorism?
            </li>
            <li>Was the family displaced?</li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            No - No evidence that the child has been exposed to war, political
            violence, torture or terrorism.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            Yes - Child has experienced, or there is suspicion that they have
            experienced or been affected by war, terrorism or political
            violence. Examples include: Family members directly related to the
            child may have been exposed to war, political violence, or torture
            resulting in displacement, injury or disability, or death; parents
            may have been physically or psychologically disabled from the war
            and are unable to adequately care for the child; child may have
            spent an extended amount of time in a refugee camp, or feared for
            their own life during war or terrorism due to bombings or shelling
            very near to them; child may have been directly injured, tortured,
            or kidnapped in a terrorist attack; child may have served as a
            soldier, guerrilla, or other combatant in their home country. Also
            included is a child who did not live in war or terrorism-affected
            region or refugee camp, but family was affected by war.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <b>Supplemental Information:</b> Terrorism is defined as “the
          calculated use of violence or the threat of violence to inculcate
          fear, intended to coerce or to intimidate governments or societies in
          the pursuit of goals that are generally political, religious or
          ideological.” Terrorism includes attacks by individuals acting in
          isolation (e.g. sniper attacks).
        </div>
      </div>
    </>
  ),
  ChildParentalCriminalTrauma: (
    <>
      {
        // nothing to show
      }
    </>
  ),
  ChildAttachmentTrauma: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">
            ACE11. DISRUPTIONS IN CAREGIVING/ATTACHMENT LOSSES
          </div>
          This item documents the extent to which a child has had one or more
          major changes in caregivers, potentially resulting in disruptions in
          attachment.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Has the child ever lived apart from their parents/caregivers?
            </li>
            <li>
              What happened that resulted in the child living apart from their
              parents/caregivers?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            No - There is no evidence that the child has experienced disruptions
            in caregiving and/or attachment losses.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <div className="ms-2">
            Yes - Child has been exposed to, or there is suspicion that they
            have been exposed to at least one disruption in caregiving with
            familiar alternative caregivers or unknown caregivers (this includes
            placement in foster or other out-of-home care such as residential
            care facilities). Child may or may not have had ongoing contact with
            primary attachment figure(s) during this disruption. Shift in
            caregiving may have been temporary or permanent.
          </div>
        </div>
      </div>
      <div className="row border-bottom">
        <div className="col">
          <b>Supplemental Information:</b> Children who have been exposed to
          disruptions in caregiving involving separation from primary attachment
          figure(s) and/or attachment losses would be rated here. Children who
          have had placement changes, including stays in foster care,
          residential treatment facilities or juvenile justice settings, can be
          rated here. Short-term hospital stays or brief juvenile detention
          stays, during which the child's caregiver remains the same, would not
          be rated on this item.
        </div>
      </div>
    </>
  ),
  ChildFamilyStrengths: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">FAMILY STRENGTHS</div>
          This item refers to the presence of a sense of family identity as well
          as love and communication among family members. Even families who are
          struggling often have a firm foundation that consists of a positive
          sense of family and strong underlying love and commitment to each
          other. These are the constructs this strength is intended to identify.
          As with Family Functioning, the definition of family comes from the
          child’s perspective (i.e., who the child describes as their family).
          If this information is not known, then we recommend a definition of
          family that includes biological/adoptive relatives and their
          significant others with whom the child is still in contact.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Does the child have good relationships with any family member?
            </li>
            <li>
              Is there potential to develop positive family relationships?
            </li>
            <li>
              Is there a family member that the child can go to in time of need
              for support? That can advocate for the child?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          0 -{" "}
          <i>
            Well-developed, centerpiece strength; may be used as a centerpiece
            in an intervention/ action plan.
          </i>
          <div className="ms-2">
            Family has strong relationships and significant family strengths.
            This level indicates a family with much love and respect for one
            another. There is at least one family member who has a strong loving
            relationship with the child and is able to provide significant
            emotional or concrete support. Child is fully included in family
            activities.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          1 -{" "}
          <i>
            Identified and useful strength. Strength will be used, maintained or
            built upon as part of the plan. May require some effort to develop
            strength into a centerpiece strength.
          </i>
          <div className="ms-2">
            Family has some good relationships and good communication. Family
            members are able to enjoy each other’s company. There is at least
            one family member who has a strong, loving relationship with the
            child and is able to provide limited emotional or concrete support.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          2 -{" "}
          <i>
            Strengths have been identified but require strength-building efforts
            before they can be effectively utilized as part of a plan.
            Identified but not useful.
          </i>
          <div className="ms-2">
            Family needs some assistance in developing relationships and/or
            communications. Family members are known, but currently none are
            able to provide emotional or concrete support.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          3 -{" "}
          <i>
            An area in which no current strength is identified; efforts may be
            recommended to develop a strength in this area.
          </i>
          <div className="ms-2">
            Family needs significant assistance in developing relationships and
            communications, or child has no identified family. Child is not
            included in normal family activities.
          </div>
        </div>
      </div>
    </>
  ),
  ChildCommunityLife: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">COMMUNITY LIFE</div>
          This item reflects the child’s connection to people, places or
          institutions in their community. This connection is measured by the
          degree to which the child is involved with institutions of that
          community which might include (but are not limited to) community
          centers, little league teams, jobs, after-school activities, religious
          groups, etc. Connections through specific people (e.g., friends and
          family) could be considered an important community connection, if many
          people who are important to the child live in the same neighborhood.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>Does the child feel like they are part of a community?</li>
            <li>Are there activities that the child does in the community?</li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          0 -{" "}
          <i>
            Well-developed, centerpiece strength; may be used as a centerpiece
            in an intervention/ action plan.
          </i>
          <div className="ms-2">
            Child is well integrated into their community. The child is a member
            of community organizations and has positive ties to the community.
            For example, child may be a member of a community group (e.g. Girl
            or Boy Scouts) for more than one year, may be widely accepted by
            neighbors, or involved in other community activities, informal
            networks, etc.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          1 -{" "}
          <i>
            Identified and useful strength. Strength will be used, maintained or
            built upon as part of the plan. May require some effort to develop
            strength into a centerpiece strength.
          </i>
          <div className="ms-2">
            Child is somewhat involved with their community. This level can also
            indicate a child with significant community ties although they may
            be relatively short term.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          2 -{" "}
          <i>
            Strengths have been identified but require strength-building efforts
            before they can be effectively utilized as part of a plan.
            Identified but not useful.
          </i>
          <div className="ms-2">
            Child has an identified community but has only limited, or
            unhealthy, ties to that community.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          3 -{" "}
          <i>
            An area in which no current strength is identified; efforts may be
            recommended to develop a strength in this area.
          </i>
          <div className="ms-2">
            There is no evidence of an identified community of which child is a
            member at this time.
          </div>
        </div>
      </div>
    </>
  ),
  ChildTalents: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">TALENTS AND INTERESTS</div>
          This item refers to hobbies, skills, artistic interests, and talents
          that are positive ways that young people can spend theirtime, and also
          give them pleasure and a positive sense of self.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>What does the child do with free time?</li>
            <li>What does the child enjoy doing?</li>
            <li>Is the child engaged in any pro-social activities?</li>
            <li>What are the things that the child does particularly well?</li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          0 -{" "}
          <i>
            Well-developed, centerpiece strength; may be used as a centerpiece
            in an intervention/ action plan.
          </i>
          <div className="ms-2">
            Child has a talent that provides pleasure and/or self-esteem. A
            child with significant creative/artistic/athletic strengths would be
            rated here.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          1 -{" "}
          <i>
            Identified and useful strength. Strength will be used, maintained or
            built upon as part of the plan. May require some effort to develop
            strength into a centerpiece strength.
          </i>
          <div className="ms-2">
            Child has a talent, interest, or hobby that has the potential to
            provide pleasure and self-esteem. This level indicates a child with
            a notable talent. For example, a child who is involved in athletics
            or plays a musical instrument would be rated here.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          2 -{" "}
          <i>
            Strengths have been identified but require strength-building efforts
            before they can be effectively utilized as part of a plan.
            Identified but not useful.
          </i>
          <div className="ms-2">
            Child has expressed interest in developing a specific talent,
            interest or hobby even if that talent has not been developed to
            date, or whether it would provide them with any benefit.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          3 -{" "}
          <i>
            An area in which no current strength is identified; efforts may be
            recommended to develop a strength in this area.
          </i>
          <div className="ms-2">
            There is no evidence of identified talents, interests or hobbies at
            this time and/or child requires significant assistance to identify
            and develop talents and interests.
          </div>
        </div>
      </div>
    </>
  ),
  ChildOptimism: (
    <>
      <div className="row border-bottom">
        <div className="col">
          <div className="fw-bold">OPTIMISM</div>
          This item should be rated based on the child’s sense of self in their
          own future. This rates the child’s future orientation.
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          <b>Questions to Consider</b>
          <ul>
            <li>
              Does the child have a generally positive outlook on things; have
              things to look forward to?
            </li>
            <li>How does the child see themselves in the future?</li>
            <li>
              Is the child forward looking/sees themselves as likely to be
              successful?
            </li>
          </ul>
        </div>
      </div>
      <div className="row">
        <div className="col fw-bold">Ratings and Descriptions</div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          0 -{" "}
          <i>
            Well-developed, centerpiece strength; may be used as a centerpiece
            in an intervention/ action plan.
          </i>
          <div className="ms-2">
            Child has a strong and stable optimistic outlook for their future.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          1 -{" "}
          <i>
            Identified and useful strength. Strength will be used, maintained or
            built upon as part of the plan. May require some effort to develop
            strength into a centerpiece strength.
          </i>
          <div className="ms-2">
            Child is generally optimistic about their future.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          2 -{" "}
          <i>
            Strengths have been identified but require strength-building efforts
            before they can be effectively utilized as part of a plan.
            Identified but not useful.
          </i>
          <div className="ms-2">
            Child has difficulty maintaining a positive view of themselves and
            their life. Child’s outlook may vary from overly optimistic to
            overly pessimistic.
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col border-bottom">
          3 -{" "}
          <i>
            An area in which no current strength is identified; efforts may be
            recommended to develop a strength in this area.
          </i>
          <div className="ms-2">
            There is no evidence of optimism at this time and/or child has
            difficulties seeing positive aspects about themselves or their
            future.
          </div>
        </div>
      </div>
    </>
  ),
};
